import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, NavLink, Link, useNavigate, useParams, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Activity,
  ArrowLeft,
  BarChart3,
  BookOpen,
  Bot,
  Building2,
  CheckCircle2,
  Clock,
  CreditCard,
  Eye,
  FileText,
  Gauge,
  Headphones,
  KeyRound,
  Layers3,
  LogOut,
  Menu,
  MessageSquareText,
  PhoneCall,
  Plus,
  RefreshCw,
  Search,
  Settings,
  ShieldCheck,
  Sparkles,
  Users,
  Webhook,
  Workflow,
  Wrench,
  XCircle,
} from "lucide-react";
import {
  Area,
  Bar,
  BarChart,
  ComposedChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Toaster, toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { ChannelBadge, ContactPanel, ContactsPage, downloadJson } from "./omni";
import { ClientExportCard, DatenschutzPage } from "./datenschutz";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
// WS8: when served on a client host (e.g. dashboard.nudgeit.pro), render a client-only portal.
const CLIENT_HOSTS = (process.env.REACT_APP_CLIENT_HOSTS || "dashboard.nudgeit.pro").split(",").map((h) => h.trim()).filter(Boolean);
const IS_CLIENT_PORTAL = typeof window !== "undefined" && CLIENT_HOSTS.includes(window.location.hostname);
const API = `${BACKEND_URL}/api`;

const navItems = [
  { key: "overview", label: "Übersicht", path: "overview", icon: Gauge, testId: "sidebar-overview-link" },
  { key: "agents", label: "Agenten", path: "agents", icon: Bot, testId: "sidebar-agents-link" },
  { key: "prompt", label: "Prompt", path: "prompt", icon: FileText, testId: "sidebar-prompt-link" },
  { key: "conversations", label: "Gespräche", path: "conversations", icon: MessageSquareText, testId: "sidebar-conversations-link" },
  { key: "contacts", label: "Kontakte", path: "contacts", icon: Users, testId: "sidebar-contacts-link" },
  { key: "knowledge-base", label: "Wissensdatenbank", path: "knowledge-base", icon: BookOpen, testId: "sidebar-knowledge-base-link" },
  { key: "billing", label: "Abrechnung", path: "billing", icon: CreditCard, testId: "sidebar-billing-link" },
  { key: "datenschutz", label: "Datenschutz", path: "datenschutz", icon: ShieldCheck, testId: "sidebar-datenschutz-link" },
  { key: "settings", label: "Einstellungen", path: "settings", icon: Settings, testId: "sidebar-settings-link" },
];

// Resolve which client-portal tabs a given access object (GET /me/access shape) may see.
// Returns null when there is no restriction (admin / missing access). Keys absent from the
// returned map (agents, settings) are always visible. `billing` is deny-by-default: hidden
// unless an admin grants the billing right.
function visibleTabKeys(access) {
  if (!access || access.is_admin) return null;
  return {
    overview: (access.analytics?.enabled ?? true) !== false,
    conversations: access.conversations !== false,
    contacts: !!access.channels?.inbox,  // deny-by-default, like billing
    "knowledge-base": access.knowledge_base !== false,
    prompt: !!(access.agent_settings?.show_system_prompt || access.agent_settings?.edit_system_prompt || access.prompt_any_agent),
    billing: !!access.billing,
    datenschutz: !!(access.gdpr?.export || access.gdpr?.erase),  // deny-by-default, like billing
  };
}

function formatApiErrorDetail(detail) {
  if (detail == null) return "Ein unerwarteter Fehler ist aufgetreten.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((item) => item?.msg || JSON.stringify(item)).filter(Boolean).join(" ");
  }
  if (detail?.msg) return detail.msg;
  return String(detail);
}

function cx(...parts) {
  return parts.filter(Boolean).join(" ");
}

function formatDate(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 16);
  return new Intl.DateTimeFormat("de-DE", { dateStyle: "medium", timeStyle: "short" }).format(date);
}

// Narrow numeric date for the compact conversation list (so 3 columns fit without h-scroll).
function formatDateShort(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 16);
  return new Intl.DateTimeFormat("de-DE", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }).format(date);
}

function formatDuration(seconds) {
  if (!seconds && seconds !== 0) return "—";
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  return `${mins}:${String(secs).padStart(2, "0")} min`;
}

function statusClass(status) {
  if (["completed", "processed"].includes(status)) return "badge-success";
  if (["ongoing", "active"].includes(status)) return "badge-info";
  if (["failed", "error"].includes(status)) return "badge-danger";
  return "badge-muted";
}

function ProviderBadge({ provider }) {
  return (
    <Badge variant="outline" className="provider-badge" data-testid={`provider-badge-${provider || "unknown"}`}>
      Voice Agent
    </Badge>
  );
}

function StatusBadge({ status }) {
  return (
    <Badge variant="outline" className={statusClass(status)} data-testid={`status-badge-${status || "unknown"}`}>
      {status || "unknown"}
    </Badge>
  );
}

function PageMotion({ children }) {
  return (
    <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.22, ease: "easeOut" }}>
      {children}
    </motion.div>
  );
}

function LoadingCards() {
  return (
    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4" data-testid="state-loading">
      {[1, 2, 3, 4].map((item) => (
        <Card key={item} className="dashboard-card">
          <CardHeader><Skeleton className="h-4 w-28" /></CardHeader>
          <CardContent><Skeleton className="h-8 w-24" /></CardContent>
        </Card>
      ))}
    </div>
  );
}

function EmptyState({ title, description, action }) {
  return (
    <Card className="empty-card" data-testid="state-empty">
      <CardContent className="py-10 text-center">
        <Layers3 className="mx-auto h-8 w-8 text-muted-foreground" />
        <h3 className="mt-4 text-lg font-semibold">{title}</h3>
        <p className="mt-2 text-sm text-muted-foreground">{description}</p>
        {action ? <div className="mt-5">{action}</div> : null}
      </CardContent>
    </Card>
  );
}

function useAuth() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);

  const refreshUser = useCallback(async () => {
    setChecking(true);
    try {
      const response = await fetch(`${API}/auth/me`, { credentials: "include" });
      if (!response.ok) throw new Error("Not authenticated");
      setUser(await response.json());
    } catch {
      setUser(null);
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => { refreshUser(); }, [refreshUser]);

  const logout = useCallback(async () => {
    try {
      await fetch(`${API}/auth/logout`, { method: "POST", credentials: "include" });
    } finally {
      setUser(null);
      setChecking(false);
    }
  }, []);

  return { user, setUser, refreshUser, logout, checking, isAuthenticated: Boolean(user) };
}

function useApi(onUnauthorized) {
  return useCallback(async (path, options = {}) => {
    const headers = { ...(options.body ? { "Content-Type": "application/json" } : {}), ...(options.headers || {}) };
    const response = await fetch(`${API}${path}`, { ...options, headers, credentials: "include" });
    let body = null;
    const text = await response.text();
    if (text) {
      try { body = JSON.parse(text); } catch { body = text; }
    }
    if (response.status === 401 && onUnauthorized) {
      await onUnauthorized();
    }
    if (!response.ok) {
      const message = formatApiErrorDetail(body?.detail || body?.message || `Request failed (${response.status})`);
      throw new Error(message);
    }
    return body;
  }, [onUnauthorized]);
}

function useResource(api, path) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const seqRef = useRef(0);

  const load = useCallback(async () => {
    if (!path) {
      setLoading(false);
      return;
    }
    // Sequence guard: when path changes quickly (e.g. live search), a slow earlier
    // response must not overwrite the newer one.
    const seq = ++seqRef.current;
    setLoading(true);
    setError(null);
    try {
      const result = await api(path);
      if (seq === seqRef.current) setData(result);
    } catch (err) {
      if (seq === seqRef.current) setError(err.message);
    } finally {
      if (seq === seqRef.current) setLoading(false);
    }
  }, [api, path]);

  useEffect(() => { load(); }, [load]);
  return { data, loading, error, reload: load, setData };
}

function usePublicBranding() {
  const [branding, setBranding] = useState({ company_name: "Meine Bots", primary_color: "#5B51EC", accent_color: "#C7EA52" });
  useEffect(() => {
    let mounted = true;
    fetch(`${API}/public/branding`).then((r) => r.json()).then((data) => {
      if (!mounted) return;
      setBranding(data || {});
      if (data?.primary_color) document.documentElement.style.setProperty("--brand-primary-hex", data.primary_color);
      if (data?.accent_color) document.documentElement.style.setProperty("--brand-accent-hex", data.accent_color);
      if (data?.website_title) document.title = data.website_title;
      if (data?.favicon_url) {
        let link = document.querySelector("link[rel='icon']");
        if (!link) { link = document.createElement("link"); link.rel = "icon"; document.head.appendChild(link); }
        link.href = data.favicon_url;
      }
    }).catch(() => {});
    return () => { mounted = false; };
  }, []);
  return branding;
}

function LoginPage({ auth, branding }) {
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError("");
    try {
      const response = await fetch(`${API}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(form),
      });
      const body = await response.json();
      if (!response.ok) throw new Error(formatApiErrorDetail(body?.detail || "Login fehlgeschlagen"));
      auth.setUser(body.user);
      toast.success("Login erfolgreich");
      navigate(body.user.role === "admin" ? "/admin/clients" : `/clients/${body.user.client_id}/overview`, { replace: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="login-shell">
      <section className="login-brand-panel">
        <div className="brand-mark">{branding?.login_logo_url ? <img src={branding.login_logo_url} alt="Logo" style={{ maxHeight: 56, maxWidth: 60 }} data-testid="login-logo" /> : <Headphones className="h-7 w-7" />}</div>
        <div>
          <p className="eyebrow">{IS_CLIENT_PORTAL ? "Kunden-Login" : "Voice-Agent Konsole"}</p>
          <h1>{branding?.company_name || "Meine Bots"} {IS_CLIENT_PORTAL ? "Kunden-Dashboard." : "für Voice AI Operations."}</h1>
          <p className="login-copy">Agenten, Transkripte, Wissensdatenbanken, Workflows und nutzungsbasierte KPIs in einer Oberfläche.</p>
        </div>
      </section>
      <section className="login-form-panel">
        <Card className="login-card" data-testid="login-card">
          <CardHeader>
            <CardTitle>Anmelden</CardTitle>
            <CardDescription>Login für bestehende Kunden. Kein öffentliches Sign-up.</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">E-Mail</Label>
                <Input id="email" className="bg-white" data-testid="login-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} autoComplete="email" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Passwort</Label>
                <Input id="password" className="bg-white" type="password" data-testid="login-password-input" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} autoComplete="current-password" />
              </div>
              {error ? <div className="error-alert" data-testid="login-error-alert">{error}</div> : null}
              <Button className="w-full" type="submit" disabled={loading} data-testid="login-submit-button">
                {loading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />} Einloggen
              </Button>
              <Button className="w-full" variant="ghost" type="button" onClick={() => navigate("/forgot-password")} data-testid="forgot-password-link">
                Passwort vergessen?
              </Button>
            </form>
          </CardContent>
        </Card>
      </section>
    </main>
  );
}

function ProtectedRoute({ auth, children }) {
  if (auth.checking) return <div className="auth-loading" data-testid="state-loading"><RefreshCw className="h-4 w-4 animate-spin" /> Session wird geprüft...</div>;
  if (!auth.isAuthenticated) return <Navigate to="/login" replace />;
  return children;
}

function AppShell({ auth, api, branding }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [topbarQuery, setTopbarQuery] = useState("");
  const clientsState = useResource(api, "/clients", []);
  const clients = useMemo(() => clientsState.data || [], [clientsState.data]);
  // Client id taken straight from the URL (matches both /clients/:id and /admin/clients/:id).
  const urlClientId = useMemo(() => {
    const match = location.pathname.match(/\/clients\/([^/]+)/);
    return match ? match[1] : "";
  }, [location.pathname]);
  // Remember the last client the admin opened so the sidebar can always offer a way back —
  // even after navigating to admin-level pages (Agents, Billing) or another client's settings.
  const [rememberedClientId, setRememberedClientId] = useState(() => {
    try { return window.localStorage.getItem("activeClientId") || ""; } catch { return ""; }
  });
  useEffect(() => {
    if (urlClientId && urlClientId !== rememberedClientId) {
      setRememberedClientId(urlClientId);
      try { window.localStorage.setItem("activeClientId", urlClientId); } catch { /* ignore */ }
    }
  }, [urlClientId, rememberedClientId]);
  const isAdmin = auth.user?.role === "admin";
  // The sidebar filters rights itself instead of taking them from ClientPage: threading the value
  // down would mean touching ClientPage's loading gate (the card gate of a live billing system) —
  // the worst place to risk a regression. The cost is real, not free: /me/access sends no cache
  // headers, so this is a genuine second request per page load — but it is one ~400-byte GET.
  const meAccess = useResource(api, !isAdmin ? "/me/access" : null);
  const acc = isAdmin ? null : meAccess.data;
  // null for an admin and while /me/access is unresolved -> no filtering (today's behaviour).
  const vis = visibleTabKeys(acc);
  // The client_admin floor is repeated from ClientPage's canGdpr on purpose: a failed /me/access
  // leaves `acc` null, and without the floor the sidebar would withhold a page that canGdpr grants
  // that role anyway — a link missing for a page that works. What is guaranteed is only that: for a
  // client_admin the sidebar never hides Datenschutz. A client_user with unknown rights gets the
  // opposite disagreement (visibleTabKeys(null) leaves the tab strip showing every tab while this
  // entry is withheld), which is deliberate — canGdpr has no floor for that role either, so the tab
  // leads to an empty page, and withholding the link keeps it from flashing for someone who cannot
  // use it.
  const canSeeGdpr = isAdmin || auth.user?.role === "client_admin" || !!(acc?.gdpr?.export || acc?.gdpr?.erase);
  const sidebarItems = navItems.filter((item) =>
    item.key === "datenschutz" ? canSeeGdpr : !vis || vis[item.key] !== false
  );
  const activeClientId = urlClientId || rememberedClientId || auth.user?.client_id || (isAdmin ? "" : clients[0]?.id);
  const activeClient = useMemo(
    () => clients.find((client) => client.id === activeClientId) || (isAdmin ? null : clients[0]),
    [clients, activeClientId, isAdmin]
  );

  useEffect(() => {
    if (auth.user?.role !== "admin" && auth.user?.client_id && location.pathname === "/") {
      navigate(`/clients/${auth.user.client_id}/overview`, { replace: true });
    }
  }, [auth.user, location.pathname, navigate]);

  const logout = () => {
    auth.logout();
    toast.success("Abgemeldet");
    navigate("/login", { replace: true });
  };

  const sidebar = (
    <aside className="sidebar" data-testid="app-sidebar">
      <div className="sidebar-brand">
        <div className="brand-mark small"><Headphones className="h-5 w-5" /></div>
        <div><strong>{branding?.company_name || "Meine Bots"}</strong><span>{IS_CLIENT_PORTAL ? "Client Dashboard" : "Agency Console"}</span></div>
      </div>
      <nav className="sidebar-nav">
        {auth.user?.role === "admin" ? (
          <NavLink to="/admin/clients" className={({ isActive }) => cx("nav-link", isActive && "active")} data-testid="sidebar-clients-link">
            <Building2 className="h-4 w-4" /> Clients
          </NavLink>
        ) : null}
        {auth.user?.role === "admin" ? (
          <NavLink to="/admin/billing" className={({ isActive }) => cx("nav-link", isActive && "active")} data-testid="sidebar-billing-admin-link">
            <CreditCard className="h-4 w-4" /> Billing
          </NavLink>
        ) : null}
        {auth.user?.role === "admin" ? (
          <NavLink to="/admin/settings" className={({ isActive }) => cx("nav-link", isActive && "active")} data-testid="sidebar-settings-admin-link">
            <Settings className="h-4 w-4" /> Einstellungen
          </NavLink>
        ) : null}
        {auth.user?.role === "admin" && !IS_CLIENT_PORTAL ? (
          <NavLink to="/admin/agents" className={({ isActive }) => cx("nav-link", isActive && "active")} data-testid="sidebar-agents-admin-link">
            <Bot className="h-4 w-4" /> Agents
          </NavLink>
        ) : null}
        {activeClient ? (
          <div className="nav-client-group" data-testid="sidebar-active-client">
            <div className="nav-client-label">Aktueller Client</div>
            <Link
              to={isAdmin && !IS_CLIENT_PORTAL ? `/admin/clients/${activeClient.id}` : `/clients/${activeClient.id}/overview`}
              className="nav-link nav-client-head"
              data-testid="sidebar-active-client-link"
              title={`Zurück zu ${activeClient.name}`}
            >
              <Building2 className="h-4 w-4" />
              <span className="nav-client-name">{activeClient.name}</span>
            </Link>
            {/* „Einstellungen" is no longer filtered out: it holds the only password form a member
                has (the account export moved to Datenschutz, so that is not the reason). */}
            {sidebarItems.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink key={item.key} to={`/clients/${activeClient.id}/${item.path}`} className={({ isActive }) => cx("nav-link nav-sub", isActive && "active")} data-testid={item.testId}>
                  <Icon className="h-4 w-4" /> {item.label}
                </NavLink>
              );
            })}
          </div>
        ) : null}
        {auth.user?.role !== "client_user" ? (
          <NavLink to="/event-log" className={({ isActive }) => cx("nav-link", isActive && "active")} data-testid="sidebar-event-log-link">
            <Activity className="h-4 w-4" /> Event Log
          </NavLink>
        ) : null}
        {auth.user?.role === "admin" ? (
          <NavLink to="/audit-log" className={({ isActive }) => cx("nav-link", isActive && "active")} data-testid="sidebar-audit-log-link">
            <ShieldCheck className="h-4 w-4" /> Audit Log
          </NavLink>
        ) : null}
      </nav>
      <div className="sidebar-footer">
        <div className="user-chip"><ShieldCheck className="h-4 w-4" /><span>{auth.user?.role}</span></div>
        <Button variant="outline" size="sm" onClick={logout} data-testid="logout-button"><LogOut className="h-4 w-4" /> Logout</Button>
      </div>
    </aside>
  );

  return (
    <div className="app-shell app-noise">
      <div className="desktop-sidebar">{sidebar}</div>
      <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
        <SheetContent side="left" className="p-0 mobile-sheet">
          <SheetHeader className="sr-only"><SheetTitle>Navigation</SheetTitle><SheetDescription>Dashboard Navigation</SheetDescription></SheetHeader>
          {sidebar}
        </SheetContent>
      </Sheet>
      <main className="main-panel">
        <header className="topbar">
          <Button className="lg:hidden" variant="outline" size="icon" aria-label="Navigation öffnen" onClick={() => setSidebarOpen(true)} data-testid="mobile-menu-button"><Menu className="h-4 w-4" /></Button>
          <div className="topbar-title">
            <span>{isAdmin ? "Operations Console" : "Kundenbereich"}</span>
            <strong>{activeClient?.name || "Admin Workspace"}</strong>
          </div>
          {activeClient ? (
            <form
              className="topbar-search"
              role="search"
              onSubmit={(e) => {
                e.preventDefault();
                const q = topbarQuery.trim();
                navigate(`/clients/${activeClient.id}/conversations${q ? `?q=${encodeURIComponent(q)}` : ""}`);
              }}
            >
              <Search className="h-4 w-4" />
              <input
                value={topbarQuery}
                onChange={(e) => setTopbarQuery(e.target.value)}
                placeholder="Gespräche durchsuchen…"
                aria-label="Gespräche durchsuchen"
                data-testid="topbar-search-input"
              />
            </form>
          ) : <div className="topbar-search-spacer" />}
          <Badge variant="outline" className="topbar-badge" data-testid="current-user-badge">{auth.user?.email}</Badge>
        </header>
        <section className="content-wrap">
          <Routes>
            <Route path="/" element={<Navigate to={auth.user?.role === "admin" ? "/admin/clients" : `/clients/${auth.user?.client_id}/overview`} replace />} />
            <Route path="/admin/clients" element={<AdminClients api={api} user={auth.user} />} />
            <Route path="/admin/clients/:clientId" element={<AdminClientDetail api={api} user={auth.user} />} />
            <Route path="/admin/agents" element={<AdminAgents api={api} user={auth.user} />} />
            <Route path="/admin/agents/:agentId" element={<AgentDetailPage api={api} user={auth.user} />} />
            <Route path="/admin/billing" element={<AdminBilling api={api} user={auth.user} />} />
            <Route path="/admin/settings" element={<PageMotion><PageHeader eyebrow="Admin" title="Einstellungen" description="Agentur-Branding, E-Mail, Integrationen und Webhooks." /><SettingsPage api={api} client={null} clientId={null} user={auth.user} /></PageMotion>} />
            <Route path="/clients/:clientId/overview" element={<ClientPage section="overview" api={api} user={auth.user} />} />
            <Route path="/clients/:clientId/agents" element={<ClientPage section="agents" api={api} user={auth.user} />} />
            <Route path="/clients/:clientId/prompt" element={<ClientPage section="prompt" api={api} user={auth.user} />} />
            <Route path="/clients/:clientId/conversations" element={<ClientPage section="conversations" api={api} user={auth.user} />} />
            <Route path="/clients/:clientId/conversations/:conversationId" element={<ClientPage section="conversations" api={api} user={auth.user} />} />
            <Route path="/clients/:clientId/contacts" element={<ClientPage section="contacts" api={api} user={auth.user} />} />
            <Route path="/clients/:clientId/knowledge-base" element={<ClientPage section="knowledge-base" api={api} user={auth.user} />} />
            <Route path="/clients/:clientId/billing" element={<ClientPage section="billing" api={api} user={auth.user} />} />
            <Route path="/clients/:clientId/datenschutz" element={<ClientPage section="datenschutz" api={api} user={auth.user} />} />
            <Route path="/clients/:clientId/settings" element={<ClientPage section="settings" api={api} user={auth.user} />} />
            <Route path="/event-log" element={<EventLog api={api} />} />
            {auth.user?.role === "admin" && <Route path="/audit-log" element={<AuditLog api={api} />} />}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </section>
      </main>
    </div>
  );
}

function PageHeader({ eyebrow, title, description, action }) {
  return (
    <div className="page-header">
      <div>
        <p className="eyebrow">{eyebrow}</p>
        <h1>{title}</h1>
        <p>{description}</p>
      </div>
      {action ? <div className="page-header-action">{action}</div> : null}
    </div>
  );
}

function FeaturesPanel({ clientId, initialFeatures, api, onClose }) {
  const [features, setFeatures] = useState(initialFeatures);
  const [saving, setSaving] = useState(false);

  const toggle = (feat) => setFeatures(prev =>
    prev.includes(feat) ? prev.filter(f => f !== feat) : [...prev, feat]
  );

  const save = async () => {
    setSaving(true);
    await api(`/clients/${clientId}/features`, { method: "PATCH", body: JSON.stringify({ allowed_features: features }) });
    setSaving(false);
    onClose();
  };

  return (
    <div className="p-4 bg-muted rounded-lg space-y-3 mt-2">
      <p className="text-sm font-medium">Client-Berechtigungen</p>
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={features.includes("agent_prompt_view")} onChange={() => toggle("agent_prompt_view")} />
        Prompt anzeigen (agent_prompt_view)
      </label>
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={features.includes("agent_prompt_edit")} onChange={() => toggle("agent_prompt_edit")} />
        Prompt bearbeiten (agent_prompt_edit)
      </label>
      <div className="flex gap-2">
        <Button size="sm" onClick={save} disabled={saving}>{saving ? "Speichern…" : "Speichern"}</Button>
        <Button size="sm" variant="ghost" onClick={onClose}>Abbrechen</Button>
      </div>
    </div>
  );
}

function AdminClients({ api, user }) {
  const overview = useResource(api, "/admin/clients-overview", []);
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", slug: "", primary_contact_email: "" });
  const [saving, setSaving] = useState(false);
  const [createError, setCreateError] = useState("");
  const [newCreds, setNewCreds] = useState(null);

  if (user.role !== "admin") return <Navigate to={`/clients/${user.client_id}/overview`} replace />;
  const allClients = overview.data || [];

  const createClient = async (event) => {
    event.preventDefault();
    setSaving(true); setCreateError(""); setNewCreds(null);
    try {
      const res = await fetch(`${API}/clients`, { method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ ...form, slug: form.slug || undefined, primary_contact_email: form.primary_contact_email || undefined }) });
      let body = null; const text = await res.text();
      if (text) { try { body = JSON.parse(text); } catch { body = text; } }
      if (res.status === 409) { setCreateError(formatApiErrorDetail(body?.detail || "Slug bereits vergeben")); return; }
      if (!res.ok) { toast.error(formatApiErrorDetail(body?.detail || `Fehler (${res.status})`)); return; }
      toast.success("Client erstellt");
      if (body.client_admin_temp_password) setNewCreds({ email: body.client_admin_email, password: body.client_admin_temp_password });
      setForm({ name: "", slug: "", primary_contact_email: "" });
      await overview.reload();
    } catch (err) { toast.error(err.message); } finally { setSaving(false); }
  };

  return (
    <PageMotion>
      <PageHeader eyebrow="Admin" title="Clients" description="Alle Kunden — Name, Status, Agents und Erstellungsdatum." action={<Button onClick={() => document.getElementById("client-name")?.focus()}><Plus className="h-4 w-4" /> Client anlegen</Button>} />
      <div className="grid gap-5 xl:grid-cols-[1fr_360px]">
        <Card className="dashboard-card">
          <CardHeader><CardTitle>Clients</CardTitle><CardDescription>Klicke einen Client für Details.</CardDescription></CardHeader>
          <CardContent>
            {overview.loading ? <Skeleton className="h-40 w-full" data-testid="state-loading" /> : allClients.length ? (
              <Table data-testid="clients-table">
                <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Status</TableHead><TableHead>Agents</TableHead><TableHead>Erstellt</TableHead><TableHead></TableHead></TableRow></TableHeader>
                <TableBody>
                  {/* Die letzte Zeile kann der Sammel-Eintrag „Ohne Client" sein (Agenten, die eine
                      Client-Löschung bewusst behalten hat). Sie hat keine Client-ID und damit keine
                      Detailseite — deshalb nicht klickbar. */}
                  {allClients.map((client) => (
                    <TableRow key={client.id || "__unassigned__"} data-testid="client-row" className={cx(client.id && "cursor-pointer")} role={client.id ? "button" : undefined} tabIndex={client.id ? 0 : undefined}
                      onClick={() => { if (client.id) navigate(`/admin/clients/${client.id}`); }}
                      onKeyDown={(e) => { if (client.id && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); navigate(`/admin/clients/${client.id}`); } }}>
                      <TableCell className="font-medium">{client.name}</TableCell>
                      <TableCell>{client.id ? <StatusBadge status={client.is_active ? "active" : "inactive"} /> : <span className="text-sm text-muted-foreground">nicht zugewiesen</span>}</TableCell>
                      <TableCell><Badge variant="outline">{client.agent_count}</Badge></TableCell>
                      <TableCell className="text-sm text-muted-foreground">{client.created_at ? formatDate(client.created_at) : "—"}</TableCell>
                      <TableCell className="text-right">{client.id ? <Button variant="outline" size="sm" data-testid="client-open-button" onClick={(e) => { e.stopPropagation(); navigate(`/admin/clients/${client.id}`); }}>Details</Button> : <Button variant="outline" size="sm" data-testid="client-open-agents-button" onClick={() => navigate("/admin/agents")}>Agents</Button>}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : <EmptyState title="Noch keine Clients" description="Erstelle den ersten Client." />}
          </CardContent>
        </Card>
        <Card className="dashboard-card">
          <CardHeader><CardTitle>Neuer Client</CardTitle><CardDescription>Mit Kontakt-E-Mail wird automatisch ein Client-Login erstellt.</CardDescription></CardHeader>
          <CardContent>
            <form className="space-y-4" onSubmit={createClient}>
              <Field label="Name"><Input id="client-name" className="bg-white" data-testid="client-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
              <Field label="Slug"><Input className="bg-white" data-testid="client-slug-input" placeholder="auto wenn leer" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} /></Field>
              <Field label="Kontakt E-Mail (= Client-Login)"><Input className="bg-white" data-testid="client-email-input" value={form.primary_contact_email} onChange={(e) => setForm({ ...form, primary_contact_email: e.target.value })} /></Field>
              {createError ? <div className="error-alert" data-testid="client-create-error-alert">{createError}</div> : null}
              <Button type="submit" disabled={saving} data-testid="client-save-button">{saving ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Speichern</Button>
              {newCreds ? <div className="info-callout" data-testid="new-client-creds">Client-Login erstellt:<br /><strong>{newCreds.email}</strong><br />Temp-Passwort: <span className="font-mono">{newCreds.password}</span></div> : null}
            </form>
          </CardContent>
        </Card>
      </div>
    </PageMotion>
  );
}

function DetailRow({ label, value, mono }) {
  return <div className="flex justify-between gap-3"><span className="text-muted-foreground">{label}</span><span className={mono ? "font-mono text-xs truncate max-w-[200px]" : ""}>{value ?? "—"}</span></div>;
}

const ACCESS_METRIC_LABELS = [
  ["total_conversations", "Gesamt Conversations"], ["interactions", "Interaktionen"], ["total_minutes", "Gesamt Minuten"],
  ["avg_call_duration", "Ø Anrufdauer"], ["recent_call", "Letzter Anruf"], ["ended_call", "Beendete Anrufe"],
  ["transfer_rate", "Transfer-Rate"], ["success_rate", "Success Rate"],
];

function blankAccessConfig() {
  return {
    analytics: { enabled: true, metrics: Object.fromEntries(ACCESS_METRIC_LABELS.map(([k]) => [k, true])) },
    conversations: true, knowledge_base: true,
    agent_settings: { enabled: true, show_system_prompt: false, show_first_message: true, edit_system_prompt: false },
    billing: false,
    gdpr: { export: false, erase: false },
  };
}

function AccessConfigEditor({ value, onChange, extraMetrics = [], scope = "client" }) {
  const v = value || blankAccessConfig();
  const setMetric = (k, on) => onChange({ ...v, analytics: { ...v.analytics, metrics: { ...v.analytics.metrics, [k]: on } } });
  return (
    <div className="space-y-3">
      <label className="flex items-center justify-between"><span className="font-medium text-sm">Analytics</span><input type="checkbox" data-testid="access-analytics-toggle" checked={v.analytics.enabled} onChange={(e) => onChange({ ...v, analytics: { ...v.analytics, enabled: e.target.checked } })} /></label>
      {v.analytics.enabled ? <div className="grid grid-cols-2 gap-1.5 pl-3">
        {ACCESS_METRIC_LABELS.map(([k, label]) => <label key={k} className="flex items-center gap-2 text-xs"><input type="checkbox" checked={v.analytics.metrics[k] !== false} onChange={(e) => setMetric(k, e.target.checked)} /> {label}</label>)}
        {extraMetrics.map(([k, label]) => <label key={k} className="flex items-center gap-2 text-xs"><input type="checkbox" data-testid={`access-metric-${k}`} checked={v.analytics.metrics[k] === true} onChange={(e) => setMetric(k, e.target.checked)} /> {label} <span className="text-[10px] text-muted-foreground">(Custom)</span></label>)}
      </div> : null}
      <label className="flex items-center justify-between"><span className="font-medium text-sm">Conversations</span><input type="checkbox" data-testid="access-conversations-toggle" checked={v.conversations} onChange={(e) => onChange({ ...v, conversations: e.target.checked })} /></label>
      <label className="flex items-center justify-between"><span className="font-medium text-sm">Knowledge Base</span><input type="checkbox" data-testid="access-kb-toggle" checked={v.knowledge_base} onChange={(e) => onChange({ ...v, knowledge_base: e.target.checked })} /></label>
      <label className="flex items-center justify-between"><span className="font-medium text-sm">Abrechnung</span><input type="checkbox" data-testid="access-billing-toggle" checked={!!v.billing} onChange={(e) => onChange({ ...v, billing: e.target.checked })} /></label>
      {/* Hidden on a per-agent override: require_gdpr resolves access without an agent_id and
          _access_response_for ignores agent overrides for `gdpr`, so a tick here would do nothing
          in either direction — a misleading control on a compliance surface. */}
      {scope === "client" ? <div className="space-y-1.5">
        <span className="font-medium text-sm">Datenschutz (DSGVO)</span>
        <div className="space-y-1.5 pl-3">
          <label className="flex items-center gap-2 text-xs"><input type="checkbox" data-testid="access-gdpr-export-toggle" checked={!!v.gdpr?.export} onChange={(e) => onChange({ ...v, gdpr: { ...v.gdpr, export: e.target.checked } })} /> Daten exportieren (Art. 15/20)</label>
          <label className="flex items-center gap-2 text-xs"><input type="checkbox" data-testid="access-gdpr-erase-toggle" checked={!!v.gdpr?.erase} onChange={(e) => onChange({ ...v, gdpr: { ...v.gdpr, erase: e.target.checked } })} /> Daten löschen (Art. 17)</label>
        </div>
      </div> : null}
      <label className="flex items-center justify-between"><span className="font-medium text-sm">Agent Settings</span><input type="checkbox" data-testid="access-agentsettings-toggle" checked={v.agent_settings.enabled} onChange={(e) => onChange({ ...v, agent_settings: { ...v.agent_settings, enabled: e.target.checked } })} /></label>
      {v.agent_settings.enabled ? <div className="space-y-1.5 pl-3">
        <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={v.agent_settings.show_system_prompt} onChange={(e) => onChange({ ...v, agent_settings: { ...v.agent_settings, show_system_prompt: e.target.checked } })} /> System Prompt anzeigen</label>
        <label className="flex items-center gap-2 text-xs"><input type="checkbox" data-testid="access-edit-prompt-toggle" checked={!!v.agent_settings.edit_system_prompt} onChange={(e) => onChange({ ...v, agent_settings: { ...v.agent_settings, edit_system_prompt: e.target.checked } })} /> System Prompt bearbeiten &amp; pushen</label>
        <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={v.agent_settings.show_first_message} onChange={(e) => onChange({ ...v, agent_settings: { ...v.agent_settings, show_first_message: e.target.checked } })} /> First Message anzeigen</label>
      </div> : null}
    </div>
  );
}

function ClientDetailOverview({ api, client, clientId }) {
  const pm = useResource(api, `/billing/payment-method?client_id=${clientId}`, [clientId]);
  const summary = useResource(api, `/usage/summary?client_id=${clientId}`, [clientId]);
  const k = summary.data || {};
  // "Minuten (gesamt)" oben ist All-time (usage/summary). Die abrechenbaren Minuten des laufenden
  // Zyklus (mit Go-Live-Cutoff) kommen aus der Billing-Preview des aktiven Assignments — damit die
  // Zahl mit dem Abrechnung-Reiter übereinstimmt und die Diskrepanz erklärt ist.
  const assignments = useResource(api, `/billing/assignments?client_id=${clientId}`, [clientId]);
  const [cycle, setCycle] = useState(null);
  useEffect(() => {
    const active = (assignments.data || []).find((a) => a.status === "active");
    if (!active) { setCycle(null); return; }
    api("/billing/usage/preview", { method: "POST", body: JSON.stringify({ assignment_id: active.id }) })
      .then(setCycle).catch(() => {});
  }, [assignments.data]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="grid gap-5 xl:grid-cols-2">
      <Card className="dashboard-card"><CardHeader><CardTitle>Client Info</CardTitle></CardHeader><CardContent className="space-y-2 text-sm">
        <DetailRow label="Name" value={client?.name} />
        <DetailRow label="Status" value={client?.is_active ? "Aktiv" : "Inaktiv"} />
        <DetailRow label="Kontakt" value={client?.primary_contact_email || "—"} />
        <DetailRow label="Slug" value={client?.slug} />
        <DetailRow label="Erstellt" value={client?.created_at ? formatDate(client.created_at) : "—"} />
        <DetailRow label="Workspace ID" value={client?.id} mono />
      </CardContent></Card>
      <Card className="dashboard-card"><CardHeader><CardTitle>Billing & Nutzung</CardTitle></CardHeader><CardContent className="space-y-2 text-sm">
        <DetailRow label="Zahlungsmethode" value={pm.data?.on_file ? `${pm.data.brand || "Karte"} ····${pm.data.last4 || ""}` : "Keine Karte hinterlegt"} />
        <DetailRow label="Agents" value={k.agents} />
        <DetailRow label="Calls" value={k.calls} />
        <DetailRow label="Minuten (gesamt)" value={k.minutes} />
        {cycle && <DetailRow label="Minuten (Abrechnungszeitraum)" value={`${cycle.total_minutes?.toFixed(1)} min · ${money(cycle.amount, cycle.currency)}`} />}
        <DetailRow label="Conversations" value={k.conversations} />
      </CardContent></Card>
    </div>
  );
}

function ClientDetailAgents({ api, clientId }) {
  const agents = useResource(api, `/agents?client_id=${clientId}`, [clientId]);
  const clients = useResource(api, "/clients");
  const [reassign, setReassign] = useState({});
  const [busy, setBusy] = useState("");
  const move = async (aid) => {
    const target = reassign[aid]; if (!target) return toast.error("Ziel-Client wählen");
    setBusy(aid);
    try { await api(`/agents/${aid}/reassign`, { method: "POST", body: JSON.stringify({ client_id: target }) }); toast.success("Agent verschoben"); agents.reload(); }
    catch (e) { toast.error(e.message); } finally { setBusy(""); }
  };
  return (
    <Card className="dashboard-card"><CardHeader><CardTitle>Zugewiesene Agents</CardTitle><CardDescription>Name, Plattform und Erstellungsdatum.</CardDescription></CardHeader><CardContent>
      {agents.loading ? <Skeleton className="h-40" /> : (agents.data || []).length ? (
        <Table data-testid="client-agents-table"><TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Platform</TableHead><TableHead>Erstellt</TableHead><TableHead>Verschieben zu</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>{agents.data.map((a) => (
            <TableRow key={a.id} data-testid="client-agent-row">
              <TableCell className="font-medium">{a.name}</TableCell>
              <TableCell><PlatformBadge provider={a.provider} /></TableCell>
              <TableCell className="text-sm text-muted-foreground">{a.created_at ? formatDate(a.created_at) : "—"}</TableCell>
              <TableCell><select className="form-select" value={reassign[a.id] || ""} onChange={(e) => setReassign((p) => ({ ...p, [a.id]: e.target.value }))}><option value="">—</option>{(clients.data || []).filter((c) => c.id !== clientId).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select></TableCell>
              <TableCell><Button size="sm" disabled={!reassign[a.id] || busy === a.id} onClick={() => move(a.id)} data-testid="client-agent-move">{busy === a.id ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : "Verschieben"}</Button></TableCell>
            </TableRow>
          ))}</TableBody></Table>
      ) : <EmptyState title="Keine Agents" description="Diesem Client sind keine Agents zugewiesen." />}
    </CardContent></Card>
  );
}

function ClientDetailPhone({ api, clientId }) {
  const phones = useResource(api, `/clients/${clientId}/phone-numbers`, [clientId]);
  const [syncing, setSyncing] = useState(false);
  const sync = async () => {
    setSyncing(true);
    try { const r = await api(`/clients/${clientId}/phone-numbers/sync`, { method: "POST" }); toast.success(`${r.synced} Nummer(n) synchronisiert`); phones.reload(); }
    catch (e) { toast.error(e.message); } finally { setSyncing(false); }
  };
  return (
    <Card className="dashboard-card"><CardHeader><div className="flex items-center justify-between gap-3"><div><CardTitle>Telefonnummern</CardTitle><CardDescription>Nummern und ihre Integration.</CardDescription></div><Button variant="outline" size="sm" onClick={sync} disabled={syncing} data-testid="phone-sync-button"><RefreshCw className={cx("h-4 w-4", syncing && "animate-spin")} /> Sync (ElevenLabs)</Button></div></CardHeader><CardContent>
      {phones.loading ? <Skeleton className="h-40" /> : (phones.data || []).length ? (
        <Table data-testid="client-phones-table"><TableHeader><TableRow><TableHead>Nummer</TableHead><TableHead>Integration</TableHead><TableHead>Agent</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
          <TableBody>{phones.data.map((p) => (
            <TableRow key={p.id} data-testid="client-phone-row"><TableCell className="font-mono">{p.number}</TableCell><TableCell><PlatformBadge provider={p.provider} /></TableCell><TableCell>{p.agent_name || "—"}</TableCell><TableCell><StatusBadge status={p.status} /></TableCell></TableRow>
          ))}</TableBody></Table>
      ) : <EmptyState title="Keine Telefonnummern" description="Klicke Sync, um Nummern von ElevenLabs zu importieren." />}
    </CardContent></Card>
  );
}

function ClientAccessTab({ api, clientId }) {
  const clientRes = useResource(api, `/clients/${clientId}`, [clientId]);
  const agentsRes = useResource(api, `/agents?client_id=${clientId}`, [clientId]);
  const [cfg, setCfg] = useState(blankAccessConfig());
  const [agentAccess, setAgentAccess] = useState({});
  const [selAgent, setSelAgent] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const c = clientRes.data;
    if (!c) return;
    if (c.access_config) {
      const b = blankAccessConfig();
      setCfg({
        ...b, ...c.access_config,
        analytics: { ...b.analytics, ...(c.access_config.analytics || {}), metrics: { ...b.analytics.metrics, ...((c.access_config.analytics || {}).metrics || {}) } },
        agent_settings: { ...b.agent_settings, ...(c.access_config.agent_settings || {}) },
      });
    } else setCfg(blankAccessConfig());
    setAgentAccess(c.agent_access || {});
  }, [clientRes.data]);

  const save = async () => {
    setSaving(true);
    try { await api(`/clients/${clientId}/access-config`, { method: "PUT", body: JSON.stringify({ access_config: cfg, agent_access: agentAccess }) }); toast.success("Zugriff gespeichert"); clientRes.reload(); }
    catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  const extraMetrics = (clientRes.data?.custom_metrics || []).map((m) => [m.key, m.label]);
  return (
    <div className="grid gap-5 xl:grid-cols-2">
      <Card className="dashboard-card"><CardHeader><CardTitle>Standard-Zugriff</CardTitle><CardDescription>Gilt für alle Agents, sofern nicht überschrieben.</CardDescription></CardHeader><CardContent>
        <AccessConfigEditor value={cfg} onChange={setCfg} extraMetrics={extraMetrics} />
      </CardContent></Card>
      <Card className="dashboard-card"><CardHeader><CardTitle>Pro-Agent-Override</CardTitle><CardDescription>Überschreibt den Standard für einen Agent.</CardDescription></CardHeader><CardContent className="space-y-4">
        <Field label="Agent"><select className="form-select" data-testid="agent-override-select" value={selAgent} onChange={(e) => setSelAgent(e.target.value)}><option value="">Standard (kein Override)</option>{(agentsRes.data || []).map((a) => <option key={a.id} value={a.id}>{a.name}{agentAccess[a.id] ? " ✓" : ""}</option>)}</select></Field>
        {selAgent ? (<>
          <AccessConfigEditor value={agentAccess[selAgent] || blankAccessConfig()} onChange={(v) => setAgentAccess((p) => ({ ...p, [selAgent]: v }))} extraMetrics={extraMetrics} scope="agent" />
          <Button variant="ghost" size="sm" onClick={() => setAgentAccess((p) => { const n = { ...p }; delete n[selAgent]; return n; })}>Override entfernen</Button>
        </>) : <p className="text-sm text-muted-foreground">Wähle einen Agent, um einen Override zu konfigurieren.</p>}
      </CardContent></Card>
      <div className="xl:col-span-2"><Button onClick={save} disabled={saving} data-testid="access-config-save">{saving ? <RefreshCw className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />} Zugriff speichern</Button></div>
    </div>
  );
}

function ClientMembersTab({ api, clientId }) {
  const membersRes = useResource(api, `/clients/${clientId}/members`, [clientId]);
  const clientRes = useResource(api, `/clients/${clientId}`, [clientId]);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [access, setAccess] = useState(blankAccessConfig());
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editAccess, setEditAccess] = useState(blankAccessConfig());

  const hydrate = (ma) => {
    const b = blankAccessConfig();
    if (!ma) return b;
    return { ...b, ...ma,
      analytics: { ...b.analytics, ...(ma.analytics || {}), metrics: { ...b.analytics.metrics, ...((ma.analytics || {}).metrics || {}) } },
      agent_settings: { ...b.agent_settings, ...(ma.agent_settings || {}) } };
  };

  const createMember = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api(`/clients/${clientId}/members`, { method: "POST", body: JSON.stringify({ name: form.name || null, email: form.email, password: form.password, member_access: access }) });
      toast.success("Mitglied angelegt");
      setForm({ name: "", email: "", password: "" }); setAccess(blankAccessConfig()); membersRes.reload();
    } catch (err) { toast.error(err.message); } finally { setSaving(false); }
  };

  const startEdit = (m) => { setEditAccess(hydrate(m.member_access)); setEditId(m.id); };
  const saveEdit = async () => {
    try { await api(`/clients/${clientId}/members/${editId}`, { method: "PATCH", body: JSON.stringify({ member_access: editAccess }) }); toast.success("Rechte aktualisiert"); setEditId(null); membersRes.reload(); }
    catch (err) { toast.error(err.message); }
  };
  const deactivate = async (m) => {
    if (typeof window !== "undefined" && !window.confirm(`Mitglied "${m.email}" deaktivieren? Es kann sich dann nicht mehr einloggen.`)) return;
    try { await api(`/clients/${clientId}/members/${m.id}`, { method: "DELETE" }); toast.success("Mitglied deaktiviert"); if (editId === m.id) setEditId(null); membersRes.reload(); }
    catch (err) { toast.error(err.message); }
  };
  const reactivate = async (m) => {
    try { await api(`/clients/${clientId}/members/${m.id}`, { method: "PATCH", body: JSON.stringify({ is_active: true }) }); toast.success("Mitglied reaktiviert"); membersRes.reload(); }
    catch (err) { toast.error(err.message); }
  };
  // Hard delete (DSGVO): removes the login, its rights and any open Passwort-Links. No undo.
  const hardDelete = async (m) => {
    if (typeof window !== "undefined" && !window.confirm(`Mitglied "${m.email}" endgültig löschen? Zugang, Agenten-Rechte und offene Passwort-Links werden entfernt. Das kann nicht rückgängig gemacht werden.`)) return;
    try { await api(`/clients/${clientId}/members/${m.id}?hard=true`, { method: "DELETE" }); toast.success("Mitglied gelöscht"); if (editId === m.id) setEditId(null); membersRes.reload(); }
    catch (err) { toast.error(err.message); }
  };
  const invite = async (m) => {
    try { await api(`/clients/${clientId}/members/${m.id}/invite`, { method: "POST", body: JSON.stringify({ origin_url: window.location.origin }) }); toast.success("Einladung gesendet"); }
    catch (err) { toast.error(err.message); }
  };

  const members = membersRes.data || [];
  const extraMetrics = (clientRes.data?.custom_metrics || []).map((m) => [m.key, m.label]);
  return (
    <div className="grid gap-5 xl:grid-cols-[1fr_400px]">
      <Card className="dashboard-card"><CardHeader><CardTitle>Mitglieder</CardTitle><CardDescription>Team-Logins für diesen Client, je mit eigenen Rechten.</CardDescription></CardHeader><CardContent>
        {membersRes.loading ? <Skeleton className="h-40" /> : members.length ? (
          <Table data-testid="client-members-table"><TableHeader><TableRow><TableHead>Name</TableHead><TableHead>E-Mail</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>{members.map((m) => (
              <React.Fragment key={m.id}>
                <TableRow data-testid="client-member-row" className={m.is_active === false ? "opacity-50" : ""}>
                  <TableCell className="font-medium">{m.name || "—"}</TableCell>
                  <TableCell className="text-sm">{m.email}</TableCell>
                  <TableCell>{m.is_active === false ? <Badge variant="outline">inaktiv</Badge> : <StatusBadge status="active" />}</TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <Button variant="outline" size="sm" data-testid="member-edit-button" onClick={() => startEdit(m)}>Rechte</Button>
                    <Button variant="ghost" size="sm" className="ml-1" data-testid="member-invite-button" onClick={() => invite(m)}>Einladen</Button>
                    {m.is_active === false
                      ? <Button variant="ghost" size="sm" className="ml-1" data-testid="member-reactivate-button" onClick={() => reactivate(m)}>Reaktivieren</Button>
                      : <Button variant="ghost" size="sm" className="ml-1 text-red-600 hover:text-red-700" data-testid="member-deactivate-button" onClick={() => deactivate(m)}>Deaktivieren</Button>}
                    <Button variant="ghost" size="sm" className="ml-1 text-red-600 hover:text-red-700" data-testid="member-delete-button" onClick={() => hardDelete(m)}>Löschen</Button>
                  </TableCell>
                </TableRow>
                {editId === m.id ? (
                  <TableRow><TableCell colSpan={4}>
                    <div className="rounded-lg border p-3 bg-muted/40 space-y-3" data-testid="member-access-editor">
                      <div className="font-medium text-sm">Rechte für {m.email}</div>
                      <AccessConfigEditor value={editAccess} onChange={setEditAccess} extraMetrics={extraMetrics} />
                      <div className="flex gap-2"><Button size="sm" onClick={saveEdit} data-testid="member-access-save"><ShieldCheck className="h-4 w-4" /> Speichern</Button><Button size="sm" variant="outline" onClick={() => setEditId(null)}>Abbrechen</Button></div>
                    </div>
                  </TableCell></TableRow>
                ) : null}
              </React.Fragment>
            ))}</TableBody></Table>
        ) : <EmptyState title="Keine Mitglieder" description="Lege rechts das erste Team-Mitglied an." />}
      </CardContent></Card>
      <Card className="dashboard-card"><CardHeader><CardTitle>Mitglied hinzufügen</CardTitle><CardDescription>Name, E-Mail, Passwort + individuelle Rechte.</CardDescription></CardHeader><CardContent>
        <form className="space-y-4" onSubmit={createMember}>
          <Field label="Name"><Input className="bg-white" data-testid="member-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="z.B. Max (CEO)" /></Field>
          <Field label="E-Mail (Login)"><Input className="bg-white" type="email" required data-testid="member-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
          <Field label="Passwort"><Input className="bg-white" type="text" required minLength={8} data-testid="member-password-input" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="min. 8 Zeichen" /></Field>
          <div className="rounded-lg border p-3 space-y-2"><div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Rechte</div><AccessConfigEditor value={access} onChange={setAccess} extraMetrics={extraMetrics} /></div>
          <Button type="submit" disabled={saving} data-testid="member-create-button">{saving ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Mitglied anlegen</Button>
        </form>
      </CardContent></Card>
    </div>
  );
}

const AGG_OPTIONS = [
  ["count", "Anzahl (nicht leer)"], ["count_where_equals", "Anzahl, wenn = Wert"], ["sum", "Summe"],
  ["average", "Durchschnitt"], ["percent_true", "% wahr"], ["latest", "Letzter Wert"],
];

function slugifyMetricKey(label) {
  const base = (label || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 36);
  return "cm_" + (base || "metrik");
}

// Local-only row identity so React keys stay stable while rows are added/removed (never sent to the API).
const newRowId = () => (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `row_${Math.random().toString(36).slice(2)}`);

function ClientCustomMetricsTab({ api, clientId }) {
  const clientRes = useResource(api, `/clients/${clientId}`, [clientId]);
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (clientRes.data) setRows((clientRes.data.custom_metrics || []).map((r) => ({ ...r, _id: newRowId() }))); }, [clientRes.data]);

  const update = (i, patch) => setRows((p) => p.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const addRow = () => setRows((p) => [...p, { _id: newRowId(), key: "", label: "", source: "", aggregation: "count", value: "" }]);
  const removeRow = (i) => setRows((p) => p.filter((_, j) => j !== i));

  const save = async () => {
    setSaving(true);
    try {
      const payload = rows.map((r) => {
        const key = r.key && /^cm_[a-z0-9_]{1,40}$/.test(r.key) ? r.key : slugifyMetricKey(r.label);
        const out = { key, label: r.label, source: r.source, aggregation: r.aggregation };
        if (r.aggregation === "count_where_equals") out.value = r.value;
        if (r.filter_field) { out.filter_field = r.filter_field; out.filter_value = r.filter_value; }
        return out;
      });
      await api(`/clients/${clientId}/custom-metrics`, { method: "PUT", body: JSON.stringify({ custom_metrics: payload }) });
      toast.success("Custom Metrics gespeichert");
      clientRes.reload();
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  return (
    <Card className="dashboard-card"><CardHeader><CardTitle>Custom Metrics</CardTitle><CardDescription>Eigene Kennzahlen aus den Call-Analysedaten der Agenten. Feldpfad zeigt in <span className="font-mono">metadata.analysis.…</span> (z.B. <span className="font-mono">data_collection_results.appointment.value</span> oder <span className="font-mono">custom_analysis_data.outcome</span>). Sichtbarkeit pro Mitglied im Tab „Client Access" / „Members". Hinweis: Retell liefert Analysedaten nur für neue Anrufe.</CardDescription></CardHeader><CardContent className="space-y-3">
      {rows.map((r, i) => (
        <div key={r._id} className="grid gap-2 md:grid-cols-[1.1fr_1.5fr_1fr_0.9fr_auto] items-end rounded-lg border p-3" data-testid="custom-metric-row">
          <Field label="Bezeichnung"><Input className="bg-white" data-testid="custom-metric-label" value={r.label || ""} onChange={(e) => update(i, { label: e.target.value })} placeholder="z.B. Termine gebucht" /></Field>
          <Field label="Feldpfad"><Input className="bg-white font-mono text-xs" data-testid="custom-metric-source" value={r.source || ""} onChange={(e) => update(i, { source: e.target.value })} placeholder="data_collection_results.appointment.value" /></Field>
          <Field label="Aggregation"><select className="form-select" data-testid="custom-metric-agg" value={r.aggregation || "count"} onChange={(e) => update(i, { aggregation: e.target.value })}>{AGG_OPTIONS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></Field>
          {r.aggregation === "count_where_equals" ? <Field label="Wert"><Input className="bg-white" data-testid="custom-metric-value" value={r.value ?? ""} onChange={(e) => update(i, { value: e.target.value })} /></Field> : <div />}
          <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700" onClick={() => removeRow(i)} data-testid="custom-metric-remove">Entfernen</Button>
        </div>
      ))}
      {!rows.length ? <p className="text-sm text-muted-foreground">Noch keine eigenen Kennzahlen.</p> : null}
      <div className="flex gap-2">
        <Button variant="outline" onClick={addRow} data-testid="custom-metric-add"><Plus className="h-4 w-4" /> Metrik hinzufügen</Button>
        <Button onClick={save} disabled={saving} data-testid="custom-metric-save">{saving ? <RefreshCw className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />} Speichern</Button>
      </div>
    </CardContent></Card>
  );
}

function AdminClientDetail({ api, user }) {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const clientRes = useResource(api, `/clients/${clientId}`, [clientId]);
  const clientsRes = useResource(api, "/clients");  // Ziel-Liste für „Verschieben zu …"
  const [deletePreview, setDeletePreview] = useState(null);
  const [confirmValue, setConfirmValue] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [deleteResult, setDeleteResult] = useState(null);
  // Per agent: "detach" (Default) | "delete" | "reassign:<client id>". Never carried across clients.
  const [agentActions, setAgentActions] = useState({});
  // Never carry a preview or a typed confirmation from one client to the next (same reason as the
  // contacts sheet in omni.js: a late dry-run must not paint client A's counts over client B).
  useEffect(() => { setDeletePreview(null); setConfirmValue(""); setDeleteResult(null); setAgentActions({}); }, [clientId]);
  if (user.role !== "admin") return <Navigate to={`/clients/${user.client_id}/overview`} replace />;
  const client = clientRes.data;
  const TABS = [{ key: "overview", label: "Overview" }, { key: "agents", label: "Assigned Agents" }, { key: "phone", label: "Phone Numbers" }, { key: "members", label: "Members" }, { key: "access", label: "Client Access" }, { key: "metrics", label: "Custom Metrics" }];

  // Step 1: dry_run=true is a pure preview, it deletes nothing. Stamped with the client it
  // describes, so the confirm panel below can only ever be shown for that client.
  const previewDelete = async () => {
    const targetId = clientId;
    setConfirmValue("");
    setDeleteResult(null);
    setAgentActions({});
    try { setDeletePreview({ ...(await api(`/clients/${targetId}?dry_run=true`, { method: "DELETE" })), clientId: targetId }); }
    catch (err) { toast.error(err.message); }
  };
  // Built from the rendered rows, never from the raw state: an agent that has already been moved
  // drops out of the preview, and sending its id again would be refused by the backend.
  const buildAgentActions = (preview) => Object.fromEntries((preview?.provider_agents || []).map((a) => {
    const choice = agentActions[a.id] || "detach";
    return [a.id, choice.startsWith("reassign:")
      ? { action: "reassign", target_client_id: choice.slice("reassign:".length) }
      : { action: choice }];
  }));
  // Step 2: the real, irreversible run — aimed at the id the preview was computed for. A big
  // tenant needs several rounds (the backend erases one chunk per call), so an `incomplete` result
  // KEEPS the preview and the typed confirmation and offers "Weiter löschen" — clearing them would
  // mean re-typing the client id for every chunk.
  const runDelete = async (withProvider) => {
    const targetId = deletePreview?.clientId;
    if (!targetId) return;
    setDeleting(true);
    try {
      const result = await api(`/clients/${targetId}?dry_run=false&delete_provider=${withProvider}`, { method: "DELETE", body: JSON.stringify({ confirm: confirmValue, agent_actions: buildAgentActions(deletePreview) }) });
      setDeleteResult(result);
      if (result.status === "complete") toast.success("Client vollständig gelöscht");
      else if (result.status === "archived") toast.success("Client archiviert — Abrechnungsdaten bleiben erhalten");
      else if (result.status === "incomplete") {
        // Name what is actually left. "N Gespräch(e)" was wrong whenever the blocker was an agent
        // stuck at the provider — the operator retried conversations that were long gone.
        const left = Object.entries(result.leftover_rows || {}).map(([k, v]) => `${v}× ${k}`).join(", ");
        toast.warning(`Löschung unvollständig — offen: ${left || `${result.remaining || 0} Datensätze`}. Bitte „Weiter löschen" ausführen.`);
        // The rendered agent rows are stale now: verschobene und abgehängte Agenten fallen aus der
        // Liste. Re-fire the dry-run and reduce the selection to the agents that are still there.
        const fresh = await api(`/clients/${targetId}?dry_run=true`, { method: "DELETE" });
        setDeletePreview({ ...fresh, clientId: targetId });
        setAgentActions((prev) => Object.fromEntries((fresh.provider_agents || [])
          .filter((a) => prev[a.id]).map((a) => [a.id, prev[a.id]])));
      } else toast.error("Abbruch bei der Abrechnung — es wurde nichts gelöscht. Stripe prüfen und erneut versuchen.");
      // Ein abgehängter Agent nimmt weiter Anrufe an — die Nummer zeigt weiterhin auf ihn.
      const stranded = (result.agents_retained || []).filter((a) => a.action === "detach").flatMap((a) => a.phone_numbers || []).filter(Boolean);
      if (stranded.length) toast.warning(`Nummer(n) ${stranded.join(", ")} routen weiterhin auf einen behaltenen Agenten — beim Anbieter umhängen oder freigeben.`);
      // Gilt über ALLE Läufe dieser Löschung hinweg, nicht nur über diesen.
      if (result.provider_residue_confirmed) toast.warning("Beim Anbieter sind Daten offen geblieben — aus diesem Lauf darf KEINE Löschbestätigung (AVV § 10.1) erstellt werden. Details im Audit Log.");
      else if (result.provider_residue_unverifiable) toast.info("Hinweis: Wissensdatenbank-Inhalte können beim Anbieter nicht automatisch gelöscht werden (keine Anbieter-ID gespeichert).");
      if (result.status === "complete" || result.status === "archived") navigate("/admin/clients");
      else clientRes.reload();
    } catch (err) { toast.error(err.message); } finally { setDeleting(false); }
  };

  return (
    <PageMotion>
      <Button variant="ghost" size="sm" className="mb-3" onClick={() => navigate("/admin/clients")} data-testid="client-detail-back"><ArrowLeft className="h-4 w-4" /> Zurück zu Clients</Button>
      <PageHeader eyebrow="Client" title={client?.name || "Client"} description={client ? `${client.is_active ? "Aktiv" : "Inaktiv"} · ${client.primary_contact_email || "keine E-Mail"}` : "Lädt…"} action={<div className="flex gap-2"><Button variant="outline" data-testid="client-detail-preview" onClick={() => navigate(`/admin/clients/${clientId}/preview`)}><Eye className="h-4 w-4" /> Preview</Button><Button variant="destructive" data-testid="client-delete-button" onClick={previewDelete}>Löschen</Button></div>} />
      {deletePreview?.clientId === clientId ? (
        <div className="rounded-md border border-destructive/40 p-3 space-y-2 text-sm mb-4" data-testid="client-delete-confirm">
          <p className="font-medium text-destructive">{client?.name || "Client"} endgültig löschen — nicht rückgängig zu machen.</p>
          {/* „Betroffen", nicht „gelöscht": would_delete is computed BEFORE any disposition exists,
              und die Gespräche eines Agenten, der unten auf „Verschieben" steht, wandern mit. */}
          <p className="text-muted-foreground">
            Betroffen: {deletePreview.would_delete?.conversations} Gespräch(e), {deletePreview.would_delete?.messages} Nachricht(en),
            {" "}{deletePreview.would_delete?.contacts} Kontakt(e), {deletePreview.would_delete?.app_users} Login(s).
            {" "}Kontakte und Logins werden gelöscht; Gespräche werden gelöscht, sofern ihr Agent unten nicht auf „Verschieben" gesetzt wird.
            {" "}Agenten: {deletePreview.would_delete?.agents} — was mit ihnen passiert, wird unten je Agent entschieden.
            {" "}{deletePreview.hinweis}
          </p>
          {deletePreview.provider_agents?.length ? (
            <div data-testid="client-delete-provider-agents" className="space-y-2">
              Agenten beim Sprachanbieter — bitte Namen prüfen, sie müssen zu diesem Client gehören:
              {/* Colour follows the CHOSEN action, not `shared`: red is what actually gets destroyed
                  at the provider, and that is now a per-agent decision. */}
              {deletePreview.provider_agents.map((a) => {
                const choice = agentActions[a.id] || "detach";
                return (
                  <div key={a.id} className={cx("rounded-md border p-2 space-y-1", choice === "delete" ? "border-destructive/60" : "border-muted")} data-testid="client-delete-agent-row">
                    <div className={choice === "delete" ? "text-destructive font-medium" : "font-medium"}>
                      {a.name || a.id} <span className="font-mono text-xs">{a.provider_agent_id || "— keine Anbieter-ID"}</span>
                    </div>
                    <select className="form-select" data-testid="client-delete-agent-action" value={choice}
                      onChange={(e) => setAgentActions((p) => ({ ...p, [a.id]: e.target.value }))}>
                      <option value="detach">Behalten (ohne Client)</option>
                      <option value="delete">Beim Anbieter löschen</option>
                      {(clientsRes.data || []).filter((c) => c.id !== clientId && c.is_active).map((c) => (
                        <option key={c.id} value={`reassign:${c.id}`}>Verschieben zu {c.name}</option>
                      ))}
                    </select>
                    {/* Ein früherer Lauf wollte diesen Agenten verschieben und ist dabei
                        gescheitert. Die Auswahl oben lebt nur in dieser Sitzung, also steht sie nach
                        einem Neuladen wieder auf „Behalten" — ohne diesen Hinweis würde der Umzug
                        stillschweigend zum Abhängen. */}
                    {a.prior_reassign_failed_target && !choice.startsWith("reassign:") ? (
                      <div className="text-xs text-destructive" data-testid="client-delete-agent-prior-reassign">
                        Ein früherer Lauf sollte diesen Agenten nach {(clientsRes.data || []).find((c) => c.id === a.prior_reassign_failed_target)?.name || a.prior_reassign_failed_target} verschieben und ist dabei gescheitert. Erneut „Verschieben zu …" wählen — sonst wird der Agent nur abgehängt und seine Gesprächshistorie gelöscht.
                      </div>
                    ) : null}
                    <div className="text-xs text-muted-foreground">
                      {choice === "delete"
                        ? (a.shared
                          ? "Wird beim Anbieter NICHT gelöscht: diese Anbieter-ID ist auch anderweitig hinterlegt (anderer Client oder ein Agent ohne Client). Die lokale Zeile wird entfernt."
                          : "Wird beim Anbieter GELÖSCHT — nicht rückgängig zu machen, eine daran hängende Telefonnummer geht mit.")
                        : choice === "detach"
                          ? "Bleibt bestehen und nimmt weiterhin Anrufe an — die Gespräche werden dann ohne Client geparkt. Telefonnummern routen weiter auf diesen Agenten."
                          : "Wird verschoben — die Gesprächshistorie wandert mit zum Ziel-Client. Abrechnungs- und Wissensdaten bleiben beim gelöschten Client."}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : null}
          <p className="text-muted-foreground">Zum Bestätigen die Client-ID eingeben: <span className="font-mono">{clientId}</span></p>
          <Input className="bg-white" value={confirmValue} onChange={(e) => setConfirmValue(e.target.value)} placeholder={clientId} data-testid="client-delete-confirm-input" />
          {deleteResult?.status === "incomplete" ? (
            <div className="text-muted-foreground" data-testid="client-delete-leftover">
              Noch offen: {Object.entries(deleteResult.leftover_rows || {}).map(([k, v]) => `${v}× ${k}`).join(", ") || `${deleteResult.remaining || 0} Datensätze`}.
              {/* Auf die REASON bedingt, nicht auf die Ressource: „reassign_failed" kommt ebenfalls
                  als resource „agent" — dort ist der Agent beim Anbieter unangetastet, und „manuell
                  löschen" wäre die eine unwiderrufliche Handlung, die dieser Weg verhindern soll. */}
              {deleteResult.failures?.some((f) => f.resource === "agent" && f.reason === "provider_delete_unconfirmed")
                ? " Ein Agent konnte beim Sprachanbieter nicht gelöscht werden. „Ohne Anbieter-Löschung fortfahren\" schließt die lokale Löschung ab — der Agent bleibt dann beim Anbieter bestehen und ist dort manuell zu löschen."
                : ""}
              {deleteResult.failures?.some((f) => f.reason === "reassign_failed")
                ? " Verschieben fehlgeschlagen — „Weiter löschen\" wiederholt den Umzug. Der Agent und seine Gesprächshistorie sind unverändert; beim Sprachanbieter ist nichts zu tun."
                : ""}
            </div>
          ) : null}
          <div className="flex gap-2 flex-wrap">
            <Button variant="destructive" size="sm" disabled={deleting || !confirmValue.trim()} onClick={() => runDelete(true)} data-testid="client-delete-submit-button">
              {deleting ? "Löschen läuft…" : deleteResult?.status === "incomplete" ? "Weiter löschen" : "Endgültig löschen"}
            </Button>
            {deleteResult?.status === "incomplete" ? (
              <Button variant="outline" size="sm" disabled={deleting || !confirmValue.trim()} onClick={() => runDelete(false)} data-testid="client-delete-without-provider-button">Ohne Anbieter-Löschung fortfahren</Button>
            ) : null}
            <Button variant="outline" size="sm" disabled={deleting} onClick={() => { setDeletePreview(null); setDeleteResult(null); }}>Abbrechen</Button>
          </div>
        </div>
      ) : null}
      <Tabs value={tab} className="client-tabs"><TabsList className="tabs-list" data-testid="client-detail-tabs">{TABS.map((t) => <TabsTrigger key={t.key} value={t.key} onClick={() => setTab(t.key)} data-testid={`client-tab-${t.key}`}>{t.label}</TabsTrigger>)}</TabsList></Tabs>
      <div className="mt-5">
        {tab === "overview" ? <ClientDetailOverview api={api} client={client} clientId={clientId} /> : null}
        {tab === "agents" ? <ClientDetailAgents api={api} clientId={clientId} /> : null}
        {tab === "phone" ? <ClientDetailPhone api={api} clientId={clientId} /> : null}
        {tab === "members" ? <ClientMembersTab api={api} clientId={clientId} /> : null}
        {tab === "access" ? <ClientAccessTab api={api} clientId={clientId} /> : null}
        {tab === "metrics" ? <ClientCustomMetricsTab api={api} clientId={clientId} /> : null}
      </div>
    </PageMotion>
  );
}

const PROVIDER_LABELS = { elevenlabs: "ElevenLabs", retell: "Retell", vapi: "Vapi", voiceflow: "Voiceflow" };

function PlatformBadge({ provider }) {
  return (
    <Badge variant="outline" className="provider-badge" data-testid={`platform-badge-${provider || "unknown"}`}>
      {PROVIDER_LABELS[provider] || provider || "—"}
    </Badge>
  );
}

function AddAgentSheet({ open, onClose, api, clients, onImported }) {
  const [provider, setProvider] = useState("elevenlabs");
  const [clientId, setClientId] = useState("");
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState({});
  const [loaded, setLoaded] = useState(false);
  const [err, setErr] = useState("");
  // Voiceflow kennt keine Agent-Auflistung: ein „Agent" ist ein Projekt, und der Workspace-Key
  // zählt Projekte nicht auf. Statt der Auswahlliste werden Projekt-ID und Name eingegeben und der
  // Import-Payload direkt daraus gebaut (/agents/importable antwortet für Voiceflow mit 400).
  const [manual, setManual] = useState({ provider_agent_id: "", name: "", environment_id: "" });
  const isManual = provider === "voiceflow";

  const loadImportable = async () => {
    setLoading(true); setErr(""); setItems([]); setSelected({}); setLoaded(false);
    try {
      const res = await api(`/agents/importable?provider=${provider}`);
      setItems(res.agents || []); setLoaded(true);
    } catch (e) { setErr(e.message); } finally { setLoading(false); }
  };

  const doManualImport = async () => {
    if (!clientId) return toast.error("Bitte Client auswählen");
    if (!manual.provider_agent_id.trim() || !manual.name.trim()) return toast.error("Projekt-ID und Name werden benötigt");
    setLoading(true);
    try {
      const res = await api("/agents/import", { method: "POST", body: JSON.stringify({ provider, client_id: clientId, agents: [{ provider_agent_id: manual.provider_agent_id.trim(), name: manual.name.trim(), language: "de-DE", environment_id: manual.environment_id.trim() || null }] }) });
      if (res.skipped?.length) toast.error("Diese Projekt-ID ist bereits als Agent angelegt");
      else toast.success(`${res.created?.length || 0} Agent(s) importiert`);
      setManual({ provider_agent_id: "", name: "", environment_id: "" });
      onImported?.(); onClose();
    } catch (e) { toast.error(e.message); } finally { setLoading(false); }
  };

  const doImport = async () => {
    if (!clientId) return toast.error("Bitte Client auswählen");
    const chosen = items.filter((it) => selected[it.provider_agent_id] && !it.already_imported);
    if (!chosen.length) return toast.error("Keine Agents ausgewählt");
    setLoading(true);
    try {
      const res = await api("/agents/import", { method: "POST", body: JSON.stringify({ provider, client_id: clientId, agents: chosen.map((it) => ({ provider_agent_id: it.provider_agent_id, name: it.name, language: it.language || "de-DE" })) }) });
      toast.success(`${res.created?.length || 0} Agent(s) importiert${res.skipped?.length ? `, ${res.skipped.length} übersprungen` : ""}`);
      onImported?.(); onClose();
    } catch (e) { toast.error(e.message); } finally { setLoading(false); }
  };

  return (
    <Sheet open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="add-agent-sheet">
        <SheetHeader><SheetTitle>Add Agent</SheetTitle><SheetDescription>Importiere Agents von deinem Voice-Provider via API.</SheetDescription></SheetHeader>
        <div className="space-y-4 mt-5">
          <Field label="Platform">
            <select className="form-select" data-testid="import-provider-select" value={provider} onChange={(e) => { setProvider(e.target.value); setLoaded(false); setItems([]); }}>
              <option value="elevenlabs">ElevenLabs</option>
              <option value="retell">Retell</option>
              <option value="vapi">Vapi</option>
              <option value="voiceflow">Voiceflow</option>
            </select>
          </Field>
          <Field label="Assign to Client">
            <select className="form-select" data-testid="import-client-select" value={clientId} onChange={(e) => setClientId(e.target.value)}>
              <option value="">Client wählen…</option>
              {(clients || []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </Field>
          {isManual ? (
            <div className="space-y-4" data-testid="manual-agent-form">
              <Field label="Voiceflow Projekt-ID"><Input className="bg-white" data-testid="manual-agent-id-input" value={manual.provider_agent_id} onChange={(e) => setManual({ ...manual, provider_agent_id: e.target.value })} placeholder="z.B. 6612ab…" /></Field>
              <Field label="Agent-Name"><Input className="bg-white" data-testid="manual-agent-name-input" value={manual.name} onChange={(e) => setManual({ ...manual, name: e.target.value })} placeholder="Praxis-Chat" /></Field>
              {/* Ohne Environment-ID landen auch Development- und Test-Sessions desselben Projekts
                  als echte Chats in der Kundenhistorie. Optional und fail-open: ein Transkript ohne
                  environmentID wird weiterhin übernommen. */}
              <Field label="Environment-ID (optional)"><Input className="bg-white" data-testid="manual-agent-environment-input" value={manual.environment_id} onChange={(e) => setManual({ ...manual, environment_id: e.target.value })} placeholder="Produktions-Environment, sonst alle" /></Field>
              <Button onClick={doManualImport} disabled={loading || !clientId} data-testid="manual-import-button"><Plus className="h-4 w-4" /> Agent anlegen</Button>
            </div>
          ) : (
            <Button variant="outline" onClick={loadImportable} disabled={loading} data-testid="import-load-button">
              {loading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} Verfügbare Agents laden
            </Button>
          )}
          {err ? <div className="error-alert" data-testid="import-error">{err}</div> : null}
          {loaded && !items.length ? <p className="text-sm text-muted-foreground">Keine importierbaren Agents gefunden.</p> : null}
          {items.length ? (
            <div className="space-y-2" data-testid="importable-list">
              {items.map((it) => (
                <label key={it.provider_agent_id} className={cx("flex items-center gap-3 rounded-md border p-2.5 text-sm", it.already_imported && "opacity-50")}>
                  <input type="checkbox" disabled={it.already_imported} checked={!!selected[it.provider_agent_id]} onChange={(e) => setSelected((p) => ({ ...p, [it.provider_agent_id]: e.target.checked }))} data-testid="importable-checkbox" />
                  <span className="font-medium flex-1">{it.name}</span>
                  {it.already_imported ? <Badge variant="outline">bereits importiert</Badge> : null}
                </label>
              ))}
              <Button onClick={doImport} disabled={loading || !clientId} data-testid="import-confirm-button"><Plus className="h-4 w-4" /> Ausgewählte importieren</Button>
            </div>
          ) : null}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function AdminAgents({ api, user }) {
  const navigate = useNavigate();
  const agentsRes = useResource(api, "/agents");
  const clientsRes = useResource(api, "/clients");
  const [importOpen, setImportOpen] = useState(false);

  if (user.role !== "admin") return <Navigate to={`/clients/${user.client_id}/overview`} replace />;
  const agents = agentsRes.data || [];

  return (
    <PageMotion>
      <PageHeader eyebrow="Admin" title="Agents" description="Alle Voice-Agents über deine Clients hinweg verwalten." action={<Button data-testid="add-agent-button" onClick={() => setImportOpen(true)}><Plus className="h-4 w-4" /> Add Agent</Button>} />
      <Card className="dashboard-card">
        <CardHeader><CardTitle>Alle Agents</CardTitle><CardDescription>Name, Plattform und zugewiesener Client.</CardDescription></CardHeader>
        <CardContent>
          {agentsRes.loading ? <Skeleton className="h-40 w-full" data-testid="state-loading" /> : agents.length ? (
            <Table data-testid="admin-agents-table">
              <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Platform</TableHead><TableHead>Assigned Client</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
              <TableBody>
                {agents.map((a) => (
                  <TableRow key={a.id} data-testid="admin-agent-row" className="cursor-pointer" onClick={() => navigate(`/admin/agents/${a.id}`)}>
                    <TableCell className="font-medium">{a.name}</TableCell>
                    <TableCell><PlatformBadge provider={a.provider} /></TableCell>
                    <TableCell className="text-sm text-muted-foreground">{a.client_name || "—"}</TableCell>
                    <TableCell><StatusBadge status={a.is_active ? "active" : "inactive"} /></TableCell>
                    <TableCell className="text-right"><Button variant="outline" size="sm" data-testid="admin-agent-open-button" onClick={(e) => { e.stopPropagation(); navigate(`/admin/agents/${a.id}`); }}>Öffnen</Button></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : <EmptyState title="Keine Agents" description="Importiere Agents über „Add Agent“." action={<Button onClick={() => setImportOpen(true)}><Plus className="h-4 w-4" /> Add Agent</Button>} />}
        </CardContent>
      </Card>
      <AddAgentSheet open={importOpen} onClose={() => setImportOpen(false)} api={api} clients={clientsRes.data || []} onImported={() => agentsRes.reload()} />
    </PageMotion>
  );
}

function AgentDetailPage({ api, user }) {
  const { agentId } = useParams();
  const navigate = useNavigate();
  const agentRes = useResource(api, `/agents/${agentId}`);
  // Falsy path = no request: this page redirects non-admins below, and the client list is admin-only
  // data. Hooks cannot move past the redirect, so the SKIP is what keeps it off a client's wire.
  const clientsRes = useResource(api, user.role === "admin" ? "/clients" : null);
  const [cfg, setCfg] = useState(null);
  const [draft, setDraft] = useState({ prompt: "", first_message: "", language: "", llm: "" });
  const [loadingCfg, setLoadingCfg] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [assignTo, setAssignTo] = useState("");
  const [assigning, setAssigning] = useState(false);

  const loadCfg = useCallback(async () => {
    setLoadingCfg(true);
    try {
      const res = await api(`/agents/${agentId}/provider-config`);
      setCfg(res);
      setDraft({ prompt: res.prompt || "", first_message: res.first_message || "", language: res.language || "", llm: res.llm || "" });
    } catch (e) {
      setCfg({ status: "error", detail: e.message });
    } finally { setLoadingCfg(false); }
  }, [api, agentId]);
  useEffect(() => { loadCfg(); }, [loadCfg]);

  if (user.role !== "admin") return <Navigate to={`/clients/${user.client_id}/overview`} replace />;

  const agent = agentRes.data;
  const editable = agent?.provider === "elevenlabs";
  const tools = (cfg && (cfg.tools || (cfg.config && cfg.config.tools))) || [];

  const saveDraft = async () => {
    setSaving(true);
    try { await api(`/agents/${agentId}/provider-config`, { method: "PUT", body: JSON.stringify({ ...draft, config: {} }) }); toast.success("Entwurf gespeichert"); }
    catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };
  const pushCfg = async () => {
    setPushing(true);
    try {
      // Persist the live edits as the draft first, then push that draft to the provider
      // (the push endpoint applies the stored draft, so an unsaved edit would otherwise be lost).
      await api(`/agents/${agentId}/provider-config`, { method: "PUT", body: JSON.stringify({ ...draft, config: {} }) });
      await api(`/agents/${agentId}/provider-config/push`, { method: "POST" });
      toast.success("Konfiguration an Provider übertragen"); loadCfg();
    }
    catch (e) { toast.error(e.message); } finally { setPushing(false); }
  };
  // Der einzige Weg zurück für einen Agenten ohne Client. "__detach__" hängt ihn ab: die
  // Gesprächshistorie bleibt beim bisherigen Client, nur künftige Anrufe werden geparkt.
  const assignClient = async () => {
    if (!assignTo) return toast.error("Bitte Client wählen");
    const detach = assignTo === "__detach__";
    if (detach && typeof window !== "undefined" && !window.confirm("Agent ohne Client: künftige Anrufe dieses Agenten erscheinen nicht mehr im Portal des bisherigen Clients. Die bisherige Historie bleibt dort. Fortfahren?")) return;
    setAssigning(true);
    try {
      await api(`/agents/${agentId}/reassign`, { method: "POST", body: JSON.stringify({ client_id: detach ? null : assignTo }) });
      toast.success(detach ? "Agent abgehängt" : "Agent zugewiesen");
      setAssignTo(""); agentRes.reload();
    } catch (e) { toast.error(e.message); } finally { setAssigning(false); }
  };

  return (
    <PageMotion>
      <Button variant="ghost" size="sm" className="mb-3" onClick={() => navigate("/admin/agents")} data-testid="agent-back-button"><ArrowLeft className="h-4 w-4" /> Zurück zu Agents</Button>
      <PageHeader eyebrow="Agent" title={agent?.name || "Agent"} description={agent ? `${PROVIDER_LABELS[agent.provider] || agent.provider} · ${agent.client_name || "—"}` : "Lädt…"} action={editable ? <Button variant="outline" onClick={loadCfg} data-testid="agent-reload-config"><RefreshCw className="h-4 w-4" /> Neu laden</Button> : null} />
      <div className="grid gap-5 xl:grid-cols-[1fr_320px]">
        <Card className="dashboard-card">
          <CardHeader><CardTitle>Agent Konfiguration</CardTitle><CardDescription>System Prompt, First Message, Sprache, LLM und Functions.</CardDescription></CardHeader>
          <CardContent className="space-y-4">
            {loadingCfg ? <Skeleton className="h-60 w-full" data-testid="state-loading" /> : (
              <>
                {!editable ? <div className="error-alert" data-testid="agent-config-unavailable">Konfiguration kann aktuell nur für ElevenLabs-Agents bearbeitet werden{cfg?.status ? ` (${cfg.status})` : ""}.</div> : null}
                <Field label="System Prompt"><Textarea rows={8} className="bg-white" data-testid="agent-prompt-input" value={draft.prompt} onChange={(e) => setDraft({ ...draft, prompt: e.target.value })} disabled={!editable} /></Field>
                <Field label="First Message"><Input className="bg-white" data-testid="agent-firstmessage-input" value={draft.first_message} onChange={(e) => setDraft({ ...draft, first_message: e.target.value })} disabled={!editable} /></Field>
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Language"><Input className="bg-white" data-testid="agent-language-input" value={draft.language} onChange={(e) => setDraft({ ...draft, language: e.target.value })} disabled={!editable} /></Field>
                  <Field label="LLM Provider"><Input className="bg-white" data-testid="agent-llm-input" value={draft.llm} onChange={(e) => setDraft({ ...draft, llm: e.target.value })} disabled={!editable} /></Field>
                </div>
                <Field label="Functions / Tools">
                  <pre className="whitespace-pre-wrap rounded-md border bg-muted/40 p-3 text-xs font-mono max-h-64 overflow-auto" data-testid="agent-functions">{tools && tools.length ? JSON.stringify(tools, null, 2) : "Keine Functions konfiguriert."}</pre>
                </Field>
                {editable ? (
                  <div className="flex gap-2">
                    <Button onClick={saveDraft} disabled={saving} data-testid="agent-save-config"><CheckCircle2 className="h-4 w-4" /> Entwurf speichern</Button>
                    <Button variant="outline" onClick={pushCfg} disabled={pushing} data-testid="agent-push-config"><Sparkles className="h-4 w-4" /> An Provider übertragen</Button>
                  </div>
                ) : null}
              </>
            )}
          </CardContent>
        </Card>
        <Card className="dashboard-card">
          <CardHeader><CardTitle>Details</CardTitle></CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex justify-between gap-3"><span className="text-muted-foreground">Platform</span><span>{agent ? (PROVIDER_LABELS[agent.provider] || agent.provider) : "—"}</span></div>
            <div className="flex justify-between gap-3"><span className="text-muted-foreground">Provider Agent ID</span><span className="font-mono text-xs truncate max-w-[170px]">{agent?.provider_agent_id || "—"}</span></div>
            <div className="flex justify-between gap-3"><span className="text-muted-foreground">Client</span><span>{agent?.client_name || "—"}</span></div>
            <div className="flex justify-between gap-3"><span className="text-muted-foreground">Sync Status</span><span>{cfg?.status || "—"}</span></div>
            <Field label="Client zuweisen">
              <select className="form-select" data-testid="agent-assign-client-select" value={assignTo} onChange={(e) => setAssignTo(e.target.value)}>
                <option value="">Client wählen…</option>
                {(clientsRes.data || []).filter((c) => c.id !== agent?.client_id && c.is_active).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                {agent?.client_id ? <option value="__detach__">Ohne Client (abhängen)</option> : null}
              </select>
              <span className="text-xs text-muted-foreground">{agent?.client_id
                ? "Beim Zuweisen wandern Gespräche, Nutzungs- und Wissensdaten dieses Agenten mit."
                : "Beim Zuweisen wandern nur die Gespräche mit, die ohne Client geparkt wurden. Nutzungsdaten aus dieser Zeit wandern nicht mit und bleiben unabgerechnet."}</span>
            </Field>
            <Button size="sm" disabled={!assignTo || assigning} onClick={assignClient} data-testid="agent-assign-client-button">{assigning ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : "Zuweisen"}</Button>
          </CardContent>
        </Card>
      </div>
    </PageMotion>
  );
}

function CardGateScreen({ api, clientId, user, clientName }) {
  const [email, setEmail] = useState(user?.email || "");
  const [loading, setLoading] = useState(false);
  const proceed = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    setLoading(true);
    try {
      const res = await api("/billing/setup-session", { method: "POST", body: JSON.stringify({ client_id: clientId, origin_url: window.location.origin, email: email || null }) });
      if (res.url) window.location.href = res.url; else setLoading(false);
    } catch (err) { toast.error(err.message); setLoading(false); }
  };
  return (
    <div className="card-gate" data-testid="card-gate">
      <Card className="login-card card-gate-card">
        <CardHeader>
          <div className="brand-mark small" style={{ marginBottom: ".85rem" }}><CreditCard className="h-5 w-5" /></div>
          <CardTitle>Zahlungsmethode hinterlegen</CardTitle>
          <CardDescription>{clientName ? `${clientName}: ` : ""}Bitte hinterlege eine Karte, um dein Dashboard freizuschalten. Die Abrechnung erfolgt automatisch monatlich nach Nutzung.</CardDescription>
        </CardHeader>
        <CardContent>
          <form className="space-y-4" onSubmit={proceed}>
            <Field label="Rechnungs-E-Mail"><Input className="bg-white" type="email" data-testid="card-gate-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@firma.de" /><span className="text-xs text-muted-foreground">Die Quittung geht an diese Adresse.</span></Field>
            <Button type="submit" disabled={loading} className="w-full" data-testid="card-gate-submit">{loading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />} Weiter zu Stripe</Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

function ClientPage({ section, api, user }) {
  const { clientId } = useParams();
  const clientState = useResource(api, `/clients/${clientId}`, [clientId]);
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";
  const meAccess = useResource(api, !isAdmin ? "/me/access" : null);
  const pm = useResource(api, !isAdmin ? "/billing/payment-method" : null);
  const cfg = useResource(api, !isAdmin ? "/billing/config" : null);
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("setup")) pm.reload();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Hard card gate: a member cannot use the portal until a payment method is on file.
  if (!isAdmin) {
    if (clientState.loading || meAccess.loading || pm.loading || cfg.loading) {
      return <PageMotion><div className="auth-loading" data-testid="state-loading"><RefreshCw className="h-4 w-4 animate-spin" /> Lädt…</div></PageMotion>;
    }
    if (cfg.data?.stripe_configured && pm.data?.on_file === false) {
      return <CardGateScreen api={api} clientId={clientId} user={user} clientName={clientState.data?.name} />;
    }
  }

  const acc = isAdmin ? null : meAccess.data;
  const vis = visibleTabKeys(acc);
  // Admin always sees the omni-channel UI (mirrors the server's admin-exempt-from-flag pattern);
  // a client user only sees it once the client's `inbox` feature flag is granted.
  const showInbox = isAdmin || !!acc?.channels?.inbox;
  // Art. 15/20 + Art. 17 per member (deny-by-default, server-side gate in require_gdpr). Derived
  // here — `acc` is deliberately null for the agency admin, so leaves must never read it raw.
  // The client_admin bypass is repeated from require_gdpr rather than read off `acc`: a failed
  // /me/access leaves `acc` null, and without this floor it would strip a client_admin of the
  // Art. 15 self-service the server still grants them.
  const isClientAdmin = user?.role === "client_admin";
  const canGdpr = { export: isAdmin || isClientAdmin || !!acc?.gdpr?.export, erase: isAdmin || isClientAdmin || !!acc?.gdpr?.erase };
  const tabs = navItems
    .filter((item) => !vis || vis[item.key] !== false)
    .map((item) => ({ ...item, url: `/clients/${clientId}/${item.path}` }));
  return (
    <PageMotion>
      <PageHeader eyebrow="Client-Bereich" title={clientState.data?.name || "Client"} description="Agenten-Betrieb, Gespräche, Wissensquellen, Workflows und Nutzung in einem Bereich." action={isAdmin ? <ProviderSetup clientId={clientId} /> : null} />
      <Tabs value={section} className="client-tabs">
        <TabsList className="tabs-list" data-testid="client-section-tabs">
          {tabs.map((tab) => <TabsTrigger key={tab.key} value={tab.key} onClick={() => navigate(tab.url)} data-testid={`tab-${tab.key}`}>{tab.label}</TabsTrigger>)}
        </TabsList>
      </Tabs>
      {section === "overview" ? <Overview api={api} clientId={clientId} /> : null}
      {section === "agents" ? <Agents api={api} clientId={clientId} user={user} /> : null}
      {section === "prompt" ? <PromptPanel api={api} clientId={clientId} user={user} /> : null}
      {section === "conversations" ? <Conversations api={api} clientId={clientId} showInbox={showInbox} canGdpr={canGdpr} /> : null}
      {section === "contacts" && showInbox ? <ContactsPage api={api} clientId={clientId} canGdpr={canGdpr} /> : null}
      {section === "knowledge-base" ? <KnowledgeBase api={api} clientId={clientId} user={user} /> : null}
      {section === "billing" ? <BillingPage api={api} clientId={clientId} user={user} /> : null}
      {section === "datenschutz" ? <DatenschutzPage api={api} clientId={clientId} canGdpr={canGdpr} /> : null}
      {section === "settings" ? <SettingsPage api={api} client={clientState.data} clientId={clientId} user={user} canGdpr={canGdpr} /> : null}
    </PageMotion>
  );
}

function ProviderSetup({ clientId }) {
  const base = BACKEND_URL;
  return (
    <div className="provider-setup-mini" data-testid="provider-setup-card">
      <Webhook className="h-4 w-4" />
      <span className="font-mono">{base}/api/webhooks/retell</span>
      <span className="separator-dot" />
      <span className="font-mono">{base}/api/webhooks/elevenlabs</span>
    </div>
  );
}

function Field({ label, children }) {
  return <div className="space-y-2"><Label>{label}</Label>{children}</div>;
}

function KpiTile({ testId, title, value, subtitle, icon: Icon, tone = "default" }) {
  return (
    <Card className="dashboard-card kpi-card" data-testid={testId}>
      <CardContent className="p-5">
        <div className="kpi-top"><span>{title}</span><div className={cx("kpi-icon", tone)}><Icon className="h-4 w-4" /></div></div>
        <div className="kpi-number">{value}</div>
        <p className="kpi-subtitle">{subtitle}</p>
      </CardContent>
    </Card>
  );
}

function Overview({ api, clientId }) {
  const [agentId, setAgentId] = useState("");
  useEffect(() => { setAgentId(""); }, [clientId]); // reset filter when switching client (avoid stale agent_id)
  const aq = agentId ? `&agent_id=${agentId}` : "";
  const summary = useResource(api, `/usage/summary?client_id=${clientId}${aq}`, [clientId, agentId]);
  const timeseries = useResource(api, `/usage/timeseries?client_id=${clientId}${aq}`, [clientId, agentId]);
  const conversations = useResource(api, `/conversations?client_id=${clientId}&limit=6${aq}`, [clientId, agentId]);
  const agents = useResource(api, `/agents?client_id=${clientId}`, [clientId]);

  if (summary.loading && !summary.data) return <LoadingCards />;
  const kpi = summary.data || {};
  const chartData = timeseries.data || [];

  return (
    <div className="space-y-5">
      {(agents.data || []).length > 1 ? (
        <div className="flex items-center justify-end">
          <select className="form-select w-auto" data-testid="overview-agent-select" value={agentId} onChange={(e) => setAgentId(e.target.value)}>
            <option value="">Gesamt (alle Agenten)</option>
            {(agents.data || []).map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        </div>
      ) : null}
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <KpiTile testId="kpi-total-calls" title="Anrufe" value={kpi.calls || 0} subtitle="Abgeschlossene und laufende Gespräche" icon={PhoneCall} tone="info" />
        <KpiTile testId="kpi-total-minutes" title="Minuten" value={kpi.minutes || 0} subtitle="Nutzungsbasis für die Abrechnung" icon={Clock} tone="success" />
        <KpiTile testId="kpi-success-rate" title="Erfolgsquote" value={`${kpi.success_rate || 0}%`} subtitle="Abgeschlossene Gespräche" icon={CheckCircle2} tone="success" />
        <KpiTile testId="kpi-active-agents" title="Agenten" value={kpi.agents || 0} subtitle="Aktive Agenten" icon={Bot} tone="default" />
      </div>
      {(kpi.custom_metrics || []).length ? (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4" data-testid="custom-metrics-grid">
          {kpi.custom_metrics.map((m) => <KpiTile key={m.key} testId={`kpi-${m.key}`} title={m.label} value={m.value ?? "—"} subtitle="Eigene Kennzahl" icon={Gauge} tone="default" />)}
        </div>
      ) : null}
      <div className="grid gap-5 xl:grid-cols-[1.2fr_0.8fr]">
        <Card className="dashboard-card">
          <CardHeader><CardTitle>Nutzungsverlauf</CardTitle><CardDescription>Anrufe und Minuten pro Tag.</CardDescription></CardHeader>
          <CardContent className="h-[320px]" data-testid="usage-trend-chart">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData} margin={{ left: 0, right: 12, top: 12, bottom: 0 }}>
                <defs><linearGradient id="tealArea" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#5B51EC" stopOpacity={0.24} /><stop offset="95%" stopColor="#5B51EC" stopOpacity={0.03} /></linearGradient></defs>
                <CartesianGrid stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="date" tickLine={false} axisLine={false} tick={{ fontSize: 12 }} />
                <YAxis tickLine={false} axisLine={false} tick={{ fontSize: 12 }} />
                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #DDE3EA" }} />
                <Area type="monotone" dataKey="minutes" stroke="#5B51EC" fill="url(#tealArea)" strokeWidth={2} />
                <Bar dataKey="calls" fill="#CFCBF6" radius={[6, 6, 0, 0]} />
              </ComposedChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
        <Card className="dashboard-card">
          <CardHeader><CardTitle>Agenten-Übersicht</CardTitle><CardDescription>Aktive Agenten.</CardDescription></CardHeader>
          <CardContent>
            <div className="space-y-3">
              {(agents.data || []).map((agent) => (
                <div className="agent-mini-row" key={agent.id} data-testid="agent-mini-row">
                  <div><strong>{agent.name}</strong><span>{agent.language || ""}</span></div>
                  <StatusBadge status={agent.is_active ? "active" : "inactive"} />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
      <RecentConversations rows={conversations.data || []} clientId={clientId} />
    </div>
  );
}

function RecentConversations({ rows, clientId }) {
  return (
    <Card className="dashboard-card">
      <CardHeader><CardTitle>Letzte Gespräche</CardTitle><CardDescription>Neueste Anrufe und Ergebnisse.</CardDescription></CardHeader>
      <CardContent>
        {rows.length ? <ConversationTable rows={rows} clientId={clientId} compact /> : <EmptyState title="Keine Gespräche" description="Sende einen Provider-Webhook oder nutze Seed-Daten." />}
      </CardContent>
    </Card>
  );
}

// Ein Voiceflow-Sync antwortet sofort mit 202, der Toast kann also nur „gestartet" sagen. Das
// ERGEBNIS landet auf dem Agent-Dokument (metadata.last_sync_result) und steht hier — sonst ist ein
// Lauf, der sofort scheitert (vertippte Project-ID, 4xx auf dem allerersten List-Call), unsichtbar:
// „Letzter Sync" bleibt „—" und nirgendwo steht, warum.
const SYNC_RESULT_LABELS = {
  ok: () => "Sync ok",
  // Ein Lauf, der am Cap gestoppt hat, ist nicht „ok": Voiceflow hält noch mehr von diesem Fenster,
  // als wir geholt haben. Der nächste Zyklus macht dort weiter — deshalb ein Hinweis, kein Fehler.
  stalled: () => "Sync unvollständig — noch nicht alle Chats geholt",
  // Beim Start gestempelt, damit der Refetch ~3 s nach dem 202 nicht das Ergebnis des VORHERIGEN
  // Laufs unter „Sync gestartet" rendert. Ein harter Kill überspringt das `finally` im Backend und
  // lässt „läuft" stehen; der 5-Minuten-Zyklus heilt nur aktive Agenten (bewusst in Kauf genommen).
  running: () => "Sync läuft …",
  partial_failure: (r) => `Sync unvollständig — ${r.failed} Fehler`,
  error: (r) => `Sync fehlgeschlagen: ${r.error || "unbekannter Fehler"}`,
  cancelled: () => "Sync abgebrochen (Neustart?)",
};
// Status, die keine Störung sind und deshalb nicht rot gerendert werden. „running" muss hier stehen,
// sonst liest sich der frisch gestartete Lauf als gescheitert — genau der Fehler, den der Stempel
// beseitigen soll.
const SYNC_RESULT_NEUTRAL = new Set(["ok", "running"]);

function LastSyncResult({ result }) {
  if (!result?.status) return null;
  const label = (SYNC_RESULT_LABELS[result.status] || ((r) => `Sync: ${r.status}`))(result);
  return <div data-testid="agent-sync-result" title={result.finished_at || ""} className={SYNC_RESULT_NEUTRAL.has(result.status) ? "" : "text-destructive"}>{label}</div>;
}

// Der 202 kommt, BEVOR der Hintergrund-Lauf irgendetwas geschrieben hat. Ein Lauf, der sofort
// stirbt (vertippte Project-ID, 4xx auf dem ersten List-Call), legt sein Ergebnis rund eine Sekunde
// später auf dem Agenten ab — und ohne ein zweites Laden sieht der Klickende bis zum nächsten
// Seitenwechsel nur „Sync gestartet …" und eine unveränderte Zeile.
const SYNC_RESULT_REFETCH_MS = 3000;

function Agents({ api, clientId, user }) {
  const agents = useResource(api, `/agents?client_id=${clientId}`, [clientId]);
  const canEdit = user.role !== "client_user";
  const [syncingId, setSyncingId] = useState("");
  const refetchTimer = useRef(null);
  useEffect(() => () => clearTimeout(refetchTimer.current), []);

  const syncAgent = async (agentId) => {
    setSyncingId(agentId);
    try {
      const res = await api(`/agents/${agentId}/sync`, { method: "POST" });
      // Voiceflow answers 202: the import runs in the background (a first sync can take minutes),
      // so there are no numbers to report yet. ElevenLabs stays synchronous and unchanged.
      if (res.status === "started") {
        toast.info("Sync gestartet – er läuft im Hintergrund und kann einige Minuten dauern.");
        clearTimeout(refetchTimer.current);
        refetchTimer.current = setTimeout(() => agents.reload(), SYNC_RESULT_REFETCH_MS);
      }
      else if (res.status === "already_running") toast.info("Für diesen Agenten läuft bereits ein Sync im Hintergrund.");
      else toast.success(`Sync: ${res.created} neu, ${res.updated} aktualisiert${res.failed ? `, ${res.failed} Fehler` : ""}`);
      agents.reload();
    } catch (err) { toast.error(err.message); }
    finally { setSyncingId(""); }
  };

  return (
    <Card className="dashboard-card">
      <CardHeader><CardTitle>Agenten</CardTitle><CardDescription>Sprache und Betriebsstatus deiner Agenten.</CardDescription></CardHeader>
      <CardContent>
        {agents.loading ? <Skeleton className="h-40" data-testid="state-loading" /> : agents.data?.length ? (
          <Table data-testid="agents-table"><TableHeader><TableRow><TableHead>Agent</TableHead><TableHead>Sprache</TableHead><TableHead>Status</TableHead><TableHead>Letzter Sync</TableHead><TableHead>Aktion</TableHead></TableRow></TableHeader><TableBody>
            {agents.data.map((agent) => <TableRow key={agent.id} data-testid="agent-row"><TableCell className="font-medium">{agent.name}</TableCell><TableCell>{agent.language}</TableCell><TableCell><StatusBadge status={agent.is_active ? "active" : "inactive"} /></TableCell><TableCell className="text-xs text-muted-foreground">{agent.last_synced_at ? new Date(agent.last_synced_at).toLocaleString() : "—"}<LastSyncResult result={agent.metadata?.last_sync_result} /></TableCell><TableCell>{["elevenlabs", "voiceflow"].includes(agent.provider) && canEdit ? <Button size="sm" variant="outline" disabled={syncingId === agent.id} onClick={() => syncAgent(agent.id)} data-testid="agent-sync-button"><RefreshCw className={`h-3.5 w-3.5 ${syncingId === agent.id ? "animate-spin" : ""}`} /> Sync</Button> : <span className="text-xs text-muted-foreground">—</span>}</TableCell></TableRow>)}
          </TableBody></Table>
        ) : <EmptyState title="Keine Agenten" description="Lege einen neuen Agenten an." />}
      </CardContent>
    </Card>
  );
}

// Own tab — only mounted when the member has prompt view/edit rights (see ClientPage tab filter).
function PromptPanel({ api, clientId, user }) {
  const agents = useResource(api, `/agents?client_id=${clientId}`, [clientId]);
  const isAdmin = user.role === "admin";
  const [selectedAgentId, setSelectedAgentId] = useState("");
  const [providerPrompt, setProviderPrompt] = useState("");
  const [providerStatus, setProviderStatus] = useState("");
  const [providerCanEdit, setProviderCanEdit] = useState(false);
  const [promptVisible, setPromptVisible] = useState(true);

  const fetchProviderConfig = async () => {
    if (!selectedAgentId) return toast.error("Bitte Agent auswählen");
    try {
      const config = await api(`/agents/${selectedAgentId}/provider-config`);
      setPromptVisible(config.prompt !== undefined);   // server strips `prompt` if no view right for this agent
      setProviderPrompt(config.prompt || "");
      setProviderStatus(config.status || config.provider);
      setProviderCanEdit(!!config.can_edit_prompt);
      toast.success("Prompt geladen");
    } catch (err) { toast.error(err.message); }
  };
  const saveProviderDraft = async () => {
    if (!selectedAgentId) return toast.error("Bitte Agent auswählen");
    try {
      await api(`/agents/${selectedAgentId}/provider-config`, { method: "PUT", body: JSON.stringify({ prompt: providerPrompt, config: {} }) });
      setProviderStatus("needs_sync");
      toast.success("Prompt Draft gespeichert");
    } catch (err) { toast.error(err.message); }
  };
  const pushProviderConfig = async () => {
    if (!selectedAgentId) return toast.error("Bitte Agent auswählen");
    try {
      const result = await api(`/agents/${selectedAgentId}/provider-config/push`, { method: "POST" });
      setProviderStatus(result.status);
      toast.success("Prompt zum Provider gepusht");
    } catch (err) { toast.error(err.message); }
  };

  return (
    <Card className="dashboard-card"><CardHeader><CardTitle>Agent-Prompt &amp; Konfiguration</CardTitle><CardDescription>Sieh und bearbeite den Prompt deines Agenten.</CardDescription></CardHeader><CardContent className="space-y-4">
      <div className="grid gap-3 md:grid-cols-[260px_1fr_auto]">
        <select className="form-select" data-testid="provider-config-agent-select" value={selectedAgentId} onChange={(e) => setSelectedAgentId(e.target.value)}>
          <option value="">Agent wählen</option>
          {/* PROVIDER_LABELS, nicht ein binärer Ternary: der hat jeden Nicht-ElevenLabs-Agenten
              als „Retell AI" ausgewiesen — bei einem Voiceflow-Agenten schlicht falsch. */}
          {(agents.data || []).map((agent) => (
            <option key={agent.id} value={agent.id}>{agent.name}{isAdmin ? ` · ${PROVIDER_LABELS[agent.provider] || agent.provider}` : ""}</option>
          ))}
        </select>
        <Input className="bg-white" value={providerStatus} readOnly placeholder="Status" data-testid="provider-config-status" />
        <Button variant="outline" onClick={fetchProviderConfig} data-testid="provider-config-load-button">Prompt laden</Button>
      </div>
      {promptVisible ? (
        <Textarea className="bg-white min-h-[260px] font-mono" value={providerPrompt} onChange={(e) => setProviderPrompt(e.target.value)} placeholder="Prompt erscheint hier nach dem Laden..." data-testid="provider-prompt-textarea" />
      ) : (
        <p className="text-sm text-muted-foreground" data-testid="provider-prompt-no-access">Für diesen Agenten hast du keine Berechtigung, den Prompt zu sehen.</p>
      )}
      {providerCanEdit ? <div className="flex gap-2 flex-wrap"><Button variant="outline" onClick={saveProviderDraft} data-testid="provider-config-save-draft-button">Entwurf speichern</Button><Button onClick={pushProviderConfig} data-testid="provider-config-push-button">Veröffentlichen</Button></div> : null}
    </CardContent></Card>
  );
}

function ConversationTable({ rows, clientId, compact = false, showChannel = false }) {
  const navigate = useNavigate();
  const open = (id) => navigate(`/clients/${clientId}/conversations/${id}`);
  return (
    <Table data-testid="conversations-table">
      <TableHeader><TableRow>
        <TableHead>Datum</TableHead><TableHead>Agent</TableHead>
        {showChannel ? <TableHead>Kanal</TableHead> : null}
        <TableHead>Dauer</TableHead>
        {/* Anrufer: in der breiten Ansicht eine eigene Spalte, im compact-Panel (420px) unter dem
            Agentennamen gestapelt — dort ist die Tabelle auf drei Spalten ausgelegt (siehe
            formatDateShort), eine vierte brächte den H-Scroll zurück. */}
        {!compact ? <><TableHead>Anrufer</TableHead><TableHead>Status</TableHead><TableHead>Tags</TableHead></> : null}
      </TableRow></TableHeader>
      <TableBody>
        {rows.map((conversation) => (
          <TableRow key={conversation.id} data-testid="conversation-row" className="cursor-pointer" role="button" tabIndex={0}
            onClick={() => open(conversation.id)}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); open(conversation.id); } }}>
            <TableCell className={cx("whitespace-nowrap", compact && "text-xs")}>{compact ? formatDateShort(conversation.started_at) : formatDate(conversation.started_at)}</TableCell>
            <TableCell className={cx("font-medium truncate max-w-[130px]", compact && "text-xs")}>
              {conversation.agent_name || "Agent"}
              {compact ? <div className="font-mono font-normal text-[11px] text-muted-foreground truncate" data-testid="conversation-caller">{conversation.contact_address || "—"}</div> : null}
            </TableCell>
            {showChannel ? <TableCell><ChannelBadge channel={conversation.channel} /></TableCell> : null}
            <TableCell className="font-mono text-xs whitespace-nowrap">{formatDuration(conversation.duration_seconds)}</TableCell>
            {!compact ? <><TableCell className="font-mono text-xs whitespace-nowrap">{conversation.contact_address || "—"}</TableCell><TableCell><StatusBadge status={conversation.status} /></TableCell><TableCell><div className="tag-list">{(conversation.topic_tags || []).slice(0, 3).map((tag) => <Badge key={tag} variant="secondary">{tag}</Badge>)}</div></TableCell></> : null}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function Conversations({ api, clientId, showInbox = false, canGdpr = {} }) {
  const { conversationId } = useParams();
  const location = useLocation();
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [channel, setChannel] = useState("");
  // Seed the filter from ?q= (set by the topbar search) — also when it changes while mounted.
  const urlQ = new URLSearchParams(location.search).get("q");
  useEffect(() => {
    if (urlQ !== null) { setSearchInput(urlQ); setSearch(urlQ.trim()); }
  }, [urlQ]);
  // Debounce keystrokes so we don't fire one request per character.
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);
  const query = `/conversations?client_id=${clientId}&limit=100${status ? `&status=${status}` : ""}${search ? `&search=${encodeURIComponent(search)}` : ""}${channel ? `&channel=${channel}` : ""}`;
  const conversations = useResource(api, query, [clientId, status, search, channel]);
  const detail = useResource(api, conversationId ? `/conversations/${conversationId}` : null, [conversationId]);

  return (
    <div className="grid gap-5 xl:grid-cols-[420px_minmax(0,1fr)]">
      <Card className="dashboard-card min-w-0">
        <CardHeader><CardTitle>Gespräche</CardTitle><CardDescription>Klicke eine Zeile, um das Transkript zu sehen.</CardDescription></CardHeader>
        <CardContent>
          <div className="filters-bar">
            <Input className="bg-white" placeholder="Gespräch suchen" data-testid="conversations-search-input" value={searchInput} onChange={(e) => setSearchInput(e.target.value)} />
            <select className="form-select" data-testid="conversations-status-select" value={status} onChange={(e) => setStatus(e.target.value)}><option value="">Alle Status</option><option value="completed">Abgeschlossen</option><option value="ongoing">Laufend</option><option value="failed">Fehlgeschlagen</option><option value="deleted">DSGVO-gelöscht</option></select>
            {showInbox ? (
              <select className="form-select" data-testid="conversations-channel-select" value={channel} onChange={(e) => setChannel(e.target.value)}>
                <option value="">Alle Kanäle</option><option value="voice">Anruf</option><option value="whatsapp">WhatsApp</option><option value="email">E-Mail</option>
              </select>
            ) : null}
          </div>
          <div className="max-h-[calc(100vh-300px)] overflow-y-auto overflow-x-hidden -mx-2 px-2">
            {conversations.loading && !conversations.data ? <Skeleton className="h-60" data-testid="state-loading" /> : conversations.data?.length ? <ConversationTable rows={conversations.data} clientId={clientId} compact showChannel={showInbox} /> : <EmptyState title="Keine Treffer" description="Passe die Filter an oder sende einen Test-Webhook." />}
          </div>
        </CardContent>
      </Card>
      <ConversationDetail detail={detail} api={api} clientId={clientId} showInbox={showInbox} canGdpr={canGdpr} />
    </div>
  );
}

// Anzeigenamen der Chat-Eigenschaften (metadata.provider_payload). Die sechs bekannten Schlüssel
// kommen bereits deutsch aus dem Backend (_VOICEFLOW_PROPERTY_LABELS) und bekommen hier nur eine
// lesbare Form. Alles andere — Modalität und die projekteigenen Custom-Properties, also genau das,
// woran ein Kunde ablesen kann, wofür der Chatbot genutzt wird — reicht das Backend unverändert
// durch und wird hier unter seinem eigenen Namen angezeigt statt verschluckt.
const CHAT_PROPERTY_LABELS = {
  land: "Land", browser: "Browser", geraet: "Gerät",
  betriebssystem: "Betriebssystem", dauer_sekunden: "Dauer (Sek.)", kanal: "Kanal",
  modality: "Modalität",
};

// Eine projekteigene Property, deren Name auf einen der bekannten Schlüssel fällt, liefert das
// Backend unter `<schlüssel>_eigen` aus (server.py: die Zuordnung gehört der Zuordnung, der eigene
// Wert wird daneben behalten statt verworfen). Ohne diesen Zweig stünde im Detail roh
// „dauer_sekunden_eigen".
function chatPropertyLabel(key) {
  if (CHAT_PROPERTY_LABELS[key]) return CHAT_PROPERTY_LABELS[key];
  if (key.endsWith("_eigen")) {
    const base = key.slice(0, -"_eigen".length);
    return `${CHAT_PROPERTY_LABELS[base] || base} (eigen)`;
  }
  return key;
}

function ConversationDetail({ detail, api, clientId, showInbox = false, canGdpr = {} }) {
  const navigate = useNavigate();
  const conv = detail.data;
  const contact = useResource(api, showInbox && conv?.contact_id ? `/contacts/${conv.contact_id}` : null, [conv?.contact_id, showInbox]);
  const exportConversation = async () => {
    if (!detail.data) return;
    try {
      downloadJson(await api(`/conversations/${detail.data.id}/export`), `${detail.data.id}.json`);
    } catch (err) { toast.error(err.message); }
  };
  const deleteConversation = async () => {
    if (!detail.data || !window.confirm("Conversation lokal und beim Provider löschen, sofern möglich?")) return;
    try {
      await api(`/conversations/${detail.data.id}?delete_provider=true`, { method: "DELETE" });
      toast.success("Conversation gelöscht");
      navigate(`/clients/${clientId}/conversations`);
    } catch (err) { toast.error(err.message); }
  };
  return (
    <Card className="dashboard-card detail-panel min-w-0" data-testid="conversation-detail-panel">
      <CardHeader><CardTitle>Transkript</CardTitle><CardDescription>{conv ? conv.provider_conversation_id : "Gespräch auswählen"}</CardDescription></CardHeader>
      <CardContent>
        {!conv ? <EmptyState title="Kein Gespräch ausgewählt" description="Öffne ein Gespräch aus der Liste." /> : detail.loading ? <Skeleton className="h-72" data-testid="state-loading" /> : (
          <div className="space-y-5">
            {canGdpr.export || canGdpr.erase ? <div className="flex gap-2 flex-wrap">{canGdpr.export ? <Button variant="outline" size="sm" onClick={exportConversation} data-testid="conversation-export-button">Export JSON</Button> : null}{canGdpr.erase ? <Button variant="destructive" size="sm" onClick={deleteConversation} data-testid="conversation-delete-button">DSGVO löschen</Button> : null}</div> : null}
            {showInbox && conv.contact_id ? <ContactPanel contact={contact.data} loading={contact.loading} /> : null}
            <div className="metadata-grid" data-testid="conversation-metadata">
              <div><span>Datum</span><strong>{formatDate(conv.started_at)}</strong></div>
              <div><span>Status</span><StatusBadge status={conv.status} /></div>
              <div><span>Dauer</span><strong>{formatDuration(conv.duration_seconds)}</strong></div>
              {/* Anrufer-Nummer: an das Gesprächs-Recht gekoppelt (wer das Transkript sehen darf,
                  sieht auch die Nummer), nicht an das DSGVO-Recht. Bei einem Chat gibt es keinen
                  Anrufer — Web-Chat-Besucher sind anonym, die Zeile stünde auf jedem Chat als
                  „Anrufer —" und würde eine fehlende Angabe suggerieren statt einer nicht
                  existierenden. */}
              {conv.channel !== "chat" ? <div><span>Anrufer</span><strong className="font-mono text-sm">{conv.contact_address || "—"}</strong></div> : null}
            </div>
            {/* Eigenschaften-Block: nur für Chats, und nur die tatsächlich gelieferten Werte —
                fehlende Properties werden weggelassen statt als „unbekannt" erfunden (ältere Chats
                haben sie schlicht nicht). */}
            {conv.channel === "chat" && Object.keys(conv.metadata?.provider_payload || {}).length ? (
              <div className="metadata-grid" data-testid="conversation-properties">
                {Object.entries(conv.metadata.provider_payload).map(([key, value]) => (
                  <div key={key}><span>{chatPropertyLabel(key)}</span><strong className="text-sm">{String(value)}</strong></div>
                ))}
              </div>
            ) : null}
            <div className="tag-list" data-testid="conversation-topic-tags">{(conv.topic_tags || []).map((tag) => <Badge key={tag} variant="secondary">{tag}</Badge>)}</div>
            <div className="transcript text-[15px] leading-7 max-h-[calc(100vh-420px)] overflow-y-auto" data-testid="conversation-transcript">
              {(conv.messages || []).map((message) => <div key={message.id} className={cx("transcript-bubble", message.side === "agent" ? "agent" : "user")}><div className="transcript-meta"><strong>{message.side === "agent" ? "Agent" : "Anrufer"}</strong><span>{formatDate(message.timestamp)}</span></div><p>{message.text}</p></div>)}
            </div>
            <div className="workflow-runs">
              <h4>Workflow-Läufe</h4>
              {(conv.workflow_runs || []).map((run) => <div key={run.id} className="run-row"><StatusBadge status={run.status} /><span>{run.workflow_name}</span></div>)}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function KnowledgeBase({ api, clientId, user }) {
  const sources = useResource(api, `/knowledge-sources?client_id=${clientId}`, [clientId]);
  const agents = useResource(api, `/agents?client_id=${clientId}`, [clientId]);
  const [form, setForm] = useState({ title: "", provider: "retell", type: "text", agent_id: "", source_url: "", content: "" });
  const canEdit = user.role !== "client_user";
  const [selectedAgentId, setSelectedAgentId] = useState("");
  const [providerPrompt, setProviderPrompt] = useState("");
  const [providerStatus, setProviderStatus] = useState("");

  const fetchProviderConfig = async () => {
    if (!selectedAgentId) return toast.error("Bitte Agent auswählen");
    try {
      const config = await api(`/agents/${selectedAgentId}/provider-config`);
      setProviderPrompt(config.prompt || "");
      setProviderStatus(config.status || config.provider);
      toast.success("Prompt geladen");
    } catch (err) { toast.error(err.message); }
  };

  const saveProviderDraft = async () => {
    if (!selectedAgentId) return toast.error("Bitte Agent auswählen");
    try {
      await api(`/agents/${selectedAgentId}/provider-config`, { method: "PUT", body: JSON.stringify({ prompt: providerPrompt, config: {} }) });
      setProviderStatus("needs_sync");
      toast.success("Prompt Draft gespeichert");
    } catch (err) { toast.error(err.message); }
  };

  const pushProviderConfig = async () => {
    if (!selectedAgentId) return toast.error("Bitte Agent auswählen");
    try {
      const result = await api(`/agents/${selectedAgentId}/provider-config/push`, { method: "POST" });
      setProviderStatus(result.status);
      toast.success("Prompt zum Provider gepusht");
    } catch (err) { toast.error(err.message); }
  };

  const create = async (event) => {
    event.preventDefault();
    try {
      await api("/knowledge-sources", { method: "POST", body: JSON.stringify({ ...form, client_id: clientId, agent_id: form.agent_id || null, source_url: form.source_url || null, content: form.content || null }) });
      toast.success("Knowledge Source angelegt");
      setForm({ title: "", provider: "retell", type: "text", agent_id: "", source_url: "", content: "" });
      sources.reload();
    } catch (err) { toast.error(err.message); }
  };

  return (
    <div className="grid gap-5 xl:grid-cols-[1fr_380px]">
      <Card className="dashboard-card"><CardHeader><CardTitle>Wissensquellen</CardTitle><CardDescription>Datei-, URL- und Textquellen pro Agent.</CardDescription></CardHeader><CardContent>
        {sources.loading ? <Skeleton className="h-44" data-testid="state-loading" /> : sources.data?.length ? <div className="kb-grid" data-testid="knowledge-source-list">{sources.data.map((source) => <Card key={source.id} className="source-card"><CardContent className="p-4"><div className="source-card-top"><Badge variant="outline">{source.type}</Badge><ProviderBadge provider={source.provider} /></div><h3>{source.title}</h3><p>{source.source_url || source.storage_path || source.content || "Synchronisierung folgt"}</p><StatusBadge status={source.is_active ? "active" : "inactive"} /></CardContent></Card>)}</div> : <EmptyState title="Keine Wissensquellen" description="Lege Text-, URL- oder Datei-Quellen an." />}
      </CardContent></Card>
      <Card className="dashboard-card"><CardHeader><CardTitle>Quelle hinzufügen</CardTitle><CardDescription>Provider-KB Sync ist vorbereitet, im MVP ohne externe API-Aufrufe.</CardDescription></CardHeader><CardContent>
        {canEdit ? <form className="space-y-4" onSubmit={create}>
          <Field label="Titel"><Input className="bg-white" data-testid="kb-title-input" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></Field>
          <Field label="Typ"><select className="form-select" data-testid="kb-type-select" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}><option value="text">Text</option><option value="url">URL</option><option value="file">File</option></select></Field>
          <Field label="Agent"><select className="form-select" data-testid="kb-agent-select" value={form.agent_id} onChange={(e) => { const agentObj = (agents.data || []).find(a => a.id === e.target.value); setForm({ ...form, agent_id: e.target.value, provider: agentObj?.provider || "" }); }}><option value="">Kein Agent</option>{(agents.data || []).map((agent) => <option key={agent.id} value={agent.id}>{agent.name}</option>)}</select></Field>
          <Field label="URL / Text"><Textarea className="bg-white" data-testid="kb-content-input" value={form.type === "url" ? form.source_url : form.content} onChange={(e) => form.type === "url" ? setForm({ ...form, source_url: e.target.value }) : setForm({ ...form, content: e.target.value })} /></Field>
          <Button type="submit" data-testid="kb-save-button"><Plus className="h-4 w-4" /> Quelle speichern</Button>
        </form> : <p className="text-sm text-muted-foreground">Lesender Zugriff.</p>}
      </CardContent></Card>
    </div>
  );
}

function Workflows({ api, clientId, user }) {
  const workflows = useResource(api, `/workflows?client_id=${clientId}`, [clientId]);
  const [form, setForm] = useState({ name: "", trigger_event: "call_ended", condition_expression: "outcome == 'appointment_booked'", actions: '[{"type":"event_log","label":"CRM Übergabe vorbereiten","target":"crm-placeholder"}]' });
  const canEdit = user.role !== "client_user";
  const [selectedAgentId, setSelectedAgentId] = useState("");
  const [providerPrompt, setProviderPrompt] = useState("");
  const [providerStatus, setProviderStatus] = useState("");

  const fetchProviderConfig = async () => {
    if (!selectedAgentId) return toast.error("Bitte Agent auswählen");
    try {
      const config = await api(`/agents/${selectedAgentId}/provider-config`);
      setProviderPrompt(config.prompt || "");
      setProviderStatus(config.status || config.provider);
      toast.success("Prompt geladen");
    } catch (err) { toast.error(err.message); }
  };

  const saveProviderDraft = async () => {
    if (!selectedAgentId) return toast.error("Bitte Agent auswählen");
    try {
      await api(`/agents/${selectedAgentId}/provider-config`, { method: "PUT", body: JSON.stringify({ prompt: providerPrompt, config: {} }) });
      setProviderStatus("needs_sync");
      toast.success("Prompt Draft gespeichert");
    } catch (err) { toast.error(err.message); }
  };

  const pushProviderConfig = async () => {
    if (!selectedAgentId) return toast.error("Bitte Agent auswählen");
    try {
      const result = await api(`/agents/${selectedAgentId}/provider-config/push`, { method: "POST" });
      setProviderStatus(result.status);
      toast.success("Prompt zum Provider gepusht");
    } catch (err) { toast.error(err.message); }
  };

  const create = async (event) => {
    event.preventDefault();
    try {
      const actions = JSON.parse(form.actions || "[]");
      await api("/workflows", { method: "POST", body: JSON.stringify({ client_id: clientId, name: form.name, trigger_event: form.trigger_event, condition_expression: form.condition_expression, actions }) });
      toast.success("Workflow erstellt");
      setForm({ ...form, name: "" });
      workflows.reload();
    } catch (err) { toast.error(err.message); }
  };

  return (
    <div className="grid gap-5 xl:grid-cols-[1fr_420px]">
      <Card className="dashboard-card"><CardHeader><CardTitle>Workflows</CardTitle><CardDescription>Trigger → Conditions → Actions für Call-Events.</CardDescription></CardHeader><CardContent>
        {workflows.loading ? <Skeleton className="h-44" data-testid="state-loading" /> : workflows.data?.length ? <div className="workflow-list" data-testid="workflow-list">{workflows.data.map((workflow) => <div key={workflow.id} className="workflow-card"><div><h3>{workflow.name}</h3><p>{workflow.condition_expression || "Keine Bedingung"}</p></div><div className="workflow-meta"><Badge variant="outline">{workflow.trigger_event}</Badge><StatusBadge status={workflow.is_active ? "active" : "inactive"} /></div></div>)}</div> : <EmptyState title="Keine Workflows" description="Erstelle den ersten Call-Event Workflow." />}
      </CardContent></Card>
      <Card className="dashboard-card"><CardHeader><CardTitle>Workflow Editor MVP</CardTitle><CardDescription>Actions als JSON. Webhook (nur Admin): {"{"}"type":"webhook","label":"…","target":"https://…","secret":"…"{"}"} sendet Anruf, Analyse und Transkript per POST (Header X-VoiceOps-Secret).</CardDescription></CardHeader><CardContent>
        {canEdit ? <form className="space-y-4" onSubmit={create}>
          <Field label="Name"><Input className="bg-white" data-testid="workflow-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
          <Field label="Trigger"><select className="form-select" data-testid="workflow-editor-trigger-select" value={form.trigger_event} onChange={(e) => setForm({ ...form, trigger_event: e.target.value })}><option value="call_started">call_started</option><option value="call_ended">call_ended</option><option value="call_analyzed">call_analyzed</option><option value="conversation_tagged">conversation_tagged</option></select></Field>
          <Field label="Condition Expression"><Input className="bg-white font-mono" data-testid="workflow-condition-input" value={form.condition_expression} onChange={(e) => setForm({ ...form, condition_expression: e.target.value })} /></Field>
          <Field label="Actions JSON"><Textarea className="bg-white font-mono min-h-[130px]" data-testid="workflow-actions-input" value={form.actions} onChange={(e) => setForm({ ...form, actions: e.target.value })} /></Field>
          <Button type="submit" data-testid="workflow-editor-save-button"><Plus className="h-4 w-4" /> Workflow speichern</Button>
        </form> : <p className="text-sm text-muted-foreground">Lesender Zugriff.</p>}
      </CardContent></Card>
    </div>
  );
}

function money(value, currency = "eur") {
  return new Intl.NumberFormat("de-DE", { style: "currency", currency: currency.toUpperCase() }).format(Number(value || 0));
}

function BillingPage({ api, clientId, user }) {
  const isAdmin = user.role === "admin";
  // Per-Client-Abrechnung zeigt nur: Aktueller Verbrauch, Abrechnung ausführen (Admin),
  // Zahlungsmethode, Abrechnungshistorie, Client/Agent Assignments. Plan-Katalog + Plan-Builder +
  // Plan-Zuweisung leben in der globalen Billing-Konsole (Admin → Billing).
  const assignments = useResource(api, `/billing/assignments?client_id=${clientId}`, [clientId]);
  const invoices = useResource(api, `/billing/invoices?client_id=${clientId}`, [clientId]);
  const pm = useResource(api, `/billing/payment-method?client_id=${clientId}`, [clientId]);
  const [selectedAssignment, setSelectedAssignment] = useState("");
  const [currentUsage, setCurrentUsage] = useState(null);
  const [runningBill, setRunningBill] = useState(false);

  // Auto-fetch current usage when an active assignment is available
  useEffect(() => {
    const active = (assignments.data || []).find((a) => a.status === "active");
    if (!active) return;
    if (!selectedAssignment) setSelectedAssignment(active.id);
    api("/billing/usage/preview", { method: "POST", body: JSON.stringify({ assignment_id: active.id }) })
      .then((r) => setCurrentUsage(r))
      .catch(() => {});
  }, [assignments.data]); // eslint-disable-line react-hooks/exhaustive-deps

  // Poll Stripe checkout status on redirect back. Depend on the stable reload callbacks —
  // not the resource objects, which get a new identity every render and would restart the
  // poll on each state change.
  const reloadAssignments = assignments.reload;
  const reloadInvoices = invoices.reload;
  useEffect(() => {
    const sessionId = new URLSearchParams(window.location.search).get("session_id");
    if (!sessionId) return;
    let cancelled = false;
    let timer = null;
    const poll = async (attempt = 0) => {
      try {
        const status = await api(`/billing/checkout/status/${sessionId}`);
        if (!cancelled && (status.payment_status === "paid" || status.status === "complete" || attempt >= 5)) {
          toast.success(status.payment_status === "paid" ? "Stripe Zahlung bestätigt" : "Checkout Status aktualisiert");
          reloadAssignments(); reloadInvoices();
          window.history.replaceState({}, "", window.location.pathname);
          return;
        }
        if (!cancelled) timer = setTimeout(() => poll(attempt + 1), Math.min(2000 + attempt * 1000, 6000));
      } catch (err) { if (!cancelled) toast.error(err.message); }
    };
    poll();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [api, reloadAssignments, reloadInvoices]);

  const startCheckout = async (assignmentId) => {
    try {
      const session = await api("/billing/checkout/session", { method: "POST", body: JSON.stringify({ assignment_id: assignmentId, origin_url: window.location.origin }) });
      window.location.href = session.url;
    } catch (err) { toast.error(err.message); }
  };

  const changeCard = async () => {
    try {
      const res = await api("/billing/setup-session", { method: "POST", body: JSON.stringify({ client_id: clientId, origin_url: window.location.origin }) });
      if (res.url) window.location.href = res.url;
    } catch (err) { toast.error(err.message); }
  };

  const runBilling = async (dryRun = false) => {
    if (!selectedAssignment) return toast.error("Bitte Assignment auswählen");
    setRunningBill(true);
    try {
      const result = await api("/billing/run", { method: "POST", body: JSON.stringify({ assignment_id: selectedAssignment, dry_run: dryRun }) });
      const r = result.results?.[0];
      if (!r) return toast.error("Keine Ergebnisse");
      if (r.status === "dry_run") toast.success(`Vorschau: ${r.overage_minutes?.toFixed(1)} min Overage → ${money(r.amount, "eur")}`);
      else if (r.status === "already_billed") toast.info("Dieser Monat wurde bereits abgerechnet");
      else if (r.status === "zero_overage") toast.success("Kein Overage — kein Charge notwendig");
      else if (r.status === "invoiced") toast.success(`Rechnung erstellt: ${money(r.amount, "eur")}`);
      else toast.error(r.detail || "Unbekannter Status");
      invoices.reload();
    } catch (err) { toast.error(err.message); }
    finally { setRunningBill(false); }
  };

  // Derive next billing date from active assignment's anchor day
  const activeAssignment = (assignments.data || []).find((a) => a.status === "active");
  const nextBillingLabel = (() => {
    const anchor = activeAssignment?.billing_anchor_day;
    if (!anchor) return null;
    const now = new Date();
    const target = new Date(now.getFullYear(), now.getMonth(), anchor);
    if (target <= now) target.setMonth(target.getMonth() + 1);
    return target.toLocaleDateString("de-DE", { day: "2-digit", month: "long", year: "numeric" });
  })();

  const usagePct = currentUsage && currentUsage.included_minutes > 0
    ? Math.min(100, Math.round((currentUsage.total_minutes / currentUsage.included_minutes) * 100))
    : null;

  const invoiceStatusColor = (s) => s === "paid" ? "text-green-600" : s === "failed" ? "text-red-500" : "text-yellow-600";

  return (
    <div className="space-y-5" data-testid="billing-page">

      {/* ── 1. Usage Gauge + Billing Actions ── */}
      {activeAssignment && <div className="grid gap-5 xl:grid-cols-2">
        <Card className="dashboard-card"><CardHeader><CardTitle>Aktueller Verbrauch</CardTitle><CardDescription>{activeAssignment.plan?.name || "Aktiver Plan"}{nextBillingLabel ? ` · Nächste Abrechnung: ${nextBillingLabel}` : ""}</CardDescription></CardHeader><CardContent className="space-y-4">
          {currentUsage ? (<>
            <div className="space-y-1">
              <div className="flex justify-between text-sm"><span className="text-muted-foreground">Minuten</span><span className="font-medium">{currentUsage.total_minutes?.toFixed(1)} / {currentUsage.included_minutes > 0 ? `${currentUsage.included_minutes} inkl.` : "unbegrenzt"}</span></div>
              {usagePct !== null && <div className="h-2 bg-muted rounded-full overflow-hidden"><div className="h-2 rounded-full bg-primary transition-all" style={{ width: `${usagePct}%`, backgroundColor: usagePct >= 90 ? "#ef4444" : usagePct >= 70 ? "#f59e0b" : undefined }} /></div>}
            </div>
            <div className="grid grid-cols-3 gap-3 text-center">
              <div className="rounded-lg bg-muted/50 p-3"><p className="text-xs text-muted-foreground">Gesamt</p><p className="font-semibold">{currentUsage.total_minutes?.toFixed(1)} min</p></div>
              <div className="rounded-lg bg-muted/50 p-3"><p className="text-xs text-muted-foreground">Overage</p><p className="font-semibold text-orange-600">{currentUsage.overage_minutes?.toFixed(1)} min</p></div>
              <div className="rounded-lg bg-muted/50 p-3"><p className="text-xs text-muted-foreground">Betrag</p><p className="font-semibold">{money(currentUsage.amount, currentUsage.currency)}</p></div>
            </div>
          </>) : <Skeleton className="h-24" />}
        </CardContent></Card>
        {isAdmin && <Card className="dashboard-card"><CardHeader><CardTitle>Abrechnung ausführen</CardTitle><CardDescription>Trockentest oder echte Stripe Rechnung für das ausgewählte Assignment.</CardDescription></CardHeader><CardContent className="space-y-4">
          <Field label="Assignment"><select className="form-select" data-testid="billing-usage-assignment-select" value={selectedAssignment} onChange={(e) => setSelectedAssignment(e.target.value)}><option value="">Assignment wählen</option>{(assignments.data || []).map((a) => <option key={a.id} value={a.id}>{a.plan?.name || a.plan_id} · {a.agent?.name || "Alle Agents"}</option>)}</select></Field>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" disabled={runningBill} onClick={() => runBilling(true)} data-testid="billing-dry-run-button"><RefreshCw className={`h-4 w-4 ${runningBill ? "animate-spin" : ""}`} /> Trockentest</Button>
            <Button disabled={runningBill} onClick={() => runBilling(false)} data-testid="billing-run-button">Jetzt abrechnen</Button>
          </div>
          <p className="text-xs text-muted-foreground">Der Scheduler läuft täglich automatisch und berechnet alle aktiven Assignments mit passendem Abrechnungstag.</p>
        </CardContent></Card>}
      </div>}

      {/* ── 2. Zahlungsmethode ── */}
      <Card className="dashboard-card" data-testid="client-payment-method"><CardHeader><CardTitle>Zahlungsmethode</CardTitle><CardDescription>Hinterlegte Karte für die automatische Abrechnung.</CardDescription></CardHeader><CardContent className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3"><CreditCard className="h-5 w-5" /><div>{pm.data?.on_file ? <strong>{(pm.data.brand || "Karte")} ····{pm.data.last4 || ""}</strong> : <strong>Keine Karte hinterlegt</strong>}<p className="text-sm text-muted-foreground">{pm.data?.on_file ? "Aktiv — wird monatlich automatisch belastet." : "Bitte hinterlege eine Karte."}</p></div></div>
        <Button variant="outline" onClick={changeCard} data-testid="change-card-button"><CreditCard className="h-4 w-4" /> Karte ändern</Button>
      </CardContent></Card>

      {/* ── 3. Abrechnungshistorie ── */}
      <Card className="dashboard-card"><CardHeader><CardTitle>Abrechnungshistorie</CardTitle><CardDescription>Automatisch erzeugte Stripe Rechnungen für diesen Client.</CardDescription></CardHeader><CardContent>
        {invoices.loading ? <Skeleton className="h-40" /> : (invoices.data || []).length ? (
          <Table data-testid="billing-invoices-table"><TableHeader><TableRow><TableHead>Periode</TableHead><TableHead>Minuten</TableHead><TableHead>Overage</TableHead><TableHead>Betrag</TableHead><TableHead>Status</TableHead><TableHead>Stripe Invoice</TableHead></TableRow></TableHeader>
          <TableBody>{invoices.data.map((inv) => <TableRow key={inv.id}>
            <TableCell className="text-xs">{inv.period_year}/{String(inv.period_month).padStart(2, "0")}</TableCell>
            <TableCell>{inv.total_minutes?.toFixed(1)} min</TableCell>
            <TableCell className="text-orange-600">{inv.overage_minutes?.toFixed(1)} min</TableCell>
            <TableCell className="font-semibold">{money(inv.amount, inv.currency)}</TableCell>
            <TableCell><span className={`text-xs font-medium ${invoiceStatusColor(inv.status)}`}>{inv.status}</span></TableCell>
            <TableCell className="font-mono text-xs">{inv.stripe_invoice_id ? inv.stripe_invoice_id.slice(0, 20) + "…" : "—"}</TableCell>
          </TableRow>)}</TableBody></Table>
        ) : <EmptyState title="Noch keine Rechnungen" description="Rechnungen erscheinen hier nach dem ersten Abrechnungslauf." />}
      </CardContent></Card>

      {/* ── 4. Client / Agent Assignments (admin only) — Plan-Katalog/Builder/Zuweisung leben in der globalen Billing-Konsole ── */}
      {isAdmin && <Card className="dashboard-card"><CardHeader><CardTitle>Client / Agent Assignments</CardTitle><CardDescription>Plan-Zuordnungen dieses Clients — Checkout starten, um Stripe Customer zu aktivieren. Neue Zuweisungen unter Admin → Billing → Zuweisungen.</CardDescription></CardHeader><CardContent>
        {assignments.loading ? <Skeleton className="h-40" /> : <Table data-testid="billing-assignment-table"><TableHeader><TableRow><TableHead>Plan</TableHead><TableHead>Agent</TableHead><TableHead>Status</TableHead><TableHead>Abrechnungstag</TableHead><TableHead>Letzter Lauf</TableHead><TableHead></TableHead></TableRow></TableHeader><TableBody>{(assignments.data || []).map((assignment) => <TableRow key={assignment.id} data-testid="billing-assignment-row"><TableCell>{assignment.plan?.name || assignment.plan_id}</TableCell><TableCell>{assignment.agent?.name || "Alle Agents"}</TableCell><TableCell><StatusBadge status={assignment.status} /></TableCell><TableCell className="text-xs">{assignment.billing_anchor_day ? `${assignment.billing_anchor_day}. des Monats` : "—"}</TableCell><TableCell className="text-xs text-muted-foreground">{assignment.last_billed_period || "—"}</TableCell><TableCell className="text-right"><Button variant="outline" size="sm" onClick={() => startCheckout(assignment.id)} data-testid="billing-checkout-button">Checkout</Button></TableCell></TableRow>)}</TableBody></Table>}
      </CardContent></Card>}
    </div>
  );
}


function EventLog({ api }) {
  const events = useResource(api, "/event-log", []);
  return (
    <PageMotion>
      <PageHeader eyebrow="Operations" title="Event Log" description="Idempotency Ledger und Workflow-Ausführung als Debug-Oberfläche." />
      <Card className="dashboard-card"><CardContent className="pt-6">
        {events.loading ? <Skeleton className="h-64" data-testid="state-loading" /> : events.data?.length ? <Table data-testid="event-log-table"><TableHeader><TableRow><TableHead>Created</TableHead><TableHead>Provider / Workflow</TableHead><TableHead>Event</TableHead><TableHead>Status</TableHead><TableHead>Payload Hash</TableHead></TableRow></TableHeader><TableBody>{events.data.map((event) => <TableRow key={event.id} data-testid="event-log-row"><TableCell>{formatDate(event.created_at)}</TableCell><TableCell>{event.provider || event.workflow_name || "—"}</TableCell><TableCell>{event.event_type || event.trigger_event}</TableCell><TableCell><StatusBadge status={event.processed_at ? "processed" : event.status || "queued"} /></TableCell><TableCell className="font-mono text-xs">{event.payload_hash || event.conversation_id || "—"}</TableCell></TableRow>)}</TableBody></Table> : <EmptyState title="Kein Event Log" description="Webhook Events erscheinen nach Provider Calls." />}
      </CardContent></Card>
    </PageMotion>
  );
}

function AuditLog({ api }) {
  const entries = useResource(api, "/admin/audit-log", []);
  return (
    <PageMotion>
      <PageHeader eyebrow="Operations" title="Audit Log" description="Vollständiges Audit-Trail aller Admin-Aktionen, neueste zuerst." />
      <Card className="dashboard-card"><CardContent className="pt-6">
        {entries.loading ? <Skeleton className="h-64" data-testid="state-loading" /> : entries.data?.length ? <Table data-testid="audit-log-table"><TableHeader><TableRow><TableHead>Timestamp</TableHead><TableHead>Action</TableHead><TableHead>Actor</TableHead><TableHead>Resource</TableHead></TableRow></TableHeader><TableBody>{entries.data.map((entry, idx) => <TableRow key={entry.id || idx} data-testid="audit-log-row"><TableCell>{formatDate(entry.created_at)}</TableCell><TableCell>{entry.action || "—"}</TableCell><TableCell>{entry.actor_email || entry.actor_id || "—"}</TableCell><TableCell className="font-mono text-xs">{entry.target || "—"}</TableCell></TableRow>)}</TableBody></Table> : <EmptyState title="Kein Audit Log" description="Audit-Einträge erscheinen hier nach Admin-Aktionen." />}
      </CardContent></Card>
    </PageMotion>
  );
}

function SettingsPage({ api, client, clientId, user, canGdpr = {} }) {
  const isAdmin = user?.role === "admin";
  const settings = useResource(api, isAdmin ? "/admin/settings" : null, []);
  const users = useResource(api, isAdmin ? "/users" : null, []);
  const agents = useResource(api, isAdmin && clientId ? `/agents?client_id=${clientId}` : null, [clientId]);
  const [branding, setBranding] = useState({ company_name: "", primary_color: "#5B51EC", accent_color: "#CFCBF6", logo_url: "", logo_data_url: "", login_logo_url: "", login_logo_data_url: "", favicon_url: "", favicon_data_url: "", website_title: "", workspace_id: "", custom_domain: "" });
  const [email, setEmail] = useState({ provider: "resend", sender_email: "", sender_name: "", resend_api_key: "", sending_domain: "", email_logo_url: "", email_logo_data_url: "", reset_subject: "Passwort zurücksetzen", change_subject: "Passwort geändert", reset_template_html: "", change_template_html: "" });
  const [integrations, setIntegrations] = useState({ elevenlabs_api_key: "", retell_api_key: "", vapi_api_key: "", voiceflow_api_key: "" });
  const [newUser, setNewUser] = useState({ email: "", name: "", role: "client_user", password: "" });
  const [memberForm, setMemberForm] = useState({ agent_id: "", user_id: "", role: "viewer" });
  const [temporaryPassword, setTemporaryPassword] = useState("");
  const [inviteLink, setInviteLink] = useState(null); // { userId, url } — shown when email not configured
  const [pwForm, setPwForm] = useState({ current: "", next: "", confirm: "" });
  const [pwMsg, setPwMsg] = useState(null);

  useEffect(() => {
    if (settings.data?.branding) setBranding((p) => ({ ...p, ...settings.data.branding, logo_data_url: "", login_logo_data_url: "", favicon_data_url: "" }));
    if (settings.data?.email) setEmail((p) => ({ ...p, ...settings.data.email, provider: "resend", email_logo_data_url: "" }));
    if (settings.data?.integrations) setIntegrations({ elevenlabs_api_key: settings.data.integrations.elevenlabs_api_key || "", retell_api_key: settings.data.integrations.retell_api_key || "", vapi_api_key: settings.data.integrations.vapi_api_key || "", voiceflow_api_key: settings.data.integrations.voiceflow_api_key || "" });
  }, [settings.data]);

  const buildSettingsPayload = () => ({
    branding: {
      company_name: branding.company_name || "", primary_color: branding.primary_color || "", accent_color: branding.accent_color || "",
      logo_url: branding.logo_data_url || branding.logo_url || "",
      login_logo_url: branding.login_logo_data_url || branding.login_logo_url || "",
      favicon_url: branding.favicon_data_url || branding.favicon_url || "",
      website_title: branding.website_title || "", workspace_id: branding.workspace_id || "", custom_domain: branding.custom_domain || "",
    },
    email: {
      sender_email: email.sender_email || null, sender_name: email.sender_name || "", resend_api_key: email.resend_api_key || "",
      sending_domain: email.sending_domain || "", email_logo_url: email.email_logo_data_url || email.email_logo_url || "",
      reset_subject: email.reset_subject || "", change_subject: email.change_subject || "",
      reset_template_html: email.reset_template_html || "", change_template_html: email.change_template_html || "",
    },
    integrations: { elevenlabs_api_key: integrations.elevenlabs_api_key || "", retell_api_key: integrations.retell_api_key || "", vapi_api_key: integrations.vapi_api_key || "", voiceflow_api_key: integrations.voiceflow_api_key || "" },
  });

  const saveSettings = async (event) => {
    event.preventDefault();
    try {
      await api("/admin/settings", { method: "PUT", body: JSON.stringify(buildSettingsPayload()) });
      toast.success("Einstellungen gespeichert");
      settings.reload();
    } catch (err) { toast.error(err.message); }
  };

  const saveIntegrationsKey = async (event) => {
    event.preventDefault();
    try {
      await api("/admin/settings", { method: "PUT", body: JSON.stringify(buildSettingsPayload()) });
      toast.success("API Key gespeichert");
      settings.reload();
    } catch (err) { toast.error(err.message); }
  };

  const testElevenlabs = async () => {
    try {
      const res = await api("/integrations/elevenlabs/status");
      if (res.ok) toast.success("Platform verbunden ✓");
      else if (!res.configured) toast.error("Kein API Key gespeichert");
      else toast.error(`Verbindung fehlgeschlagen (HTTP ${res.status_code})`);
    } catch (err) { toast.error(err.message); }
  };

  const testVoiceflow = async () => {
    try {
      const res = await api("/integrations/voiceflow/status");
      // ok === null heißt „Key da, aber nichts zum Prüfen": Voiceflow hat kein key-gebundenes
      // Neutral-Endpoint, ohne Agent ist der Key weder als gut noch als kaputt belegbar.
      if (res.ok) toast.success("Platform verbunden ✓");
      else if (!res.configured) toast.error("Kein API Key gespeichert");
      else if (res.ok === null) toast.info(res.detail || "Erst nach dem Anlegen eines Agents prüfbar");
      else toast.error(`Verbindung fehlgeschlagen (HTTP ${res.status_code})`);
    } catch (err) { toast.error(err.message); }
  };

  const handleLogoUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setBranding({ ...branding, logo_data_url: reader.result, logo_url: "" });
    reader.readAsDataURL(file);
  };

  const handleBrandingImageUpload = (field) => (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setBranding((p) => ({ ...p, [field]: reader.result }));
    reader.readAsDataURL(file);
  };

  const handleEmailLogoUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setEmail((p) => ({ ...p, email_logo_data_url: reader.result }));
    reader.readAsDataURL(file);
  };

  const createUser = async (event) => {
    event.preventDefault();
    try {
      const created = await api("/users", { method: "POST", body: JSON.stringify({ ...newUser, client_id: clientId, password: newUser.password || undefined }) });
      setTemporaryPassword(created.temporary_password || "");
      toast.success("User erstellt");
      setNewUser({ email: "", name: "", role: "client_user", password: "" });
      users.reload();
    } catch (err) { toast.error(err.message); }
  };

  const inviteUser = async (userId, userEmail) => {
    try {
      const result = await api(`/users/${userId}/invite`, { method: "POST", body: JSON.stringify({ origin_url: window.location.origin }) });
      if (result.sent) {
        toast.success(`Einladungsmail gesendet an ${userEmail}`);
        setInviteLink(null);
      } else {
        setInviteLink({ userId, url: result.invite_url });
        toast.info("E-Mail nicht konfiguriert — Einladungslink bereit zum Kopieren");
      }
    } catch (err) { toast.error(err.message); }
  };

  const assignMember = async (event) => {
    event.preventDefault();
    try {
      await api("/agent-members", { method: "POST", body: JSON.stringify(memberForm) });
      toast.success("Person dem Agent zugewiesen");
      setMemberForm({ agent_id: "", user_id: "", role: "viewer" });
    } catch (err) { toast.error(err.message); }
  };

  const clientUsers = (users.data || []).filter((u) => u.role === "admin" || u.client_id === clientId);

  const changePassword = async () => {
    if (pwForm.next !== pwForm.confirm) { setPwMsg({ ok: false, text: "Passwörter stimmen nicht überein" }); return; }
    if (pwForm.next.length < 8) { setPwMsg({ ok: false, text: "Mindestens 8 Zeichen" }); return; }
    try {
      await api("/auth/password/change", { method: "POST", body: JSON.stringify({ current_password: pwForm.current, new_password: pwForm.next }) });
      setPwMsg({ ok: true, text: "Passwort geändert" });
      setPwForm({ current: "", next: "", confirm: "" });
    } catch (e) {
      setPwMsg({ ok: false, text: e.message || "Fehler beim Ändern" });
    }
  };

  return (
    <div className="space-y-5">
      {isAdmin ? <div className="grid gap-5 xl:grid-cols-2">
        <Card className="dashboard-card"><CardHeader><CardTitle>White Label Branding</CardTitle><CardDescription>Globales Agentur-Branding für Login und Dashboard. Client-Overrides sind vorbereitet.</CardDescription></CardHeader><CardContent>
          <form className="space-y-4" onSubmit={saveSettings}>
            <Field label="Agentur-Name"><Input className="bg-white" data-testid="branding-company-input" value={branding.company_name || ""} onChange={(e) => setBranding({ ...branding, company_name: e.target.value })} /></Field>
            <Field label="Website-Titel (Browser-Tab)"><Input className="bg-white" data-testid="branding-website-title-input" value={branding.website_title || ""} onChange={(e) => setBranding({ ...branding, website_title: e.target.value })} placeholder="z.B. Meine Bots Console" /></Field>
            <div className="grid grid-cols-2 gap-3"><Field label="Primärfarbe (Theme)"><Input className="bg-white" data-testid="branding-primary-color-input" type="color" value={branding.primary_color || "#5B51EC"} onChange={(e) => setBranding({ ...branding, primary_color: e.target.value })} /></Field><Field label="Accent-Farbe (Theme)"><Input className="bg-white" type="color" value={branding.accent_color || "#CFCBF6"} onChange={(e) => setBranding({ ...branding, accent_color: e.target.value })} /></Field></div>
            <Field label="Dashboard Logo Upload"><Input className="bg-white" type="file" accept="image/*" onChange={handleLogoUpload} data-testid="branding-logo-upload" /></Field>
            {(branding.logo_data_url || branding.logo_url) ? <img className="brand-preview" src={branding.logo_data_url || branding.logo_url} alt="Dashboard Logo" style={{ maxHeight: 48 }} /> : null}
            <Field label="Login-Seite Logo Upload"><Input className="bg-white" type="file" accept="image/*" onChange={handleBrandingImageUpload("login_logo_data_url")} data-testid="branding-login-logo-upload" /></Field>
            {(branding.login_logo_data_url || branding.login_logo_url) ? <img className="brand-preview" src={branding.login_logo_data_url || branding.login_logo_url} alt="Login Logo" style={{ maxHeight: 48 }} /> : null}
            <Field label="Favicon Upload"><Input className="bg-white" type="file" accept="image/*" onChange={handleBrandingImageUpload("favicon_data_url")} data-testid="branding-favicon-upload" /></Field>
            {(branding.favicon_data_url || branding.favicon_url) ? <img className="brand-preview" src={branding.favicon_data_url || branding.favicon_url} alt="Favicon" style={{ maxHeight: 32, maxWidth: 32 }} /> : null}
            <Field label="Domain für Client-Dashboard"><Input className="bg-white" data-testid="branding-custom-domain-input" value={branding.custom_domain || ""} onChange={(e) => setBranding({ ...branding, custom_domain: e.target.value })} placeholder="dash.deine-domain.de" /></Field>
            <Field label="Workspace ID (Referenz & Support)"><Input className="bg-muted font-mono text-xs" data-testid="branding-workspace-id" value={branding.workspace_id || "wird automatisch erzeugt"} readOnly /></Field>
            <Button type="submit" data-testid="settings-save-button">Einstellungen speichern</Button>
          </form>
        </CardContent></Card>
        <Card className="dashboard-card"><CardHeader><CardTitle>E-Mail & Passwort Reset</CardTitle><CardDescription>Resend API Key später hier einfügen. Bis dahin bleibt Versand deaktiviert.</CardDescription></CardHeader><CardContent>
          <form className="space-y-4" onSubmit={saveSettings}>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Absender E-Mail"><Input className="bg-white" data-testid="email-sender-input" value={email.sender_email || ""} onChange={(e) => setEmail({ ...email, sender_email: e.target.value })} placeholder="noreply@deine-domain.de" /></Field>
              <Field label="Absender Name"><Input className="bg-white" data-testid="email-sender-name-input" value={email.sender_name || ""} onChange={(e) => setEmail({ ...email, sender_name: e.target.value })} placeholder="Meine Bots" /></Field>
            </div>
            <Field label="Sending Domain"><Input className="bg-white" data-testid="email-sending-domain-input" value={email.sending_domain || ""} onChange={(e) => setEmail({ ...email, sending_domain: e.target.value })} placeholder="deine-domain.de" /></Field>
            <Field label="Resend API Key"><Input className="bg-white" type="password" data-testid="resend-api-key-input" value={email.resend_api_key || ""} onChange={(e) => setEmail({ ...email, resend_api_key: e.target.value })} placeholder="re_..." /></Field>
            <Field label="E-Mail Logo Upload"><Input className="bg-white" type="file" accept="image/*" onChange={handleEmailLogoUpload} data-testid="email-logo-upload" /></Field>
            {(email.email_logo_data_url || email.email_logo_url) ? <img className="brand-preview" src={email.email_logo_data_url || email.email_logo_url} alt="Email Logo" style={{ maxHeight: 40 }} /> : null}
            <div className="grid grid-cols-2 gap-3">
              <Field label="Reset Betreff"><Input className="bg-white" data-testid="email-reset-subject-input" value={email.reset_subject || ""} onChange={(e) => setEmail({ ...email, reset_subject: e.target.value })} /></Field>
              <Field label="Passwort geändert Betreff"><Input className="bg-white" data-testid="email-change-subject-input" value={email.change_subject || ""} onChange={(e) => setEmail({ ...email, change_subject: e.target.value })} /></Field>
            </div>
            <Field label="Reset-Passwort Template (HTML — Platzhalter: {'{{company}}'}, {'{{reset_url}}'})"><Textarea rows={4} className="bg-white font-mono text-xs" data-testid="email-reset-template-input" value={email.reset_template_html || ""} onChange={(e) => setEmail({ ...email, reset_template_html: e.target.value })} placeholder="Leer = Standard-Template" /></Field>
            <Field label="Passwort-geändert Template (HTML)"><Textarea rows={4} className="bg-white font-mono text-xs" data-testid="email-change-template-input" value={email.change_template_html || ""} onChange={(e) => setEmail({ ...email, change_template_html: e.target.value })} placeholder="Leer = Standard-Template" /></Field>
            <div className="info-callout"><ShieldCheck className="h-4 w-4" /> Status: {settings.data?.email?.configured ? "E-Mail konfiguriert" : "Noch nicht konfiguriert"}</div>
            <Button type="submit" data-testid="email-settings-save-button">E-Mail Settings speichern</Button>
          </form>
        </CardContent></Card>
        <Card className="dashboard-card"><CardHeader><CardTitle>ElevenLabs Integration</CardTitle><CardDescription>API Key verbinden, um Anrufe, Dauer, Transkripte und Prompts live zu synchronisieren.</CardDescription></CardHeader><CardContent>
          <form className="space-y-4" onSubmit={saveIntegrationsKey}>
            <Field label="ElevenLabs API Key"><Input className="bg-white" type="password" data-testid="elevenlabs-api-key-input" value={integrations.elevenlabs_api_key || ""} onChange={(e) => setIntegrations({ ...integrations, elevenlabs_api_key: e.target.value })} placeholder="API Key eingeben..." /></Field>
            <div className="info-callout"><ShieldCheck className="h-4 w-4" /> Status: {settings.data?.integrations?.elevenlabs_configured ? "ElevenLabs verbunden ✓" : "Noch nicht verbunden"}</div>
            <div className="flex gap-2">
              <Button type="submit" data-testid="integrations-save-button">Key speichern</Button>
              <Button type="button" variant="outline" onClick={testElevenlabs} data-testid="elevenlabs-test-button">Verbindung testen</Button>
            </div>
          </form>
        </CardContent></Card>
        <Card className="dashboard-card"><CardHeader><CardTitle>Retell Integration</CardTitle><CardDescription>API Key verbinden, um Retell-Agents zu importieren und zu synchronisieren.</CardDescription></CardHeader><CardContent>
          <form className="space-y-4" onSubmit={saveIntegrationsKey}>
            <Field label="Retell API Key"><Input className="bg-white" type="password" data-testid="retell-api-key-input" value={integrations.retell_api_key || ""} onChange={(e) => setIntegrations({ ...integrations, retell_api_key: e.target.value })} placeholder="key_..." /></Field>
            <div className="info-callout"><ShieldCheck className="h-4 w-4" /> Status: {settings.data?.integrations?.retell_configured ? "Retell verbunden ✓" : "Noch nicht verbunden"}</div>
            <Button type="submit" data-testid="retell-save-button">Key speichern</Button>
          </form>
        </CardContent></Card>
        <Card className="dashboard-card"><CardHeader><CardTitle>Voiceflow Integration</CardTitle><CardDescription>API Key verbinden, um Chats, Dauer, Transkripte und Eigenschaften zu synchronisieren.</CardDescription></CardHeader><CardContent>
          <form className="space-y-4" onSubmit={saveIntegrationsKey}>
            <Field label="Voiceflow API Key"><Input className="bg-white" type="password" data-testid="voiceflow-api-key-input" value={integrations.voiceflow_api_key || ""} onChange={(e) => setIntegrations({ ...integrations, voiceflow_api_key: e.target.value })} placeholder="VF.DM..." /></Field>
            <div className="info-callout"><ShieldCheck className="h-4 w-4" /> Status: {settings.data?.integrations?.voiceflow_configured ? "Voiceflow verbunden ✓" : "Noch nicht verbunden"}</div>
            <div className="flex gap-2">
              <Button type="submit" data-testid="voiceflow-save-button">Key speichern</Button>
              <Button type="button" variant="outline" onClick={testVoiceflow} data-testid="voiceflow-test-button">Verbindung testen</Button>
            </div>
          </form>
        </CardContent></Card>
        <Card className="dashboard-card"><CardHeader><CardTitle>Vapi Integration</CardTitle><CardDescription>API Key verbinden, um Vapi-Assistants zu importieren und zu synchronisieren.</CardDescription></CardHeader><CardContent>
          <form className="space-y-4" onSubmit={saveIntegrationsKey}>
            <Field label="Vapi API Key"><Input className="bg-white" type="password" data-testid="vapi-api-key-input" value={integrations.vapi_api_key || ""} onChange={(e) => setIntegrations({ ...integrations, vapi_api_key: e.target.value })} placeholder="API Key eingeben..." /></Field>
            <div className="info-callout"><ShieldCheck className="h-4 w-4" /> Status: {settings.data?.integrations?.vapi_configured ? "Vapi verbunden ✓" : "Noch nicht verbunden"}</div>
            <Button type="submit" data-testid="vapi-save-button">Key speichern</Button>
          </form>
        </CardContent></Card>
      </div> : <>
        <Card className="dashboard-card">
          <CardContent className="space-y-4">
            <div>
              <p className="text-sm font-medium text-foreground">Passwort ändern</p>
              <p className="text-xs text-muted-foreground">Ändere dein Login-Passwort.</p>
            </div>
            <Field label="Aktuelles Passwort">
              <Input type="password" value={pwForm.current} onChange={e => setPwForm({...pwForm, current: e.target.value})} data-testid="pw-current-input" />
            </Field>
            <Field label="Neues Passwort">
              <Input type="password" value={pwForm.next} onChange={e => setPwForm({...pwForm, next: e.target.value})} data-testid="pw-new-input" />
            </Field>
            <Field label="Bestätigung">
              <Input type="password" value={pwForm.confirm} onChange={e => setPwForm({...pwForm, confirm: e.target.value})} data-testid="pw-confirm-input" />
            </Field>
            {pwMsg && <p className={`text-xs ${pwMsg.ok ? "text-green-600" : "text-red-500"}`}>{pwMsg.text}</p>}
            <Button onClick={changePassword} data-testid="pw-change-button">Passwort ändern</Button>
          </CardContent>
        </Card>
        <Card className="dashboard-card"><CardHeader><CardTitle>Einstellungen</CardTitle><CardDescription>Branding- und E-Mail-Einstellungen sind nur für Admins. Kundenspezifische Anpassungen folgen in der nächsten Ausbaustufe.</CardDescription></CardHeader><CardContent><p className="text-sm text-muted-foreground">Webhook-URLs und die technische Einrichtung findest du unten.</p></CardContent></Card>
      </>}

      {/* Outside the isAdmin branch: the API allows admin too, so support can produce the export
          on a client's behalf. Needs a client context — there is nothing to export at /admin/settings.
          The card itself lives in datenschutz.js and is rendered from BOTH places: it is live on a
          production tenant, so removing it here would be a regression. */}
      {clientId && canGdpr.export ? <ClientExportCard api={api} clientId={clientId} /> : null}

      {isAdmin && clientId ? <div className="grid gap-5 xl:grid-cols-2">
        <Card className="dashboard-card"><CardHeader><CardTitle>User Management</CardTitle><CardDescription>Kundenpersonen anlegen und einem Client zuordnen.</CardDescription></CardHeader><CardContent>
          <form className="space-y-4" onSubmit={createUser}>
            <Field label="E-Mail"><Input className="bg-white" data-testid="user-email-input" required value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} /></Field>
            <Field label="Name"><Input className="bg-white" value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} /></Field>
            <div className="grid grid-cols-2 gap-3"><Field label="Rolle"><select className="form-select" value={newUser.role} onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}><option value="client_user">Client User</option><option value="client_admin">Client Admin</option></select></Field><Field label="Initial Passwort optional"><Input className="bg-white" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} /></Field></div>
            <Button type="submit" data-testid="user-create-button">User anlegen</Button>
            {temporaryPassword ? <div className="info-callout" data-testid="temporary-password-box">Temporäres Passwort: <span className="font-mono">{temporaryPassword}</span></div> : null}
          </form>
          <div className="mt-5 space-y-2">
            {clientUsers.map((u) => (
              <div className="agent-mini-row" key={u.id}>
                <div><strong>{u.name}</strong><span>{u.email}</span></div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline">{u.role}</Badge>
                  {isAdmin && <Button size="sm" variant="outline" onClick={() => inviteUser(u.id, u.email)} data-testid="user-invite-button">Einladen</Button>}
                </div>
              </div>
            ))}
            {inviteLink && (
              <div className="info-callout mt-3 space-y-2">
                <p className="text-xs font-medium">Einladungslink (48h gültig):</p>
                <div className="flex gap-2">
                  <Input className="bg-white font-mono text-xs" readOnly value={inviteLink.url} />
                  <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(inviteLink.url); toast.success("Kopiert"); }}>Kopieren</Button>
                </div>
              </div>
            )}
          </div>
        </CardContent></Card>
        <Card className="dashboard-card"><CardHeader><CardTitle>Agent Members</CardTitle><CardDescription>Mehrere Personen/Kunden einem Voice Agent zuweisen.</CardDescription></CardHeader><CardContent>
          <form className="space-y-4" onSubmit={assignMember}>
            <Field label="Agent"><select className="form-select" required data-testid="agent-member-agent-select" value={memberForm.agent_id} onChange={(e) => setMemberForm({ ...memberForm, agent_id: e.target.value })}><option value="">Agent wählen</option>{(agents.data || []).map((agent) => <option key={agent.id} value={agent.id}>{agent.name}</option>)}</select></Field>
            <Field label="User"><select className="form-select" required data-testid="agent-member-user-select" value={memberForm.user_id} onChange={(e) => setMemberForm({ ...memberForm, user_id: e.target.value })}><option value="">User wählen</option>{clientUsers.map((u) => <option key={u.id} value={u.id}>{u.name} · {u.email}</option>)}</select></Field>
            <Field label="Agent Rolle"><select className="form-select" value={memberForm.role} onChange={(e) => setMemberForm({ ...memberForm, role: e.target.value })}><option value="viewer">Viewer</option><option value="editor">Editor</option><option value="owner">Owner</option></select></Field>
            <Button type="submit" data-testid="agent-member-save-button">Zuweisen</Button>
          </form>
        </CardContent></Card>
      </div> : null}

      <div className="grid gap-5 xl:grid-cols-2">
        {isAdmin && <Card className="dashboard-card"><CardHeader><CardTitle>Webhook Endpunkte</CardTitle><CardDescription>Interne Webhook-URLs für die Voice Platform Integration.</CardDescription></CardHeader><CardContent className="space-y-4"><WebhookUrl label="Webhook A" url={`${BACKEND_URL}/api/webhooks/retell`} /><WebhookUrl label="Webhook B" url={`${BACKEND_URL}/api/webhooks/elevenlabs`} /><div className="info-callout"><ShieldCheck className="h-4 w-4" /> GDPR-Löschung entfernt Daten automatisch beim Löschen einer Conversation.</div></CardContent></Card>}
      </div>
    </div>
  );
}

function WebhookUrl({ label, url }) {
  const copy = async () => {
    await navigator.clipboard.writeText(url);
    toast.success("Webhook URL kopiert");
  };
  return <div className="webhook-url-row"><div><strong>{label}</strong><span className="font-mono">{url}</span></div><Button variant="outline" size="sm" onClick={copy} data-testid={`copy-${label.toLowerCase().replaceAll(" ", "-")}-webhook-button`}>Kopieren</Button></div>;
}


function ForgotPasswordPage({ branding }) {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const submit = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      await fetch(`${API}/auth/password-reset/request`, { method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify({ email, origin_url: window.location.origin }) });
      setDone(true);
    } finally { setLoading(false); }
  };
  return <main className="login-shell"><section className="login-brand-panel"><div className="brand-mark"><KeyRound className="h-7 w-7" /></div><div><p className="eyebrow">Password Reset</p><h1>{branding?.company_name || "Meine Bots"}</h1><p className="login-copy">Wenn die E-Mail konfiguriert ist, erhältst du einen sicheren Reset-Link.</p></div></section><section className="login-form-panel"><Card className="login-card"><CardHeader><CardTitle>Passwort zurücksetzen</CardTitle><CardDescription>Gib deine E-Mail-Adresse ein.</CardDescription></CardHeader><CardContent>{done ? <div className="info-callout">Falls der Account existiert und E-Mail konfiguriert ist, wurde ein Reset-Link gesendet.</div> : <form className="space-y-4" onSubmit={submit}><Field label="E-Mail"><Input className="bg-white" data-testid="forgot-email-input" value={email} onChange={(e) => setEmail(e.target.value)} /></Field><Button type="submit" disabled={loading} data-testid="forgot-submit-button">Reset-Link anfordern</Button><Button asChild variant="ghost"><Link to="/login">Zurück zum Login</Link></Button></form>}</CardContent></Card></section></main>;
}

function ResetPasswordPage({ branding }) {
  const navigate = useNavigate();
  const token = new URLSearchParams(window.location.search).get("token") || "";
  const [password, setPassword] = useState("");
  const submit = async (event) => {
    event.preventDefault();
    try {
      const response = await fetch(`${API}/auth/password-reset/confirm`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ token, new_password: password }) });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(formatApiErrorDetail(body.detail || "Reset fehlgeschlagen"));
      toast.success("Passwort aktualisiert");
      navigate("/login");
    } catch (err) { toast.error(err.message); }
  };
  return <main className="login-shell"><section className="login-brand-panel"><div className="brand-mark"><KeyRound className="h-7 w-7" /></div><div><p className="eyebrow">New Password</p><h1>{branding?.company_name || "Meine Bots"}</h1></div></section><section className="login-form-panel"><Card className="login-card"><CardHeader><CardTitle>Neues Passwort</CardTitle><CardDescription>Mindestens 8 Zeichen.</CardDescription></CardHeader><CardContent><form className="space-y-4" onSubmit={submit}><Field label="Neues Passwort"><Input className="bg-white" type="password" data-testid="reset-password-input" value={password} onChange={(e) => setPassword(e.target.value)} /></Field><Button type="submit" data-testid="reset-submit-button">Passwort speichern</Button></form></CardContent></Card></section></main>;
}

const BILLING_CURRENCIES = [["eur", "EUR €"], ["usd", "USD $"], ["gbp", "GBP £"], ["chf", "CHF"], ["cad", "CAD $"], ["aud", "AUD $"]];

function BillingSetup({ api }) {
  const config = useResource(api, "/billing/config");
  const c = config.data || {};
  return (
    <div className="grid gap-5 xl:grid-cols-2">
      <Card className="dashboard-card"><CardHeader><CardTitle>Stripe Verbindung</CardTitle><CardDescription>Status deines Stripe-Kontos für die Abrechnung.</CardDescription></CardHeader><CardContent className="space-y-4">
        <div className="info-callout" data-testid="stripe-status"><ShieldCheck className="h-4 w-4" /> Status: {c.stripe_configured ? `Verbunden (${c.mode})` : "Noch nicht verbunden"}</div>
        {!c.stripe_configured ? (
          <>
            <p className="text-sm text-muted-foreground">Verbinde dein Stripe-Konto, um Produkte, Abos, Rechnungen und Coupons zu verwalten.</p>
            <Button asChild data-testid="stripe-connect-button"><a href="https://connect.stripe.com/setup" target="_blank" rel="noreferrer"><CreditCard className="h-4 w-4" /> Mit Stripe verbinden</a></Button>
          </>
        ) : <Button asChild variant="outline" data-testid="stripe-dashboard-link"><a href="https://dashboard.stripe.com" target="_blank" rel="noreferrer">Stripe Dashboard öffnen</a></Button>}
      </CardContent></Card>
      <Card className="dashboard-card"><CardHeader><CardTitle>Wichtiger Hinweis</CardTitle></CardHeader><CardContent>
        <div className="info-callout" data-testid="stripe-disclaimer"><ShieldCheck className="h-4 w-4" /> Bitte erstelle, bearbeite oder lösche Produkte und Pakete <strong>niemals direkt im Stripe Dashboard</strong>. Verwalte alles ausschließlich hier, damit alle Daten synchron bleiben.</div>
      </CardContent></Card>
    </div>
  );
}

const BLANK_PRODUCT_FORM = { name: "", description: "", pricing_model: "base_subscription", currency: "eur", billing_period: "monthly", price: "", metric: "minutes", usage_limit: "" };

function BillingProducts({ api }) {
  const plans = useResource(api, "/billing/plans?include_archived=true");
  const [form, setForm] = useState(BLANK_PRODUCT_FORM);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);

  const buildPayload = () => {
    const price = Number(form.price) || 0;
    let payload = { name: form.name, description: form.description || null, pricing_model: form.pricing_model, currency: form.currency };
    if (form.pricing_model === "base_subscription") payload = { ...payload, billing_model: "subscription", billing_period: form.billing_period, monthly_fee: price };
    else if (form.pricing_model === "usage_based") payload = { ...payload, billing_model: "usage_only", metric: form.metric, usage_limit: Number(form.usage_limit) || 0, included_minutes: Number(form.usage_limit) || 0, overage_price_per_minute: price, unit_price: price };
    else payload = { ...payload, billing_model: "one_time", setup_fee: price };
    return payload;
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editId) { await api(`/billing/plans/${editId}`, { method: "PUT", body: JSON.stringify(buildPayload()) }); toast.success("Produkt aktualisiert"); }
      else { await api("/billing/plans", { method: "POST", body: JSON.stringify(buildPayload()) }); toast.success("Produkt erstellt"); }
      setForm(BLANK_PRODUCT_FORM); setEditId(null); plans.reload();
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };

  const startEdit = (p) => {
    const pm = p.pricing_model || (p.billing_model === "usage_only" ? "usage_based" : p.billing_model === "one_time" ? "one_time" : "base_subscription");
    const price = pm === "usage_based" ? (p.unit_price ?? p.overage_price_per_minute) : pm === "one_time" ? p.setup_fee : p.monthly_fee;
    setForm({ name: p.name || "", description: p.description || "", pricing_model: pm, currency: p.currency || "eur", billing_period: p.billing_period || "monthly", price: price == null ? "" : String(price), metric: p.metric || "minutes", usage_limit: p.usage_limit == null ? "" : String(p.usage_limit) });
    setEditId(p.id);
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cancelEdit = () => { setForm(BLANK_PRODUCT_FORM); setEditId(null); };

  const archive = async (p) => {
    if (typeof window !== "undefined" && !window.confirm(`Produkt "${p.name}" archivieren? Es wird in Stripe deaktiviert.`)) return;
    try { await api(`/billing/plans/${p.id}/archive`, { method: "POST" }); toast.success("Produkt archiviert"); if (editId === p.id) cancelEdit(); plans.reload(); }
    catch (err) { toast.error(err.message); }
  };

  const activePlans = (plans.data || []).filter((p) => p.is_active !== false);
  const archivedPlans = (plans.data || []).filter((p) => p.is_active === false);

  const row = (p) => (
    <TableRow key={p.id} data-testid="billing-product-row" className={p.is_active === false ? "opacity-50" : ""}>
      <TableCell className="font-medium">{p.name}{p.is_active === false ? <Badge variant="outline" className="ml-2">archiviert</Badge> : null}</TableCell>
      <TableCell><Badge variant="outline">{p.pricing_model || p.billing_model}</Badge></TableCell>
      <TableCell>{money(p.unit_price || p.monthly_fee || p.setup_fee || p.overage_price_per_minute, p.currency)}{p.metric ? ` / ${p.metric}` : ""}</TableCell>
      <TableCell className="font-mono text-xs">{p.stripe_product_id ? "✓" : "—"}</TableCell>
      <TableCell className="text-right whitespace-nowrap">
        <Button variant="outline" size="sm" data-testid="product-edit-button" onClick={() => startEdit(p)}>Bearbeiten</Button>
        {p.is_active === false ? null : <Button variant="ghost" size="sm" className="ml-1 text-red-600 hover:text-red-700" data-testid="product-archive-button" onClick={() => archive(p)}>Archivieren</Button>}
      </TableCell>
    </TableRow>
  );

  return (
    <div className="grid gap-5 xl:grid-cols-[1fr_400px]">
      <Card className="dashboard-card"><CardHeader><CardTitle>Produkte</CardTitle><CardDescription>Alle Stripe-Produkte und Pakete. Bearbeiten oder archivieren.</CardDescription></CardHeader><CardContent>
        {plans.loading ? <Skeleton className="h-40" /> : (activePlans.length || archivedPlans.length) ? (
          <Table data-testid="billing-products-table"><TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Pricing</TableHead><TableHead>Preis</TableHead><TableHead>Stripe</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>{activePlans.map(row)}{archivedPlans.map(row)}</TableBody></Table>
        ) : <EmptyState title="Keine Produkte" description="Erstelle dein erstes Produkt rechts." />}
      </CardContent></Card>
      <Card className="dashboard-card"><CardHeader><CardTitle>{editId ? "Produkt bearbeiten" : "Produkt erstellen"}</CardTitle><CardDescription>Speichert Produkt und Preise in Stripe.</CardDescription></CardHeader><CardContent>
        <form className="space-y-4" onSubmit={submit}>
          <Field label="Produktname"><Input className="bg-white" required data-testid="product-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
          <Field label="Beschreibung (optional)"><Textarea className="bg-white" data-testid="product-description-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
          <Field label="Pricing Model"><select className="form-select" data-testid="product-pricing-model-select" value={form.pricing_model} onChange={(e) => setForm({ ...form, pricing_model: e.target.value })}><option value="base_subscription">Base Subscription Fee</option><option value="usage_based">Usage Based</option><option value="one_time">One Time</option></select></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Währung"><select className="form-select" data-testid="product-currency-select" value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })}>{BILLING_CURRENCIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></Field>
            {form.pricing_model === "base_subscription" ? <Field label="Abrechnungsperiode"><select className="form-select" data-testid="product-billing-period-select" value={form.billing_period} onChange={(e) => setForm({ ...form, billing_period: e.target.value })}><option value="monthly">Monatlich</option><option value="weekly">Wöchentlich</option><option value="yearly">Jährlich</option><option value="daily">Täglich</option></select></Field> : null}
          </div>
          {form.pricing_model === "usage_based" ? (
            <div className="grid grid-cols-2 gap-3">
              <Field label="Metrik"><select className="form-select" data-testid="product-metric-select" value={form.metric} onChange={(e) => setForm({ ...form, metric: e.target.value })}><option value="minutes">Minuten</option><option value="interactions">Interaktionen</option><option value="conversations">Conversations</option></select></Field>
              <Field label="Nutzungslimit"><Input className="bg-white" type="number" data-testid="product-usage-limit-input" value={form.usage_limit} onChange={(e) => setForm({ ...form, usage_limit: e.target.value })} placeholder="z.B. 200 (0 = kein Limit)" /></Field>
            </div>
          ) : null}
          <Field label={form.pricing_model === "usage_based" ? `Preis pro ${form.metric}` : "Preis"}><Input className="bg-white" required data-testid="product-price-input" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} placeholder="z.B. 0.15 oder 99" /></Field>
          <div className="flex gap-2">
            <Button type="submit" disabled={busy} data-testid="product-create-button">{busy ? <RefreshCw className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />} {editId ? "Aktualisieren" : "Produkt erstellen"}</Button>
            {editId ? <Button type="button" variant="outline" onClick={cancelEdit} data-testid="product-edit-cancel">Abbrechen</Button> : null}
          </div>
        </form>
      </CardContent></Card>
    </div>
  );
}

function BillingSubscriptions({ api }) {
  const subs = useResource(api, "/billing/subscriptions");
  const clients = useResource(api, "/clients");
  const plans = useResource(api, "/billing/plans");
  const coupons = useResource(api, "/billing/coupons");
  const [form, setForm] = useState({ client_id: "", plan_id: "", agent_ids: [], start_date: "", trial_days: "", coupon_id: "" });
  const agents = useResource(api, form.client_id ? `/agents?client_id=${form.client_id}` : null, [form.client_id]);
  const toggleAgent = (id) => setForm((f) => ({ ...f, agent_ids: f.agent_ids.includes(id) ? f.agent_ids.filter((a) => a !== id) : [...f.agent_ids, id] }));
  const create = async (e) => {
    e.preventDefault();
    try {
      await api("/billing/subscriptions", { method: "POST", body: JSON.stringify({ client_id: form.client_id, plan_id: form.plan_id, agent_ids: form.agent_ids, start_date: form.start_date || null, trial_days: form.trial_days ? Number(form.trial_days) : null, coupon_id: form.coupon_id || null }) });
      toast.success("Subscription erstellt"); setForm({ client_id: "", plan_id: "", agent_ids: [], start_date: "", trial_days: "", coupon_id: "" }); subs.reload();
    } catch (err) { toast.error(err.message); }
  };
  return (
    <div className="grid gap-5 xl:grid-cols-[1fr_400px]">
      <Card className="dashboard-card"><CardHeader><CardTitle>Subscriptions</CardTitle><CardDescription>Aktive und geplante Abos.</CardDescription></CardHeader><CardContent>
        {subs.loading ? <Skeleton className="h-40" /> : (subs.data || []).length ? (
          <Table data-testid="billing-subscriptions-table"><TableHeader><TableRow><TableHead>Client</TableHead><TableHead>Produkt</TableHead><TableHead>Status</TableHead><TableHead>Start</TableHead></TableRow></TableHeader>
            <TableBody>{subs.data.map((s) => <TableRow key={s.id}><TableCell className="font-medium">{s.client_name || s.client_id}</TableCell><TableCell>{s.plan_name || s.plan_id}</TableCell><TableCell><StatusBadge status={s.status} /></TableCell><TableCell className="text-xs">{s.start_date ? formatDate(s.start_date) : "sofort"}</TableCell></TableRow>)}</TableBody></Table>
        ) : <EmptyState title="Keine Subscriptions" description="Erstelle ein Abo rechts." />}
      </CardContent></Card>
      <Card className="dashboard-card"><CardHeader><CardTitle>Subscription erstellen</CardTitle><CardDescription>Client, Agents, Start, Trial und Coupon.</CardDescription></CardHeader><CardContent>
        <form className="space-y-4" onSubmit={create}>
          <Field label="Client"><select className="form-select" required data-testid="sub-client-select" value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value, agent_ids: [] })}><option value="">Client wählen…</option>{(clients.data || []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select></Field>
          <Field label="Produkt"><select className="form-select" required data-testid="sub-plan-select" value={form.plan_id} onChange={(e) => setForm({ ...form, plan_id: e.target.value })}><option value="">Produkt wählen…</option>{(plans.data || []).filter((p) => ["subscription", "hybrid"].includes(p.billing_model) || p.pricing_model === "base_subscription").map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select></Field>
          {form.client_id ? (
            <Field label="Agents">
              <div className="space-y-1.5 max-h-40 overflow-auto" data-testid="sub-agents-list">
                {(agents.data || []).map((a) => <label key={a.id} className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.agent_ids.includes(a.id)} onChange={() => toggleAgent(a.id)} data-testid="sub-agent-checkbox" /> {a.name}</label>)}
                {!(agents.data || []).length ? <p className="text-xs text-muted-foreground">Keine Agents für diesen Client.</p> : null}
              </div>
            </Field>
          ) : null}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Startdatum"><Input className="bg-white" type="date" data-testid="sub-start-date-input" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></Field>
            <Field label="Trial (Tage, optional)"><Input className="bg-white" type="number" data-testid="sub-trial-input" value={form.trial_days} onChange={(e) => setForm({ ...form, trial_days: e.target.value })} /></Field>
          </div>
          <Field label="Coupon (optional)"><select className="form-select" data-testid="sub-coupon-select" value={form.coupon_id} onChange={(e) => setForm({ ...form, coupon_id: e.target.value })}><option value="">Kein Coupon</option>{(coupons.data || []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select></Field>
          <Button type="submit" data-testid="sub-create-button"><Plus className="h-4 w-4" /> Subscription erstellen</Button>
        </form>
      </CardContent></Card>
    </div>
  );
}

function BillingTransactions({ api }) {
  const tx = useResource(api, "/billing/transactions");
  return (
    <Card className="dashboard-card"><CardHeader><CardTitle>Transactions</CardTitle><CardDescription>Alle Zahlungen und Checkout Sessions.</CardDescription></CardHeader><CardContent>
      {tx.loading ? <Skeleton className="h-40" /> : (tx.data || []).length ? (
        <Table data-testid="billing-transactions-list"><TableHeader><TableRow><TableHead>Datum</TableHead><TableHead>Mode</TableHead><TableHead>Status</TableHead><TableHead>Payment</TableHead></TableRow></TableHeader>
          <TableBody>{tx.data.map((t) => <TableRow key={t.id}><TableCell className="text-xs">{formatDate(t.created_at)}</TableCell><TableCell>{t.mode}</TableCell><TableCell><StatusBadge status={t.status} /></TableCell><TableCell>{t.payment_status}</TableCell></TableRow>)}</TableBody></Table>
      ) : <EmptyState title="Keine Transactions" description="Zahlungen erscheinen hier." />}
    </CardContent></Card>
  );
}

function BillingInvoices({ api }) {
  const invoices = useResource(api, "/billing/invoices/manual");
  const clients = useResource(api, "/clients");
  const [form, setForm] = useState({ client_id: "", description: "", amount: "", currency: "eur" });
  const create = async (e) => {
    e.preventDefault();
    try { await api("/billing/invoices/create", { method: "POST", body: JSON.stringify({ client_id: form.client_id, description: form.description, amount: Number(form.amount) || 0, currency: form.currency }) }); toast.success("Rechnung erstellt"); setForm({ ...form, description: "", amount: "" }); invoices.reload(); }
    catch (err) { toast.error(err.message); }
  };
  return (
    <div className="grid gap-5 xl:grid-cols-[1fr_400px]">
      <Card className="dashboard-card"><CardHeader><CardTitle>Rechnungen</CardTitle><CardDescription>Manuell erstellte Rechnungen.</CardDescription></CardHeader><CardContent>
        {invoices.loading ? <Skeleton className="h-40" /> : (invoices.data || []).length ? (
          <Table data-testid="billing-invoices-list"><TableHeader><TableRow><TableHead>Datum</TableHead><TableHead>Client</TableHead><TableHead>Beschreibung</TableHead><TableHead>Betrag</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
            <TableBody>{invoices.data.map((inv) => <TableRow key={inv.id}><TableCell className="text-xs">{formatDate(inv.created_at)}</TableCell><TableCell>{inv.client_id}</TableCell><TableCell>{inv.description}</TableCell><TableCell className="font-semibold">{money(inv.amount, inv.currency)}</TableCell><TableCell><StatusBadge status={inv.status} /></TableCell></TableRow>)}</TableBody></Table>
        ) : <EmptyState title="Keine Rechnungen" description="Erstelle eine Rechnung rechts." />}
      </CardContent></Card>
      <Card className="dashboard-card"><CardHeader><CardTitle>Rechnung erstellen</CardTitle><CardDescription>Einmalige Stripe-Rechnung.</CardDescription></CardHeader><CardContent>
        <form className="space-y-4" onSubmit={create}>
          <Field label="Client"><select className="form-select" required data-testid="invoice-client-select" value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })}><option value="">Client wählen…</option>{(clients.data || []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select></Field>
          <Field label="Beschreibung"><Input className="bg-white" required data-testid="invoice-description-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Betrag"><Input className="bg-white" required data-testid="invoice-amount-input" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} /></Field>
            <Field label="Währung"><select className="form-select" value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })}>{BILLING_CURRENCIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></Field>
          </div>
          <Button type="submit" data-testid="invoice-create-button"><FileText className="h-4 w-4" /> Rechnung erstellen</Button>
        </form>
      </CardContent></Card>
    </div>
  );
}

function BillingCoupons({ api }) {
  const coupons = useResource(api, "/billing/coupons");
  const [form, setForm] = useState({ name: "", discount_type: "percentage", amount: "", currency: "eur", duration: "once", duration_in_months: "", limitDate: false, redeem_by: "", limitRedemptions: false, max_redemptions: "" });
  const create = async (e) => {
    e.preventDefault();
    const payload = { name: form.name, discount_type: form.discount_type, amount: Number(form.amount) || 0, currency: form.currency, duration: form.duration };
    if (form.duration === "repeating") payload.duration_in_months = Number(form.duration_in_months) || 1;
    if (form.limitDate && form.redeem_by) payload.redeem_by = `${form.redeem_by}T23:59:59+00:00`;
    if (form.limitRedemptions && form.max_redemptions) payload.max_redemptions = Number(form.max_redemptions);
    try { await api("/billing/coupons", { method: "POST", body: JSON.stringify(payload) }); toast.success("Coupon erstellt"); setForm({ ...form, name: "", amount: "" }); coupons.reload(); }
    catch (err) { toast.error(err.message); }
  };
  const remove = async (id) => {
    if (!window.confirm("Coupon löschen?")) return;
    try { await api(`/billing/coupons/${id}`, { method: "DELETE" }); toast.success("Coupon gelöscht"); coupons.reload(); }
    catch (err) { toast.error(err.message); }
  };
  return (
    <div className="grid gap-5 xl:grid-cols-[1fr_400px]">
      <Card className="dashboard-card"><CardHeader><CardTitle>Coupons</CardTitle><CardDescription>Rabattcodes für Subscriptions.</CardDescription></CardHeader><CardContent>
        {coupons.loading ? <Skeleton className="h-40" /> : (coupons.data || []).length ? (
          <Table data-testid="billing-coupons-table"><TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Rabatt</TableHead><TableHead>Dauer</TableHead><TableHead>Limit</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>{coupons.data.map((c) => <TableRow key={c.id}><TableCell className="font-medium">{c.name}</TableCell><TableCell>{c.discount_type === "percentage" ? `${c.amount}%` : money(c.amount, c.currency)}</TableCell><TableCell>{c.duration}{c.duration_in_months ? ` (${c.duration_in_months}m)` : ""}</TableCell><TableCell className="text-xs">{c.max_redemptions ? `${c.times_redeemed || 0}/${c.max_redemptions}` : "∞"}</TableCell><TableCell className="text-right"><Button variant="ghost" size="sm" onClick={() => remove(c.id)} data-testid="coupon-delete-button"><XCircle className="h-4 w-4" /></Button></TableCell></TableRow>)}</TableBody></Table>
        ) : <EmptyState title="Keine Coupons" description="Erstelle einen Coupon rechts." />}
      </CardContent></Card>
      <Card className="dashboard-card"><CardHeader><CardTitle>Coupon erstellen</CardTitle><CardDescription>Rabatt, Dauer und Einlöse-Limits.</CardDescription></CardHeader><CardContent>
        <form className="space-y-4" onSubmit={create}>
          <Field label="Coupon Name"><Input className="bg-white" required data-testid="coupon-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Rabatt-Typ"><select className="form-select" data-testid="coupon-type-select" value={form.discount_type} onChange={(e) => setForm({ ...form, discount_type: e.target.value })}><option value="percentage">Prozent</option><option value="fixed">Fester Betrag</option></select></Field>
            <Field label={form.discount_type === "percentage" ? "Prozent (%)" : "Betrag"}><Input className="bg-white" required data-testid="coupon-amount-input" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} /></Field>
          </div>
          {form.discount_type === "fixed" ? <Field label="Währung"><select className="form-select" value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })}>{BILLING_CURRENCIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></Field> : null}
          <Field label="Dauer"><select className="form-select" data-testid="coupon-duration-select" value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })}><option value="once">Einmalig</option><option value="repeating">Mehrere Monate</option><option value="forever">Für immer</option></select></Field>
          {form.duration === "repeating" ? <Field label="Anzahl Monate"><Input className="bg-white" type="number" data-testid="coupon-months-input" value={form.duration_in_months} onChange={(e) => setForm({ ...form, duration_in_months: e.target.value })} /></Field> : null}
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.limitDate} onChange={(e) => setForm({ ...form, limitDate: e.target.checked })} data-testid="coupon-limit-date-check" /> Einlöse-Zeitraum begrenzen</label>
          {form.limitDate ? <Field label="Einlösbar bis"><Input className="bg-white" type="date" data-testid="coupon-redeem-by-input" value={form.redeem_by} onChange={(e) => setForm({ ...form, redeem_by: e.target.value })} /></Field> : null}
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.limitRedemptions} onChange={(e) => setForm({ ...form, limitRedemptions: e.target.checked })} data-testid="coupon-limit-redemptions-check" /> Anzahl Einlösungen begrenzen</label>
          {form.limitRedemptions ? <Field label="Max. Einlösungen"><Input className="bg-white" type="number" data-testid="coupon-max-redemptions-input" value={form.max_redemptions} onChange={(e) => setForm({ ...form, max_redemptions: e.target.value })} /></Field> : null}
          <Button type="submit" data-testid="coupon-create-button"><Plus className="h-4 w-4" /> Coupon erstellen</Button>
        </form>
      </CardContent></Card>
    </div>
  );
}

// Usage-Abrechnungs-Zuweisungen (POST /billing/assignments) — aus dem Per-Client-Reiter hierher
// verschoben. Client wählen → bestehende Zuweisungen + Checkout, und neue Zuweisung anlegen.
function BillingAssignments({ api }) {
  const clients = useResource(api, "/clients");
  const plans = useResource(api, "/billing/plans");
  const [clientId, setClientId] = useState("");
  const assignments = useResource(api, clientId ? `/billing/assignments?client_id=${clientId}` : null, [clientId]);
  const agents = useResource(api, clientId ? `/agents?client_id=${clientId}` : null, [clientId]);
  const [form, setForm] = useState({ plan_id: "", agent_id: "", billing_anchor_day: "", billing_start_date: "" });

  const createAssignment = async (e) => {
    e.preventDefault();
    try {
      await api("/billing/assignments", { method: "POST", body: JSON.stringify({
        client_id: clientId,
        plan_id: form.plan_id,
        agent_id: form.agent_id || null,
        billing_anchor_day: form.billing_anchor_day ? Number(form.billing_anchor_day) : null,
        billing_start_date: form.billing_start_date ? new Date(form.billing_start_date + "T00:00:00Z").toISOString() : null,
      }) });
      toast.success("Billing Plan zugewiesen");
      setForm({ plan_id: "", agent_id: "", billing_anchor_day: "", billing_start_date: "" });
      assignments.reload();
    } catch (err) { toast.error(err.message); }
  };

  const startCheckout = async (assignmentId) => {
    try {
      const session = await api("/billing/checkout/session", { method: "POST", body: JSON.stringify({ assignment_id: assignmentId, origin_url: window.location.origin }) });
      window.location.href = session.url;
    } catch (err) { toast.error(err.message); }
  };

  return (
    <div className="grid gap-5 xl:grid-cols-[1fr_400px]">
      <Card className="dashboard-card"><CardHeader><CardTitle>Client / Agent Assignments</CardTitle><CardDescription>Usage-Abrechnung pro Client/Agent — Checkout starten, um den Stripe Customer (Karte) zu aktivieren.</CardDescription></CardHeader><CardContent className="space-y-4">
        <Field label="Client"><select className="form-select" data-testid="assign-client-select" value={clientId} onChange={(e) => setClientId(e.target.value)}><option value="">Client wählen…</option>{(clients.data || []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select></Field>
        {!clientId ? <EmptyState title="Kein Client gewählt" description="Wähle oben einen Client, um seine Zuweisungen zu sehen." /> : assignments.loading ? <Skeleton className="h-40" /> : (assignments.data || []).length ? (
          <Table data-testid="assign-table"><TableHeader><TableRow><TableHead>Plan</TableHead><TableHead>Agent</TableHead><TableHead>Status</TableHead><TableHead>Abrechnungstag</TableHead><TableHead>Letzter Lauf</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>{assignments.data.map((a) => <TableRow key={a.id}><TableCell>{a.plan?.name || a.plan_id}</TableCell><TableCell>{a.agent?.name || "Alle Agents"}</TableCell><TableCell><StatusBadge status={a.status} /></TableCell><TableCell className="text-xs">{a.billing_anchor_day ? `${a.billing_anchor_day}. des Monats` : "—"}</TableCell><TableCell className="text-xs text-muted-foreground">{a.last_billed_period || "—"}</TableCell><TableCell className="text-right"><Button variant="outline" size="sm" onClick={() => startCheckout(a.id)} data-testid="assign-checkout-button">Checkout</Button></TableCell></TableRow>)}</TableBody></Table>
        ) : <EmptyState title="Keine Zuweisungen" description="Weise rechts einen Plan zu." />}
      </CardContent></Card>
      <Card className="dashboard-card"><CardHeader><CardTitle>Plan zuweisen</CardTitle><CardDescription>Erstellt Stripe Customer pro Client. Usage-Abrechnung am Abrechnungstag.</CardDescription></CardHeader><CardContent>
        {!clientId ? <p className="text-sm text-muted-foreground">Zuerst oben einen Client wählen.</p> : <form className="space-y-4" onSubmit={createAssignment}>
          <Field label="Plan"><select className="form-select" required data-testid="assign-plan-select" value={form.plan_id} onChange={(e) => setForm({ ...form, plan_id: e.target.value })}><option value="">Plan wählen</option>{(plans.data || []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select></Field>
          <Field label="Agent (optional)"><select className="form-select" data-testid="assign-agent-select" value={form.agent_id} onChange={(e) => setForm({ ...form, agent_id: e.target.value })}><option value="">Alle Agents des Clients</option>{(agents.data || []).map((ag) => <option key={ag.id} value={ag.id}>{ag.name}</option>)}</select></Field>
          <Field label="Abrechnungstag (1–31)"><Input className="bg-white" type="number" min="1" max="31" placeholder="31 = Monatsende" data-testid="assign-anchor-day-input" value={form.billing_anchor_day} onChange={(e) => setForm({ ...form, billing_anchor_day: e.target.value })} /></Field>
          <Field label="Abrechnung ab (Go-Live)"><Input className="bg-white" type="date" data-testid="assign-start-date-input" value={form.billing_start_date} onChange={(e) => setForm({ ...form, billing_start_date: e.target.value })} /><span className="text-xs text-muted-foreground">Nutzung vor diesem Datum wird nie berechnet.</span></Field>
          <Button type="submit" data-testid="assign-save-button"><Plus className="h-4 w-4" /> Zuweisen</Button>
        </form>}
      </CardContent></Card>
    </div>
  );
}

function AdminBilling({ api, user }) {
  const [tab, setTab] = useState("setup");
  const BILLING_CONSOLE_TABS = [
    { key: "setup", label: "Setup" }, { key: "products", label: "Products" }, { key: "subscriptions", label: "Subscriptions" },
    { key: "assignments", label: "Zuweisungen" },
    { key: "transactions", label: "Transactions" }, { key: "invoices", label: "Invoices" }, { key: "coupons", label: "Coupons" },
  ];
  if (user.role !== "admin") return <Navigate to={`/clients/${user.client_id}/overview`} replace />;
  return (
    <PageMotion>
      <PageHeader eyebrow="Admin" title="Billing" description="Stripe-basierte Produkte, Abos, Rechnungen und Coupons." />
      <Tabs value={tab} className="client-tabs">
        <TabsList className="tabs-list" data-testid="billing-console-tabs">
          {BILLING_CONSOLE_TABS.map((t) => <TabsTrigger key={t.key} value={t.key} onClick={() => setTab(t.key)} data-testid={`billing-tab-${t.key}`}>{t.label}</TabsTrigger>)}
        </TabsList>
      </Tabs>
      <div className="mt-5">
        {tab === "setup" ? <BillingSetup api={api} /> : null}
        {tab === "products" ? <BillingProducts api={api} /> : null}
        {tab === "subscriptions" ? <BillingSubscriptions api={api} /> : null}
        {tab === "assignments" ? <BillingAssignments api={api} /> : null}
        {tab === "transactions" ? <BillingTransactions api={api} /> : null}
        {tab === "invoices" ? <BillingInvoices api={api} /> : null}
        {tab === "coupons" ? <BillingCoupons api={api} /> : null}
      </div>
    </PageMotion>
  );
}

const PREVIEW_TABS = [
  { key: "overview", label: "Übersicht", icon: Gauge },
  { key: "agents", label: "Agenten", icon: Bot },
  { key: "conversations", label: "Gespräche", icon: MessageSquareText },
  { key: "knowledge-base", label: "Wissensdatenbank", icon: BookOpen },
  { key: "billing", label: "Abrechnung", icon: CreditCard },
  { key: "datenschutz", label: "Datenschutz", icon: ShieldCheck },
  { key: "prompt", label: "Prompt", icon: FileText, requiresFeature: "agent_prompt_view" },
];

const RANGE_OPTIONS = [
  { value: "24h", label: "Letzte 24 Stunden" },
  { value: "7d", label: "Letzte 7 Tage" },
  { value: "30d", label: "Letzte 30 Tage" },
  { value: "90d", label: "Letzte 90 Tage" },
  { value: "custom", label: "Benutzerdefiniert" },
];

function DateRangeSelect({ value, onChange, custom, onCustomChange }) {
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <select className="form-select" data-testid="preview-daterange-select" value={value} onChange={(e) => onChange(e.target.value)}>
        {RANGE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      {value === "custom" ? (
        <>
          <Input type="date" className="bg-white" data-testid="preview-custom-start" value={custom.start} onChange={(e) => onCustomChange({ ...custom, start: e.target.value })} />
          <span className="text-muted-foreground text-sm">–</span>
          <Input type="date" className="bg-white" data-testid="preview-custom-end" value={custom.end} onChange={(e) => onCustomChange({ ...custom, end: e.target.value })} />
        </>
      ) : null}
    </div>
  );
}

function PreviewAnalytics({ api, clientId, rangeQuery }) {
  const [agentId, setAgentId] = useState("");
  useEffect(() => { setAgentId(""); }, [clientId]); // reset filter when switching client (avoid stale agent_id)
  const aq = agentId ? `&agent_id=${agentId}` : "";
  const agents = useResource(api, `/agents?client_id=${clientId}`, [clientId]);
  const summary = useResource(api, `/usage/summary?client_id=${clientId}${rangeQuery}${aq}`, [clientId, rangeQuery, agentId]);
  const timeseries = useResource(api, `/usage/timeseries?client_id=${clientId}${rangeQuery}${aq}`, [clientId, rangeQuery, agentId]);
  if (summary.loading && !summary.data) return <LoadingCards />;
  const kpi = summary.data || {};
  const chartData = timeseries.data || [];
  return (
    <div className="space-y-5">
      {(agents.data || []).length > 1 ? (
        <div className="flex items-center justify-end">
          <select className="form-select w-auto" data-testid="preview-agent-select" value={agentId} onChange={(e) => setAgentId(e.target.value)}>
            <option value="">Gesamt (alle Agenten)</option>
            {(agents.data || []).map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        </div>
      ) : null}
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <KpiTile testId="preview-kpi-calls" title="Anrufe" value={kpi.calls || 0} subtitle="Anrufe im Zeitraum" icon={PhoneCall} tone="info" />
        <KpiTile testId="preview-kpi-minutes" title="Minuten" value={kpi.minutes || 0} subtitle="Gesamte Gesprächsdauer" icon={Clock} tone="success" />
        <KpiTile testId="preview-kpi-conversations" title="Gespräche" value={kpi.conversations || 0} subtitle="Anzahl Gespräche" icon={MessageSquareText} tone="default" />
        <KpiTile testId="preview-kpi-success" title="Erfolgsquote" value={`${kpi.success_rate || 0}%`} subtitle="Erfolgreiche Anrufe" icon={CheckCircle2} tone="success" />
      </div>
      {(kpi.custom_metrics || []).length ? (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4" data-testid="preview-custom-metrics-grid">
          {kpi.custom_metrics.map((m) => <KpiTile key={m.key} testId={`preview-kpi-${m.key}`} title={m.label} value={m.value ?? "—"} subtitle="Eigene Kennzahl" icon={Gauge} tone="default" />)}
        </div>
      ) : null}
      <Card className="dashboard-card">
        <CardHeader><CardTitle>Nutzungsverlauf</CardTitle><CardDescription>Anrufe und Minuten im gewählten Zeitraum.</CardDescription></CardHeader>
        <CardContent className="h-[320px]" data-testid="preview-usage-chart">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chartData} margin={{ left: 0, right: 12, top: 12, bottom: 0 }}>
              <defs><linearGradient id="previewArea" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#5B51EC" stopOpacity={0.24} /><stop offset="95%" stopColor="#5B51EC" stopOpacity={0.03} /></linearGradient></defs>
              <CartesianGrid stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="date" tickLine={false} axisLine={false} tick={{ fontSize: 12 }} />
              <YAxis tickLine={false} axisLine={false} tick={{ fontSize: 12 }} />
              <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #DDE3EA" }} />
              <Area type="monotone" dataKey="minutes" stroke="#5B51EC" fill="url(#previewArea)" strokeWidth={2} />
              <Bar dataKey="calls" fill="#CFCBF6" radius={[6, 6, 0, 0]} />
            </ComposedChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </div>
  );
}

function PreviewAgents({ api, clientId }) {
  const agents = useResource(api, `/agents?client_id=${clientId}`, [clientId]);
  return (
    <Card className="dashboard-card">
      <CardHeader><CardTitle>Deine Agents</CardTitle><CardDescription>Alle dir zugewiesenen Voice Agents.</CardDescription></CardHeader>
      <CardContent>
        {agents.loading ? <Skeleton className="h-40" /> : (agents.data || []).length ? (
          <Table data-testid="preview-agents-table"><TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Sprache</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
            <TableBody>{agents.data.map((a) => <TableRow key={a.id}><TableCell className="font-medium">{a.name}</TableCell><TableCell>{a.language || "—"}</TableCell><TableCell><StatusBadge status={a.is_active ? "active" : "inactive"} /></TableCell></TableRow>)}</TableBody></Table>
        ) : <EmptyState title="Keine Agents" description="Noch keine Agents zugewiesen." />}
      </CardContent>
    </Card>
  );
}

function PreviewConversations({ api, clientId, canGdpr = {} }) {
  const conversations = useResource(api, `/conversations?client_id=${clientId}&limit=100`, [clientId]);
  const [selectedId, setSelectedId] = useState(null);
  const detail = useResource(api, selectedId ? `/conversations/${selectedId}` : null, [selectedId]);
  const conv = detail.data;
  return (
    <div className="grid gap-5 xl:grid-cols-[420px_minmax(0,1fr)]">
      <Card className="dashboard-card min-w-0">
        <CardHeader><CardTitle>Gespräche</CardTitle><CardDescription>Klicke eine Zeile, um das Transkript zu sehen.</CardDescription></CardHeader>
        <CardContent>
          {conversations.loading ? <Skeleton className="h-60" /> : (conversations.data || []).length ? (
            <div className="overflow-x-hidden">
            <Table data-testid="preview-conversations-table"><TableHeader><TableRow><TableHead>Datum</TableHead><TableHead>Agent</TableHead><TableHead>Dauer</TableHead></TableRow></TableHeader>
              <TableBody>{conversations.data.map((c) => (
                <TableRow key={c.id} className={cx("cursor-pointer", selectedId === c.id && "bg-primary/5")} role="button" tabIndex={0}
                  onClick={() => setSelectedId(c.id)}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setSelectedId(c.id); } }} data-testid="preview-conversation-row">
                  <TableCell className="text-xs whitespace-nowrap">{formatDateShort(c.created_at || c.started_at)}</TableCell>
                  <TableCell className="font-medium text-xs truncate max-w-[130px]">{c.agent_name || "Agent"}</TableCell>
                  <TableCell className="font-mono text-xs whitespace-nowrap">{formatDuration(c.duration_seconds)}</TableCell>
                </TableRow>
              ))}</TableBody></Table>
            </div>
          ) : <EmptyState title="Keine Gespräche" description="Noch keine Anrufe vorhanden." />}
        </CardContent>
      </Card>
      <Card className="dashboard-card detail-panel min-w-0" data-testid="preview-transcript-panel">
        <CardHeader><CardTitle>Transkript</CardTitle><CardDescription>{conv ? (conv.provider_conversation_id || conv.id) : "Gespräch auswählen"}</CardDescription></CardHeader>
        <CardContent>
          {!conv ? <EmptyState title="Kein Transkript gewählt" description="Wähle ein Gespräch aus der Liste." /> : detail.loading ? <Skeleton className="h-72" /> : (
            <div className="space-y-4">
              {/* The real view's button row, mirrored so the Vorschau stops understating what a
                  member has — but BOTH buttons dead: an admin previewing a live tenant must not be
                  one click from an irreversible ElevenLabs deletion, and one live plus one dead
                  button is a worse affordance than two dead ones. Rendered after the previewed
                  MEMBER's gdpr rights, never unconditionally. */}
              {canGdpr.export || canGdpr.erase ? (
                <div className="flex gap-2 flex-wrap items-center" data-testid="preview-gdpr-actions">
                  {canGdpr.export ? <Button variant="outline" size="sm" disabled data-testid="preview-conversation-export-button">Export JSON</Button> : null}
                  {canGdpr.erase ? <Button variant="destructive" size="sm" disabled data-testid="preview-conversation-delete-button">DSGVO löschen</Button> : null}
                  <span className="text-xs text-muted-foreground">In der Vorschau deaktiviert</span>
                </div>
              ) : null}
              <div className="metadata-grid"><div><span>Datum</span><strong>{formatDate(conv.started_at || conv.created_at)}</strong></div><div><span>Status</span><StatusBadge status={conv.status} /></div><div><span>Dauer</span><strong>{formatDuration(conv.duration_seconds)}</strong></div><div><span>Anrufer</span><strong className="font-mono text-sm">{conv.contact_address || "—"}</strong></div></div>
              <div className="transcript text-[15px] leading-7 max-h-[calc(100vh-360px)] overflow-y-auto" data-testid="preview-transcript">{(conv.messages || []).map((m) => <div key={m.id} className={cx("transcript-bubble", m.side === "agent" ? "agent" : "user")}><div className="transcript-meta"><strong>{m.side === "agent" ? "Agent" : "Anrufer"}</strong><span>{formatDate(m.timestamp)}</span></div><p>{m.text}</p></div>)}</div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function PreviewKnowledgeBase({ api, clientId }) {
  const sources = useResource(api, `/knowledge-sources?client_id=${clientId}`, [clientId]);
  return (
    <Card className="dashboard-card">
      <CardHeader><CardTitle>Wissensdatenbank</CardTitle><CardDescription>Wissensquellen deiner Agenten.</CardDescription></CardHeader>
      <CardContent>
        {sources.loading ? <Skeleton className="h-40" /> : (sources.data || []).length ? (
          <Table data-testid="preview-kb-table"><TableHeader><TableRow><TableHead>Titel</TableHead><TableHead>Typ</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
            <TableBody>{sources.data.map((s) => <TableRow key={s.id}><TableCell className="font-medium">{s.title}</TableCell><TableCell>{s.type}</TableCell><TableCell><StatusBadge status={s.is_active ? "active" : "inactive"} /></TableCell></TableRow>)}</TableBody></Table>
        ) : <EmptyState title="Keine Wissensquellen" description="Noch keine Quellen hinterlegt." />}
      </CardContent>
    </Card>
  );
}

function PreviewPrompt({ api, clientId }) {
  const agents = useResource(api, `/agents?client_id=${clientId}`, [clientId]);
  const [agentId, setAgentId] = useState("");
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = async (id) => {
    setAgentId(id); setCfg(null);
    if (!id) return;
    setLoading(true);
    try { setCfg(await api(`/agents/${id}/provider-config`)); } catch (e) { setCfg({ error: e.message }); } finally { setLoading(false); }
  };
  return (
    <Card className="dashboard-card">
      <CardHeader><CardTitle>Agent Prompt</CardTitle><CardDescription>Vom Administrator freigegeben — nur lesend.</CardDescription></CardHeader>
      <CardContent className="space-y-4">
        <Field label="Agent">
          <select className="form-select" data-testid="preview-prompt-agent-select" value={agentId} onChange={(e) => load(e.target.value)}>
            <option value="">Agent wählen…</option>
            {(agents.data || []).map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        </Field>
        {loading ? <Skeleton className="h-40" /> : cfg ? (
          cfg.error ? <div className="error-alert" data-testid="preview-prompt-error">{cfg.error}</div> : (
            <>
              <Field label="System Prompt"><pre className="whitespace-pre-wrap rounded-md border bg-muted/40 p-3 text-xs max-h-[420px] overflow-auto" data-testid="preview-prompt-text">{cfg.prompt || "Kein Prompt verfügbar."}</pre></Field>
              {cfg.first_message ? <Field label="First Message"><p className="text-sm">{cfg.first_message}</p></Field> : null}
            </>
          )
        ) : <p className="text-sm text-muted-foreground">Wähle einen Agent, um den Prompt anzuzeigen.</p>}
      </CardContent>
    </Card>
  );
}

function PreviewShell({ api, user }) {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const clientState = useResource(api, `/clients/${clientId}`, [clientId]);
  const members = useResource(api, `/clients/${clientId}/members`, [clientId]);
  const [tab, setTab] = useState("overview");
  const [memberId, setMemberId] = useState("");
  const [range, setRange] = useState("30d");
  const [custom, setCustom] = useState({ start: "", end: "" });
  // Preview AS a specific member: resolve that member's effective access (tabs + metric visibility).
  const memberAccess = useResource(api, memberId ? `/clients/${clientId}/members/${memberId}/access` : null, [clientId, memberId]);

  // Member-scoped visibility when a member is selected; otherwise the client default.
  const memberVis = memberId && memberAccess.data ? visibleTabKeys(memberAccess.data) : null;
  const ac = clientState.data?.access_config;
  const legacyPrompt = (clientState.data?.allowed_features || []).some((f) => f === "agent_prompt_view" || f === "agent_prompt_edit");
  const clientAccess = ac || { analytics: { enabled: true }, conversations: true, knowledge_base: true, agent_settings: { show_system_prompt: legacyPrompt } };
  const tabs = PREVIEW_TABS.filter((t) => {
    if (memberVis) return memberVis[t.key] !== false;
    if (t.key === "overview") return (clientAccess.analytics || {}).enabled !== false;
    if (t.key === "conversations") return clientAccess.conversations !== false;
    if (t.key === "knowledge-base") return clientAccess.knowledge_base !== false;
    if (t.key === "billing") return !!clientAccess.billing;  // deny-by-default, like the portal
    // Explicit branch, because this path reads the RAW stored access_config: existing clients have
    // no `gdpr` subtree at all, and the `return true` below would show the tab unconditionally.
    if (t.key === "datenschutz") return !!((clientAccess.gdpr || {}).export || (clientAccess.gdpr || {}).erase);
    if (t.key === "prompt") return !!(clientAccess.agent_settings || {}).show_system_prompt;
    return true;
  });
  // The previewed member's own Art. 15/17 rights (client default when no member is selected) —
  // what that member would see, not what the admin holds.
  const previewGdpr = memberId && memberAccess.data
    ? { export: !!memberAccess.data.gdpr?.export, erase: !!memberAccess.data.gdpr?.erase }
    : { export: !!(clientAccess.gdpr || {}).export, erase: !!(clientAccess.gdpr || {}).erase };
  useEffect(() => {
    if (!tabs.find((t) => t.key === tab)) setTab("overview");
  }, [memberId, memberAccess.data]); // eslint-disable-line react-hooks/exhaustive-deps

  if (user.role !== "admin") return <Navigate to={`/clients/${user.client_id}/overview`} replace />;

  const rangeQuery = (range === "custom"
    ? `${custom.start ? `&period_start=${custom.start}T00:00:00+00:00` : ""}${custom.end ? `&period_end=${custom.end}T23:59:59+00:00` : ""}`
    : `&range=${range}`) + (memberId ? `&as_member_id=${memberId}` : "");
  const activeLabel = (tabs.find((t) => t.key === tab) || {}).label;

  return (
    <div className="preview-shell" data-testid="preview-shell">
      <aside className="preview-sidebar">
        <div className="preview-brand">
          <Badge variant="outline" className="provider-badge">Vorschau</Badge>
          <strong>{clientState.data?.name || "Client"}</strong>
          <select className="form-select" data-testid="preview-member-select" value={memberId} onChange={(e) => setMemberId(e.target.value)} style={{ marginTop: ".5rem", width: "100%" }}>
            <option value="">Client-Standard</option>
            {(members.data || []).map((m) => <option key={m.id} value={m.id}>{m.name || m.email}</option>)}
          </select>
        </div>
        <nav className="preview-nav">
          {tabs.map((t) => (
            <button key={t.key} className={cx("preview-nav-link", tab === t.key && "active")} onClick={() => setTab(t.key)} data-testid={`preview-tab-${t.key}`}>
              <t.icon className="h-4 w-4" /> {t.label}
            </button>
          ))}
        </nav>
        <button className="preview-close" onClick={() => navigate("/admin/clients")} data-testid="preview-close-button">
          <ArrowLeft className="h-4 w-4" /> Vorschau schließen
        </button>
      </aside>
      <main className="preview-main">
        <header className="preview-topbar">
          <div><p className="eyebrow">Client Dashboard — Vorschau</p><h1>{activeLabel}</h1></div>
          {tab === "overview" ? <DateRangeSelect value={range} onChange={setRange} custom={custom} onCustomChange={setCustom} /> : null}
        </header>
        <div className="preview-content">
          {tab === "overview" ? <PreviewAnalytics api={api} clientId={clientId} rangeQuery={rangeQuery} /> : null}
          {tab === "agents" ? <PreviewAgents api={api} clientId={clientId} /> : null}
          {tab === "conversations" ? <PreviewConversations api={api} clientId={clientId} canGdpr={previewGdpr} /> : null}
          {tab === "knowledge-base" ? <PreviewKnowledgeBase api={api} clientId={clientId} /> : null}
          {/* Render the member's billing view (Aktueller Verbrauch + Zahlungsmethode + Abrechnungshistorie):
              force client view via role override so admin-only sections stay hidden, exactly as the member sees it. */}
          {tab === "billing" ? <BillingPage api={api} clientId={clientId} user={{ ...user, role: "client_user" }} /> : null}
          {/* Same page the member gets, with the member's own rights — the Vorschau understating
              what a member sees is what hid this section in the first place. `previewMode` keeps the
              layout and kills every acting control: this screen is headed „Vorschau" and the
              transcript row one tab over is disabled, so a live search (an audit row in the tenant's
              own Protokoll), a live export (real patient data) or a live erase would contradict both.
              Only the Protokoll stays live — a GET that logs nothing. */}
          {tab === "datenschutz" ? <DatenschutzPage api={api} clientId={clientId} canGdpr={previewGdpr} previewMode /> : null}
          {tab === "prompt" ? <PreviewPrompt api={api} clientId={clientId} /> : null}
        </div>
      </main>
    </div>
  );
}

function AppRoot() {
  const auth = useAuth();
  const api = useApi(auth.logout);
  const branding = usePublicBranding();

  return (
    <BrowserRouter>
      <Toaster position="top-right" richColors />
      <Routes>
        <Route path="/login" element={auth.checking ? <div className="auth-loading" data-testid="state-loading"><RefreshCw className="h-4 w-4 animate-spin" /> Session wird geprüft...</div> : auth.isAuthenticated ? <Navigate to={auth.user?.role === "admin" ? "/admin/clients" : `/clients/${auth.user?.client_id}/overview`} replace /> : <LoginPage auth={auth} branding={branding} />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage branding={branding} />} />
        <Route path="/reset-password" element={<ResetPasswordPage branding={branding} />} />
        <Route path="/admin/clients/:clientId/preview" element={<ProtectedRoute auth={auth}><PreviewShell api={api} user={auth.user} /></ProtectedRoute>} />
        <Route path="/*" element={<ProtectedRoute auth={auth}><AppShell auth={auth} api={api} branding={branding} /></ProtectedRoute>} />
      </Routes>
    </BrowserRouter>
  );
}

export default AppRoot;
